import React, { useState, useEffect, useRef } from 'react';
import { enablePush, disablePush, isPushOnThisDevice, pushBlockedReason, isInstalled, registerServiceWorker, clearOfflineData } from './lib/push';
import { canPromptInstall, onInstallAvailabilityChange, promptInstall, installHint } from './lib/install';
import axios from 'axios';
import './App.css';
import BRAND, { applyBrandTheme } from './brandConfig';
import { watchForNewBuild } from './lib/appVersion';
import { AppContext } from './context/AppContext';
import { t, currentLanguage } from './i18n';
import { localized } from './lib/translated';
import { canonicalTrades, canonicalSkills } from './lib/trades';
import { fmtMoney } from './lib/format';
import { serviceAddressFrom } from './lib/docAddress';
import { getPayLink } from './lib/payLink';
import { neverExpires } from './lib/ledger';

import Home from './screens/Home';
import RequestForm, { SERVICE_TYPES } from './screens/RequestForm';
import AccountDashboard, { PropertyDetail } from './screens/AccountDashboard';
import { PropertyForm, UnitForm, MemberForm, AccountWorkOrderForm, PropertyImport } from './screens/AccountForms';
import { LoginChoice, HomeownerLogin, VendorLogin, AdminLogin, AccountLogin, ForgotPassword, ResetPassword } from './screens/Auth';
import { HomeownerDashboard, RequestDetail, EstimateReview } from './screens/HomeownerDashboard';
import AdminDashboard from './screens/AdminDashboard';
import VendorDashboard from './screens/VendorDashboard';
import Modals from './screens/Modals';
import { PrivacyPolicy, Terms } from './screens/Legal';
import Work from './screens/Work';
import { invoiceFromProject, invoiceKeyOf } from './lib/invoiceDoc';
import DocPeek from './components/DocPeek';
import Toast from './components/Toast';
import PrintDoc from './components/PrintDoc';
import PaperDoc from './components/PaperDoc';
import LeadDoc from './components/LeadDoc';
import SendToCustomer from './components/SendToCustomer';
import StripeRequiredSheet from './components/StripeRequiredSheet';
import OfflineBar from './components/OfflineBar';
import UpdateBar from './components/UpdateBar';

// In dev, requests use the CRA proxy (client/package.json → "proxy") to reach
// the backend on :5001. In production the Express server serves this app, so
// API calls are same-origin (empty base URL). REACT_APP_API_URL can override.
axios.defaults.baseURL = process.env.REACT_APP_API_URL || '';

// The signed-in session, kept on the device.
//
// Until this existed the token lived only in React state, so *any* reload threw
// it away and dumped you back on the sign-in screen: pull-to-refresh, the back
// button, following a link out and returning — and on iOS especially, Safari
// silently discarding a background tab, which it does aggressively. Julian was
// refreshing to see new messages, so he was signing in every single time.
// Server tokens are good for 7 days; there is no reason the phone should forget
// them sooner.
const SESSION_KEY = 'qps-session';

const readSession = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    const saved = raw ? JSON.parse(raw) : null;
    return saved?.token && saved?.user ? saved : null;
  } catch {
    return null;   // private mode, or a storage entry we can't read
  }
};

const screenForRole = (role) =>
  role === 'admin' ? 'screen-admin-dashboard'
  : role === 'vendor' ? 'screen-vendor-dashboard'
  : role === 'manager' ? 'screen-account-dashboard'
  : 'screen-homeowner-dashboard';

// App owns ALL state and handlers, then exposes them through AppContext so each
// screen can read exactly what it needs. The render below is just a list of
// screens — every screen shows/hides itself based on `currentScreen`.
function App() {
  // Read once, at boot. The effects below that handle reset links and /privacy
  // style paths still run afterwards and still win — someone opening a password
  // link should land on the password screen whether or not they're signed in.
  const restored = readSession();

  const [currentScreen, setCurrentScreen] = useState(
    restored ? screenForRole(restored.user.role) : 'screen-home'
  );

  // Apply the active brand's theme colors + page title once on load.
  useEffect(() => { applyBrandTheme(); }, []);

  // Auth state
  const [authToken, setAuthToken] = useState(restored?.token || null);
  const [currentUser, setCurrentUser] = useState(restored?.user || null);
  const [authForm, setAuthForm] = useState({ email: '', password: '', name: '', phone: '', mode: 'login', smsConsent: false });
  const [authError, setAuthError] = useState('');

  // Request form state
  const [formData, setFormData] = useState({
    fullName: '', phone: '', email: '', serviceAddress: '', zip: '',
    city: '', cityOther: '',
    billingSame: true, serviceType: '', description: '',
    urgency: 'Standard – 1 week', bestTime: '8-10 am',
    // Express opt-in to job texts. Starts false and must be ticked by hand —
    // carriers reject A2P campaigns whose consent box is pre-checked.
    smsConsent: false
  });

  // What the customer wants done, a line each — the leak, the gate, the hole in
  // the wall. Each line holds its own photos as FILES, because the request form
  // is public and /api/uploads needs an account: nothing is uploaded until the
  // request itself is submitted, and then it all goes in one multipart post.
  const newRequestItem = () => ({ title: '', serviceType: '', description: '', files: [] });
  const [requestItems, setRequestItems] = useState([newRequestItem()]);
  // Index of the line whose own screen is open, or null for the form itself.
  const [editingRequestItem, setEditingRequestItem] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Our Work ─────────────────────────────────────────────
  // The public portfolio page. Loaded WITHOUT a token, because the audience
  // for it is a stranger who found the site on their phone; `workAll` is the
  // admin's copy of the same records, drafts included, and only ever filled
  // for an admin.
  //
  // `workLoaded` exists so the page can tell "nothing here yet" apart from
  // "not fetched yet". Those want different words on the screen and the
  // difference is invisible from an empty array.
  const [workProjects, setWorkProjects] = useState([]);
  const [workLoaded, setWorkLoaded] = useState(false);
  const [workAll, setWorkAll] = useState([]);

  // Dashboard state
  const [requests, setRequests] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [clients, setClients] = useState([]);
  const [activeRequest, setActiveRequest] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [adminView, setAdminView] = useState('leads'); // 'leads' | 'clients' | 'techs' | 'ledger' | 'projects' | 'texting'
  // Inbound-text diagnostics: is the relay armed, and what has actually arrived.
  const [smsStatus, setSmsStatus] = useState({ enabled: false, events: [], loaded: false });
  const [isLoadingSms, setIsLoadingSms] = useState(false);
  // Set when the admin taps a vendor's name — swaps the Vendors list for that
  // vendor's profile (their jobs + ledger + progress reports).
  const [activeVendor, setActiveVendor] = useState(null);
  // Set when the admin taps a client — swaps the Clients list for that client's
  // own page: how to reach them, what they have been billed, what they owe.
  const [activeClient, setActiveClient] = useState(null);
  // Vendors tab layout: 'board' is the Trello-style columns-by-trade view,
  // 'list' is the original stacked cards (which support swipe-to-delete).
  const [vendorLayout, setVendorLayout] = useState('board');

  // Admin editing a technician's details. Null when the form is closed.
  const [vendorEditForm, setVendorEditForm] = useState(null);
  // The set-password link from the technician just added, kept on screen.
  const [lastVendorLink, setLastVendorLink] = useState('');
  const [isSavingVendor, setIsSavingVendor] = useState(false);

  // Admin delete-with-confirmation: { type: 'lead'|'client'|'vendor', id, label }
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Modal state
  const [modal, setModal] = useState(null); // 'estimate' | 'assign' | 'completion' | 'invoice'
  const [estimateForm, setEstimateForm] = useState({ breakdown: [{ label: '', description: '', photos: [], cost: '' }], notes: '' });
  // Set while writing an estimate for a client who has no job on file yet —
  // Create Estimate on their own page. The job is created when the estimate is
  // sent, not before, so a sheet he opens and thinks better of doesn't leave a
  // half-lead in the pipeline. Holds { client, jobTitle, serviceType }.
  const [estimateForClient, setEstimateForClient] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({ lineItems: [{ description: '', qty: 1, unitCost: '', total: '' }], notes: '', customerEmail: '' });
  const [isAddingLead, setIsAddingLead] = useState(false);
  const [completionFiles, setCompletionFiles] = useState([]);
  const [completionNote, setCompletionNote] = useState('');
  const [assignVendorId, setAssignVendorId] = useState('');

  // Admin: manually-added lead + appointment scheduling
  //
  // A phoned-in lead carries the same LIST of jobs as one submitted from the
  // website or taken by text — same editor screen, same shape in the database.
  // Julian on the phone hears "the faucet, and the gate, and the drywall" just
  // as often as the web form does.
  const [addLeadForm, setAddLeadForm] = useState({
    fullName: '', phone: '', email: '', serviceAddress: '',
    city: '', cityOther: '', serviceType: '', description: '',
    urgency: 'Standard – 1 week', leadSource: 'Facebook', appointmentAt: '',
    items: [{ title: '', serviceType: '', description: '', photos: [] }],
  });
  // Index of the lead's line whose own screen is open, or null.
  const [editingLeadItem, setEditingLeadItem] = useState(null);
  const [appointmentInput, setAppointmentInput] = useState('');

  // Admin: a client added by hand.
  //
  // The website only ever creates a client as a by-product of somebody filing a
  // request, so the repeat customers Julian has had for years were only in the
  // app if they happened to use the form. This is him putting one in directly,
  // with the two addresses he actually needs: where the work is, and where the
  // invoice goes.
  const [addClientForm, setAddClientForm] = useState(null);
  const [isSavingClient, setIsSavingClient] = useState(false);
  // The set-password link from the last client added, kept on screen so it can
  // be texted or read out — same reason the commercial invites keep theirs.
  const [lastClientLink, setLastClientLink] = useState('');
  // What approving an estimate just created: the client, their project, and
  // their one-time way into the portal. Held so it can be read rather than
  // flashed past in a toast.
  const [approvedOpened, setApproved] = useState(null);

  // Change-password modal (any logged-in user; used from the admin nav)
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  // Change the address you sign in with. Guarded by the current password,
  // because the sign-in email IS the account.
  const [emailForm, setEmailForm] = useState({ current: '', next: '' });

  // Forgot/reset password (homeowner + technician portals)
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotStatus, setForgotStatus] = useState(''); // confirmation shown after the email is sent
  const [forgotFrom, setForgotFrom] = useState('screen-login-choice'); // login screen to return to
  const [resetToken, setResetToken] = useState(null); // token from the emailed ?reset= link
  const [resetForm, setResetForm] = useState({ password: '', confirm: '' });
  const [isResetting, setIsResetting] = useState(false);

  // An emailed reset link opens the site as /?reset=<token> — jump straight
  // to the choose-a-new-password screen and clean the token out of the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('reset');
    if (token) {
      setResetToken(token);
      setCurrentScreen('screen-reset-password');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('portal')) {
      // "View My Portal" email link → straight to the client sign-in screen
      setCurrentScreen('screen-homeowner-login');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Land someone straight on the screen their link names, rather than the
  // homepage. The back button has to work from there too, so popstate maps the
  // path back onto a screen.
  useEffect(() => {
    const screenForPath = () => {
      const p = window.location.pathname.replace(/\/+$/, '').toLowerCase();
      // A trailing "s" is the obvious thing to mistype in a link read off a
      // business card, so accept both spellings of the two shared ones.
      const aliases = {
        '/privacy': 'screen-privacy',
        '/terms': 'screen-terms',
        // The link Julian hands to techs. It opens on SIGN IN — most people
        // following it already have an account and are trying to get to their
        // jobs. Recruiting has its own link below.
        '/technicians': 'screen-vendor-login',
        '/technician': 'screen-vendor-login',
        // For recruiting a NEW tech: same screen, opened on Create Account.
        '/apply': 'screen-vendor-login',
        '/join': 'screen-vendor-login',
        // The trades page. qpshub.com itself is for property managers now, so
        // the recruiting pitch has its own address to hand out and to put on
        // a card at a supply house.
        '/pros': 'screen-pros',
        // A management company wants different things from a homeowner, and
        // the two pitches cancel each other out on one page.
        '/managers': 'screen-managers',
        '/property-managers': 'screen-managers',
        '/management': 'screen-managers',
        '/pro': 'screen-pros',
        '/trades': 'screen-pros',
        '/contractors': 'screen-pros',
        '/portal': 'screen-homeowner-login',
        // The link Julian hands a property management company. Several
        // spellings, because this one gets read off an email and retyped.
        '/commercial': 'screen-account-login',
        '/commercial-accounts': 'screen-account-login',
        '/accounts': 'screen-account-login',
        '/manager': 'screen-account-login',
        // The portfolio page. Julian asked for Our Work to be a page of its
        // own rather than a section halfway down the homepage.
        '/work': 'screen-work',
        '/portfolio': 'screen-work',
        '/our-work': 'screen-work',
        '/projects': 'screen-work',
      };
      // Each portfolio has its own address — /work/decks is a link worth
      // texting to somebody who asked about a deck. Which portfolio is
      // showing is read off the path by the page itself, so every one of
      // them lands on the same screen here.
      if (/^\/work\/[a-z0-9-]+$/.test(p)) return 'screen-work';
      return aliases[p] || null;
    };
    const landed = screenForPath();
    if (landed) {
      setCurrentScreen(landed);
      // Only the recruiting links open on Create Account. /technicians itself
      // is the everyday "get me to my jobs" link and must open on Sign In —
      // it used to open on sign-up, which showed a returning tech a blank
      // registration form for an account they already had.
      const p = window.location.pathname.replace(/\/+$/, '').toLowerCase();
      if (landed === 'screen-vendor-login') {
        setAuthForm(prev => ({ ...prev, mode: ['/apply', '/join'].includes(p) ? 'register' : 'login' }));
      }
    }

    // Stamp the entry the app opened on, so coming back to it restores that
    // screen rather than falling through to the homepage.
    window.history.replaceState(
      { screen: landed || screenRef.current },
      '',
      window.location.pathname,
    );

    // Back undoes ONE thing: the nearest one. A sheet on top of a profile panel
    // on top of a screen is three presses, in that order — which is the only
    // arrangement that doesn't lose work somebody is in the middle of.
    //
    // Each layer got its own history entry when it opened (see the effect
    // below), so closing one here consumes that entry and nothing is re-pushed.
    const onPop = (e) => {
      const open = backStack.current;
      const closed = () => { open.closedByBack.current = true; };

      // Our own tidy-up coming back round. Swallow it: the layer it belongs
      // to is already closed, and acting on it would close the one beneath.
      if (open.unwinding.current > 0) { open.unwinding.current -= 1; return; }

      // A job's own editor, which sits on top of the Add Lead sheet.
      if (open.editingLeadItem !== null && open.editingLeadItem !== undefined) {
        open.setEditingLeadItem(null); closed(); return;
      }
      // Any bottom sheet: estimate, assign, add lead, add/edit client, invoice.
      if (open.modal || open.vendorEditForm || open.lastVendorLink) {
        open.setModal(null); open.setVendorEditForm(null); open.setLastVendorLink('');
        closed(); return;
      }
      // The full-screen panels that aren't screens of their own.
      if (open.activeVendor) { open.setActiveVendor(null); closed(); return; }
      if (open.activeClient) { open.setActiveClient(null); closed(); return; }
      if (open.activeProject) { open.setActiveProject(null); closed(); return; }

      // Nothing covering it, so this is a real screen change. The entry says
      // which screen it was; a link opened cold has only its path to go on.
      setCurrentScreen(e.state?.screen || screenForPath() || 'screen-home');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Technician partnership application (mirrors the old Google Form).
  // vendorProfile is the tech's own Vendor record; the application form shows
  // until profile.completedAt is set.
  const [vendorProfile, setVendorProfile] = useState(null);
  const [vendorAppForm, setVendorAppForm] = useState({
    // Step 1 — the business. businessName and phone live on the vendor record
    // rather than inside `profile`; registration seeds them from the sign-up
    // form, which asks for a person's name, and this is where "Miguel Ortiz"
    // becomes "Ortiz Drywall LLC".
    businessName: '', phone: '',
    // Four fields, not one line. A dispatcher filtering by city, or a map,
    // cannot pull "Lehi" out of a free-text address reliably.
    address: '', city: '', state: 'UT', zip: '',

    // Step 2 — what they do, and how work finds them.
    expertise: [], expertiseOther: '',
    skills: [], skillsOther: '',
    yearsExperience: '',
    hasTools: '',
    hasCrew: '', crewSize: '',

    // Step 3 — the paperwork. Each number pairs with a file on the same row.
    einNumber: '',
    insurancePolicyNumber: '',
    workersCompType: '',
    hasQualifier: '', qualifierName: '', qualifierLicenseNumber: '',
  });
  // The four named documents. An array loses which file is which the moment it
  // is saved, leaving whoever reviews the application to open all four to find
  // out what they are.
  const [vendorDocs, setVendorDocs] = useState({
    w9: null, insurance: null, workersComp: null, contractorLicense: null,
  });
  const [isSubmittingApp, setIsSubmittingApp] = useState(false);

  // ── Vendor Ledger + compliance ──────────────────────────────
  // The ledger tracks every vendor invoice, what's been paid against it, and
  // the live progress reports attached to it. Compliance (insurance / license
  // expiry) rides along because it gates whether a vendor can be paid.
  const [ledger, setLedger] = useState([]);
  const [ledgerVendorFilter, setLedgerVendorFilter] = useState('');
  // Which technician-portal tab is showing. The first five are the bottom tab
  // bar; 'docs' and 'settlements' are pushed from More and keep it lit.
  // Land on the overview, not on Jobs. A tech signing in for the first time
  // has no jobs, and an empty list is a poor first impression of a platform
  // asking them for their licence and their insurance.
  const [vendorView, setVendorView] = useState('home');
  // 'jobs' | 'estimates' | 'invoices' | 'payments' | 'more' | 'docs' | 'settlements'
  const [activeEntry, setActiveEntry] = useState(null);
  const [ledgerForm, setLedgerForm] = useState({
    _id: '', vendorId: '', requestId: '', invoiceNumber: '',
    serviceAddress: '', jobDetails: '', invoiceAmount: '', notes: '', externalUrl: '', status: 'open'
  });
  const [ledgerDocFiles, setLedgerDocFiles] = useState([]);
  const [paymentForm, setPaymentForm] = useState({ amount: '', date: '', mode: 'Check', reference: '', note: '', overrideReason: '' });
  // Set when the server refuses a payment because the vendor's paperwork lapsed.
  const [paymentBlock, setPaymentBlock] = useState(null);
  const [progressForm, setProgressForm] = useState({ note: '', percentComplete: '' });
  const [progressFiles, setProgressFiles] = useState([]);
  const [credentialForm, setCredentialForm] = useState({
    vendorId: '', type: 'insurance', label: '', issuer: '', number: '', issuedAt: '', expiresAt: ''
  });
  const [credentialFile, setCredentialFile] = useState(null);
  const [isSavingLedger, setIsSavingLedger] = useState(false);

  // ── Projects ────────────────────────────────────────────────
  // A project is one property job made of many trades. The server shapes the
  // payload per role: the homeowner gets the statement, a tech gets only their
  // own settlement, the admin gets everything including profit.
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [estimates, setEstimates] = useState([]);
  const [projectForm, setProjectForm] = useState({ _id: '', title: '', homeownerId: '', clientName: '', address: '', notes: '' });
  const [lineForm, setLineForm] = useState({ _id: '', trade: '', invoiceNumber: '', invoiceUrl: '', amount: '', vendorId: '', techCost: '' });
  const [clientPayForm, setClientPayForm] = useState({ lineId: '', amount: '', date: '', method: 'Check', reference: '' });
  const [expenseForm, setExpenseForm] = useState({ lineId: '', description: '', supplier: '', amount: '', paymentDetails: '', chargeToVendorId: '' });
  const [receiptFile, setReceiptFile] = useState(null);
  const [estimateForm2, setEstimateForm2] = useState({ trade: '', serviceAddress: '', notes: '', items: [] });
  const [isSavingProject, setIsSavingProject] = useState(false);

  // ── In-app messages ─────────────────────────────────────────
  // One thread per job, shared by the homeowner, the assigned tech and QPS.
  const [thread, setThread] = useState([]);
  const [threadFor, setThreadFor] = useState(null);   // the request being discussed
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [messageFiles, setMessageFiles] = useState([]);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});  // { requestId: count }

  const [toast, setToast] = useState('');
  const [homeownerTab, setHomeownerTab] = useState('requests');
  const [vendorTab, setVendorTab] = useState('active');

  // ── Helpers ──────────────────────────────────────────────

  // Screens are state, not routes — except the handful below, which are the
  // ones that get sent to someone who isn't on the site yet:
  //   /privacy, /terms  — carriers paste these into a browser while reviewing
  //                       our A2P text-messaging registration.
  //   /technicians      — the link Julian sends a tech to sign up.
  //   /portal           — the link a customer uses to reach their portal.
  // Express already serves index.html for any path, so the work is all here:
  // put the path in the address bar on the way in, restore / on the way out.
  // What the phone's Back button is looking at right now. Kept in refs so the
  // popstate listener — registered once, on mount — reads today's values
  // instead of the ones that existed when it was wired up.
  const screenRef = useRef(currentScreen);
  screenRef.current = currentScreen;
  const backStack = useRef({});
  // How many sheets/panels were open last time round, and whether the one that
  // just closed was closed BY Back — which means its history entry is already
  // gone and must not be wound off a second time.
  const overlayDepthRef = useRef(0);
  const closedByBack = useRef(false);
  // History entries this app is winding off ITSELF, after a sheet was closed
  // with Cancel or Save. Each history.go() fires one popstate, and that
  // popstate is not somebody pressing Back — it is the tidy-up. Without this
  // it was read as a Back press, so cancelling the Edit Invoice Line sheet
  // closed the sheet AND the project underneath it, and Julian landed on the
  // projects list having to find his way back in.
  const unwinding = useRef(0);

  const SCREEN_PATHS = {
    'screen-pros': '/pros',
    'screen-managers': '/managers',
    'screen-privacy': '/privacy',
    'screen-terms': '/terms',
    'screen-vendor-login': '/technicians',
    'screen-homeowner-login': '/portal',
    'screen-account-login': '/commercial',
    // Leaving the page and coming back lands on /work, not on the portfolio
    // that happened to be open. The page pushes /work/<slug> itself when a
    // portfolio is chosen, which is what makes Back step between them.
    'screen-work': '/work',
  };

  // Every screen change is a history entry now, not just the five with public
  // URLs. Before this, Back from anywhere inside the app — a job, a property, a
  // technician's profile — left the site altogether and landed on whatever page
  // was open before it, because nothing had been pushed to go back TO.
  //
  // Screens without a public URL keep the plain "/" address; the entry still
  // carries which screen it was in history.state, which is what Back reads.
  const goTo = (screen) => {
    if (screen !== screenRef.current) {
      window.history.pushState({ screen }, '', SCREEN_PATHS[screen] || '/');
    }
    setCurrentScreen(screen);
  };

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${authToken}` } });

  // Mirror the session to the device whenever it changes. Covers signing in,
  // signing out and the token being cleared underneath us — one place, so no
  // future code path can forget to keep the two in step.
  useEffect(() => {
    try {
      if (authToken && currentUser) {
        localStorage.setItem(SESSION_KEY, JSON.stringify({ token: authToken, user: currentUser }));
      } else {
        localStorage.removeItem(SESSION_KEY);
      }
    } catch { /* private mode: the session just won't outlive the tab */ }
  }, [authToken, currentUser]);

  // A restored token is up to 7 days old and may have expired. Without this the
  // app would look signed in while every request quietly failed — a worse state
  // to be in than the sign-in screen. One 401 from the API and we clear out.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      res => res,
      err => {
        // Only a rejected *token* signs you out. A 401 from the login form
        // itself is a wrong password, and must leave the form alone.
        const url = err?.config?.url || '';
        const isLoginAttempt = url.includes('/api/auth/login') || url.includes('/api/auth/register');
        if (err?.response?.status === 401 && !isLoginAttempt) {
          setAuthToken(null);
          setCurrentUser(null);
          // Leave the dashboard too. Clearing the token while staying on it
          // would show an empty portal whose every button fails — worse than
          // the sign-in screen, because it looks broken rather than expired.
          setCurrentScreen('screen-home');
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  // ── Our Work: loading and editing ────────────────────────
  //
  // The public list is fetched once, on first load, with no token. It is the
  // only data in this app a signed-out visitor sees, and it is small: a few
  // dozen rows of title, caption and photo URL.
  const loadWork = async () => {
    try {
      const res = await axios.get('/api/work');
      setWorkProjects(Array.isArray(res.data) ? res.data : []);
    } catch {
      // A portfolio that will not load must not take the page down with it.
      // The visitor sees the empty state and the button that matters.
      setWorkProjects([]);
    } finally {
      setWorkLoaded(true);
    }
  };

  // The admin's copy: the same records plus the drafts and the publish state.
  const loadWorkAll = async () => {
    try {
      const res = await axios.get('/api/work/all', authHeaders());
      setWorkAll(Array.isArray(res.data) ? res.data : []);
    } catch {
      setWorkAll([]);
    }
  };

  // Every write re-reads both lists rather than patching them in place. The
  // public page is derived from `published`, so an edit that flips it has to
  // move the project between two lists, and doing that by hand is how the
  // admin screen and the live page drift apart.
  const afterWorkWrite = async () => { await Promise.all([loadWorkAll(), loadWork()]); };

  // The order Julian dragged a client's estimates into. The whole list goes
  // up, because a position only means something against the others.
  //
  // Reloaded rather than patched in place: the rows redraw from what the
  // server actually stored, so a list that appeared to move and then sprang
  // back on the next refresh cannot happen.
  // ── Reading one invoice off a statement ──
  //
  // Click the description or the number on any statement row and the bill it
  // belongs to opens in front of the page, with an X and a press-outside to
  // close. Both sides use it: the customer asking what exactly they are
  // paying for, and Julian answering the same question.
  //
  // Nothing is fetched. The statement already holds every line, and the
  // server already decided which fields this reader may see — so the
  // customer's sheet is built from the customer's own data and cannot show
  // them a cost that was never sent to their browser.
  const [peekInvoice, setPeekInvoice] = useState(null);

  // Who the bill is addressed to. The sheet prints a Bill To block and
  // without this it printed an empty one, which is not a document.
  //
  // Three sources, worst to best, because who is looking decides what is in
  // the browser at all:
  //   the name stamped on the project, which every view carries;
  //   the owner the project was populated with, a name and an email;
  //   and the full record — the one with a billing address on it, which an
  //   out-of-state owner's invoice has to be addressed to rather than the
  //   house it was worked on.
  // A customer reading their own statement IS that full record, and has no
  // client list to look themselves up in.
  const clientForProject = (project) => {
    const ownerId = String(
      (project?.homeownerId && project.homeownerId._id) || project?.homeownerId || '',
    );
    const owner = project?.homeownerId && typeof project.homeownerId === 'object'
      ? project.homeownerId : null;
    const onFile = ownerId ? (clients || []).find(c => String(c._id) === ownerId) : null;
    const meId = String(currentUser?._id || currentUser?.id || '');
    const me = meId && meId === ownerId ? currentUser : null;
    return {
      name: project?.clientName || '',
      ...(owner || {}),
      ...(me || {}),
      ...(onFile || {}),
    };
  };

  const openStatementInvoice = (project, line) => {
    const inv = invoiceFromProject(project, invoiceKeyOf(line));
    if (!inv) { showToast('That invoice is not on this statement'); return; }
    setPeekInvoice({ ...inv, client: clientForProject(project) });
  };

  // Email ONE invoice line to the customer, after it was raised.
  //
  // Confirmed first, by name and by amount. This leaves the building and
  // cannot be taken back, and the row it is fired from sits next to Edit and
  // Delete on a grid read with a thumb.
  const sendInvoiceLine = async (project, line) => {
    const owed = Number(line?.owed) || 0;
    const who = project?.clientName || 'the customer';
    const what = line?.invoiceNumber ? `invoice ${line.invoiceNumber}` : (line?.trade || 'this line');
    const again = line?.sentAt ? '\n\nThis one has already been emailed once.' : '';
    if (!window.confirm(
      `Email ${who} ${what} for ${fmtMoney(owed)}, with a link to pay it?${again}`
    )) return;
    try {
      const res = await axios.post(
        `/api/projects/${project._id}/lines/${line._id}/send`, {}, authHeaders(),
      );
      if (res.data?.project) setActiveProject(res.data.project);
      await refreshProjects();
      showToast(`Sent to ${res.data?.to || who}`);
    } catch (err) {
      alert(err?.response?.data?.message || 'That invoice could not be sent.');
    }
  };

  const reorderEstimates = async (ids) => {
    try {
      await axios.post('/api/requests/reorder', { ids }, authHeaders());
      await loadAdminData();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Could not save that order');
    }
  };

  const saveWorkProject = async (id, fields) => {
    try {
      if (id) await axios.patch(`/api/work/${id}`, fields, authHeaders());
      else await axios.post('/api/work', fields, authHeaders());
      await afterWorkWrite();
      showToast(id ? 'Project saved' : 'Project added');
      return true;
    } catch (err) {
      showToast(err?.response?.data?.message || 'Could not save that project');
      return false;
    }
  };

  const deleteWorkProject = async (id) => {
    try {
      await axios.delete(`/api/work/${id}`, authHeaders());
      await afterWorkWrite();
      showToast('Project removed');
      return true;
    } catch (err) {
      showToast(err?.response?.data?.message || 'Could not remove that project');
      return false;
    }
  };

  const logout = () => {
    // Wipe cached API responses too, or the next person on a shared phone can
    // page through the last user's jobs with no signal and no password.
    clearOfflineData();
    setAuthToken(null);
    setCurrentUser(null);
    setRequests([]);
    setVendors([]);
    setLedger([]);
    setProjects([]);
    setEstimates([]);
    setActiveProject(null);
    setVendorProfile(null);
    goTo('screen-home');
  };

  // ── Data Loaders ─────────────────────────────────────────

  const loadHomeownerRequests = async () => {
    try {
      const res = await axios.get('/api/requests', authHeaders());
      setRequests(res.data);
    } catch { /* silently fail */ }
    try {
      const pRes = await axios.get('/api/projects', authHeaders());
      setProjects(pRes.data);
    } catch { /* silently fail */ }
  };

  const loadAdminData = async () => {
    loadStorage();   // cheap, unauthenticated, and answers "are uploads being kept?"
    try {
      const [rRes, vRes, cRes, lRes] = await Promise.all([
        axios.get('/api/requests', authHeaders()),
        axios.get('/api/vendors', authHeaders()),
        axios.get('/api/auth/clients', authHeaders()),
        axios.get('/api/ledger', authHeaders())
      ]);
      setRequests(rRes.data);
      setVendors(vRes.data);
      setClients(cRes.data);
      setLedger(lRes.data);
    } catch { /* silently fail */ }
    try {
      const [pRes, eRes] = await Promise.all([
        axios.get('/api/projects', authHeaders()),
        axios.get('/api/estimates', authHeaders()),
      ]);
      setProjects(pRes.data);
      setEstimates(eRes.data);
    } catch { /* silently fail */ }
  };

  const loadVendorJobs = async () => {
    // Fetch independently — a failure on one shouldn't blank the others.
    try {
      const rRes = await axios.get('/api/requests', authHeaders());
      setRequests(rRes.data);
    } catch { /* silently fail */ }
    try {
      const meRes = await axios.get('/api/vendors/me', authHeaders());
      setVendorProfile(meRes.data);
    } catch { /* silently fail */ }
    try {
      const lRes = await axios.get('/api/ledger', authHeaders());
      setLedger(lRes.data);
    } catch { /* silently fail */ }
    try {
      const [pRes, eRes] = await Promise.all([
        axios.get('/api/projects', authHeaders()),
        axios.get('/api/estimates', authHeaders()),
      ]);
      setProjects(pRes.data);
      setEstimates(eRes.data);
    } catch { /* silently fail */ }
  };

  const loadUnread = async () => {
    try {
      const res = await axios.get('/api/messages/unread', authHeaders());
      setUnreadCounts(res.data);
    } catch { /* silently fail */ }
  };

  // Re-pulls whichever dataset the current user is looking at, so a ledger
  // action refreshes the right dashboard without the caller knowing the role.
  // Which technician's panel is open, if any. A ref rather than state because
  // refreshData is built early in the render and would otherwise close over
  // whatever the value was then.
  const openTechRef = useRef(null);

  const refreshData = async () => {
    if (currentUser?.role === 'admin') await loadAdminData();
    else if (currentUser?.role === 'vendor') await loadVendorJobs();
    else if (currentUser?.role === 'manager') await loadAccountData();
    // A technician's panel keeps its own list, fetched separately because it
    // includes drafts. Without this, Julian fixes a draft on a support call
    // and the row he just fixed still shows the old price.
    if (openTechRef.current) await loadTechEstimates(openTechRef.current);
  };

  // Opens the admin's technician sheet. With a vendor it corrects theirs; with
  // nothing it adds one — a tech Julian has used for years who was never in the
  // app because he was never going to stop mid-job and fill in a web form.
  // ── A client turns out to be a company ────────────────────────────────
  //
  // Julian's rule: a property manager starts as an ordinary client — they
  // approve their first request, they get an account and a statement. Then
  // it turns out they look after nine buildings, and the customer is really
  // the company. This promotes them in place: their addresses become
  // properties, their jobs and statements move across, and their login
  // becomes the owner of the company.
  const promoteClientToAccount = async (client, addresses) => {
    const name = window.prompt(
      `Turn ${client.name} into a commercial account?\n\n`
      + `Each of their ${addresses} address${addresses === 1 ? '' : 'es'} becomes a property with its own work orders, `
      + `and their statements move into the company's own Billing tab.\n\n`
      + 'Company name:', client.name || '');
    if (name === null || !name.trim()) return;
    try {
      const res = await axios.post('/api/accounts/from-client',
        { clientId: client._id, name: name.trim() }, authHeaders());
      const m2 = res.data.moved;
      showToast(`${res.data.account.name} is a commercial account — `
        + `${m2.properties} propert${m2.properties === 1 ? 'y' : 'ies'}, ${m2.jobs} job${m2.jobs === 1 ? '' : 's'}`);
      setActiveClient(null);
      await loadAdminData();
      await loadAdminAccounts();
      setAdminView('accounts');
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // ── Working a lead's qualifying questions ─────────────────────────────
  //
  // Agent 8 scores every enquiry the moment it lands and names what is still
  // unanswered. This is the other half: Julian rings them, and types what
  // they said. The server re-scores from the answers — never from a number
  // the browser sends, because the band decides the order he works his day.
  const [leadQualify, setLeadQualify] = useState(null);
  const [isSavingLead, setIsSavingLead] = useState(false);

  const openLeadQualify = (req) => {
    setActiveRequest(req);
    setLeadQualify({ _id: req._id, ...(req.lead?.answers || {}) });
    setModal('qualify');
  };

  const saveLeadQualify = async () => {
    const f = leadQualify;
    if (!f) return;
    setIsSavingLead(true);
    try {
      const { _id, ...answers } = f;
      const res = await axios.patch(`/api/requests/${_id}/lead`, answers, authHeaders());
      const band = res.data?.lead?.band;
      showToast(band === 'hot' ? 'Hot lead — ring them today'
        : band === 'warm' ? 'Warm lead' : 'Saved');
      setModal(null);
      setLeadQualify(null);
      await loadAdminData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally { setIsSavingLead(false); }
  };

  const openVendorAdd = () => {
    setVendorEditForm({
      _id: '', contactName: '', businessName: '', occupation: '', phone: '', email: '',
      // The address in four boxes, not one. Utah is the default because every
      // technician on this platform so far works Utah County, and a state box
      // somebody has to fill in every time is a state box that ends up blank.
      address: '', city: '', state: 'UT', zip: '', homeAddress: '', yearsExperience: '',
      hasTools: '', licensed: '', insured: '', hasCrew: '',
      // Trades are the board columns. A tech added without one lands in "No
      // trade on file", which is where techs go to be forgotten.
      expertise: [], expertiseOther: '',
      // Ticked by default. It was off, so adding a technician quietly sent
      // them nothing and Julian waited for an email that was never requested.
      // A commercial account has always defaulted to on; there was never a
      // reason for a technician to be the exception. Only on ADD — opening an
      // existing technician to correct their phone number must not fire a new
      // invite at them, which is why openVendorEdit leaves this unset.
      sendInvite: true,
    });
    setLastVendorLink('');
  };

  // Opens the admin's edit form pre-filled from the vendor record.
  const openVendorEdit = (v) => {
    const p = v.profile || {};
    setLastVendorLink('');
    setVendorEditForm({
      _id: v._id,
      contactName: v.userId?.name || '',
      businessName: v.businessName || '',
      occupation: p.occupation || v.specialty || '',
      phone: v.phone || v.userId?.phone || '',
      email: v.email || v.userId?.email || '',
      address: p.address || '',
      city: p.city || '',
      state: p.state || '',
      zip: p.zip || '',
      homeAddress: p.homeAddress || '',
      yearsExperience: p.yearsExperience || '',
      hasTools: p.hasTools || '',
      licensed: p.licensed || '',
      insured: p.insured || '',
      hasCrew: p.hasCrew || '',
      // The trades, and which one his card is filed under. A tech ticking ten
      // boxes on a phone is how Saul ended up reading as ten companies, and
      // until now there was no way to cut it back.
      expertise: [...(p.expertise || [])],
      primaryTrade: p.primaryTrade || (p.expertise || [])[0] || '',
    });
  };

  const handleSaveVendorEdit = async () => {
    const f = vendorEditForm;
    if (!f?.businessName?.trim()) { alert('A business name is required.'); return; }
    if (!f._id && !f.phone?.trim() && !f.email?.trim()) {
      alert('A phone number or an email address is required.'); return;
    }
    // Outside the try, because the duplicate answer in the catch needs to send
    // the same fields a second time.
    const { _id, ...fields } = f;
    setIsSavingVendor(true);
    try {
      if (_id) {
        const res = await axios.patch(`/api/vendors/${_id}`, fields, authHeaders());
        setVendors(prev => prev.map(v => (v._id === _id ? res.data : v)));
        // Keep the open profile panel in step with what was just saved.
        setActiveVendor(prev => (prev && prev._id === _id ? res.data : prev));
        setVendorEditForm(null);
        showToast('Technician details updated');
      } else {
        const res = await axios.post('/api/vendors', fields, authHeaders());
        showToast(res.data?.invited ? 'Technician added and invite sent' : 'Technician added');
        await loadAdminData();
        setVendorEditForm(null);
        // Their way into the portal, kept on screen to text or read out — the
        // same reason the client and commercial invites keep theirs.
        if (res.data?.link) setLastVendorLink(res.data.link);
      }
    } catch (err) {
      // Already on the books under that phone number. Not an error to argue
      // with — a question, with the right answer offered first. Saying no
      // leaves the form exactly as it was so nothing typed is lost.
      const dupe = err.response?.status === 409 && err.response?.data?.duplicate;
      if (dupe) {
        const d = err.response.data;
        if (window.confirm(`${d.message}\n\nAdd a second record anyway?`)) {
          try {
            const res = await axios.post('/api/vendors', { ...fields, allowDuplicate: true }, authHeaders());
            showToast('Technician added');
            await loadAdminData();
            setVendorEditForm(null);
            if (res.data?.link) setLastVendorLink(res.data.link);
          } catch (e2) {
            alert('Error: ' + (e2.response?.data?.message || e2.message));
          }
        } else {
          // Take him to the record that already exists, which is what he
          // wanted in the first place.
          setVendorEditForm(null);
          const found = (vendors || []).find(v => String(v._id) === String(d.existingId));
          if (found) { setAdminView('techs'); setActiveVendor(found); }
        }
        return;
      }
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally { setIsSavingVendor(false); }
  };

  // The eye on a vendor card. Optimistic — the toggle should feel instant, and
  // the reload right after puts it back if the server disagreed.
  const handleToggleWatch = async (vendor) => {
    const next = !vendor.watched;
    setVendors(prev => prev.map(v => (v._id === vendor._id ? { ...v, watched: next } : v)));
    try {
      await axios.patch(`/api/vendors/${vendor._id}/watch`, { watched: next }, authHeaders());
      showToast(next ? `Watching ${vendor.businessName}` : `Stopped watching ${vendor.businessName}`);
    } catch (err) {
      setVendors(prev => prev.map(v => (v._id === vendor._id ? { ...v, watched: !next } : v)));
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // Moving a technician's card to another column on the admin board. One card
  // per person now, so this re-FILES him rather than trading one skill for
  // another: the column becomes his main trade and everything he can do stays
  // on his record. Dragging Saul from Drywall to Handyman does not mean he
  // stopped doing drywall.
  const handleMoveVendorTrade = async (vendor, from, to) => {
    if (!to || from === to) return;
    try {
      const res = await axios.patch(
        `/api/vendors/${vendor._id}/trades`, { from, to }, authHeaders()
      );
      setVendors(prev => prev.map(v => (
        v._id === vendor._id
          ? { ...v, profile: { ...(v.profile || {}), expertise: res.data.expertise, primaryTrade: res.data.primaryTrade } }
          : v
      )));
      showToast(`${vendor.businessName} is filed under ${to}`);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
      await refreshData();   // put the board back to whatever the server thinks
    }
  };

  // "Copy link" on a card produces /#/vendor/<id>. There's no router in this
  // app — screens are state — so the link is a hash the admin dashboard reads
  // once the vendor list has loaded. Anyone not signed in as admin just lands
  // on the homepage, which is the right outcome for a link that leaked.
  useEffect(() => {
    const match = /^#\/vendor\/([a-f0-9]{24})$/i.exec(window.location.hash);
    if (!match || currentUser?.role !== 'admin' || vendors.length === 0) return;
    const vendor = vendors.find(v => v._id === match[1]);
    window.history.replaceState(null, '', window.location.pathname);  // don't re-fire
    if (!vendor) { showToast('That vendor no longer exists'); return; }
    setAdminView('techs');
    setActiveVendor(vendor);
  }, [vendors, currentUser]); // eslint-disable-line

  useEffect(() => {
    if (!authToken) return;
    if (currentScreen === 'screen-homeowner-dashboard') loadHomeownerRequests();
    if (currentScreen === 'screen-admin-dashboard') { loadAdminData(); loadAdminAccounts(); }
    if (currentScreen === 'screen-vendor-dashboard') loadVendorJobs();
    if (currentScreen === 'screen-account-dashboard') loadAccountData();
    if (currentScreen.endsWith('-dashboard')) loadUnread();
  }, [currentScreen, authToken]); // eslint-disable-line

  // ── Auth ─────────────────────────────────────────────────

  const handleAuthInputChange = (e) => {
    const { name, type, value, checked } = e.target;
    setAuthForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setAuthError('');
  };

  // `intent` forces sign-in or sign-up regardless of the shared form state.
  //
  // The Admin screen has no Sign In / Create Account toggle — it only ever
  // shows a Sign In button — but it used to branch on `authForm.mode` like
  // every other screen. That mode is shared across all three portals, so after
  // a visit to the technician page (which opened on Create Account) the admin
  // button silently submitted a REGISTRATION. Julian was locked out of his own
  // portal by "Email already in use", which is a sign-up error appearing under
  // a button that says Sign In. A screen with no way to change the mode must
  // never be at the mercy of it.
  const handleAuthSubmit = async (role, intent) => {
    setAuthError('');
    try {
      let res;
      if ((intent || authForm.mode) === 'login') {
        res = await axios.post('/api/auth/login', { email: authForm.email, password: authForm.password });
      } else {
        res = await axios.post('/api/auth/register', {
          email: authForm.email, password: authForm.password,
          name: authForm.name, phone: authForm.phone, role,
          smsConsent: authForm.smsConsent,
          // Only the Commercial Accounts form collects this; registering as a
          // manager creates the company and makes them its owner.
          companyName: authForm.companyName,
        });
      }
      const { token, user } = res.data;
      const wasRegister = (intent || authForm.mode) === 'register';
      setAuthToken(token);
      setCurrentUser(user);
      setAuthForm({ email: '', password: '', name: '', phone: '', companyName: '', mode: 'login', smsConsent: false });

      goTo(screenForRole(user.role));

      showToast((wasRegister ? t('toast.welcome') : t('toast.welcomeBack')).replace('{name}', user.name || ''));
    } catch (err) {
      setAuthError(err.response?.data?.message || t('err.generic'));
    }
  };

  // Opens the forgot-password screen from a portal's sign-in card, remembering
  // which one so "Back to sign in" returns there.
  const openForgotPassword = (fromScreen) => {
    setForgotFrom(fromScreen);
    setForgotEmail(authForm.email);
    setForgotStatus('');
    setAuthError('');
    goTo('screen-forgot-password');
  };

  const handleForgotPassword = async () => {
    setAuthError('');
    if (!forgotEmail.trim()) { setAuthError(t('err.enterEmail')); return; }
    try {
      const res = await axios.post('/api/auth/forgot-password', { email: forgotEmail });
      // A deployment with no mail key sends nothing. Saying "check your inbox"
      // then is the app lying to somebody who is locked out, and they wait.
      setForgotStatus(res.data?.emailNotConfigured
        ? res.data.message
        : t('forgot.sent'));
    } catch (err) {
      setAuthError(err.response?.data?.message || t('err.generic'));
    }
  };

  const handleResetPassword = async () => {
    setAuthError('');
    if (resetForm.password.length < 8) { setAuthError(t('err.pw8')); return; }
    if (resetForm.password !== resetForm.confirm) { setAuthError(t('err.pwMatch')); return; }
    setIsResetting(true);
    try {
      await axios.post('/api/auth/reset-password', { token: resetToken, password: resetForm.password });
      setResetForm({ password: '', confirm: '' });
      setResetToken(null);
      showToast(t('toast.pwUpdated'));
      goTo('screen-login-choice');
    } catch (err) {
      setAuthError(err.response?.data?.message || t('err.generic'));
    } finally {
      setIsResetting(false);
    }
  };

  // ── Request Form ──────────────────────────────────────────

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // A line is real once it says something. An empty row someone added and
    // never filled in is dropped rather than argued with.
    const lines = requestItems.filter(it => (it.title || '').trim() || (it.description || '').trim() || (it.files || []).length);
    if (lines.length === 0) { alert(t('req.needItem')); return; }
    if (lines.some(it => !(it.description || '').trim() || !it.serviceType)) { alert(t('req.itemIncomplete')); return; }
    const files = lines.flatMap(it => it.files || []);
    if (files.length === 0) { alert(t('req.needPhoto')); return; }
    // 25 is multer's limit on the route. Caught here rather than at the server,
    // where the 26th photo would cost them everything they typed.
    if (files.length > 25) { alert(t('req.max25Total')); return; }

    setIsSubmitting(true);
    try {
      // Resolve the city + classify the lead as Primary vs Outside Area.
      const isOther = formData.city === 'Other';
      const finalCity = isOther ? formData.cityOther : formData.city;
      const areaTag = (!isOther && BRAND.serviceArea.primaryCities.includes(formData.city))
        ? 'Primary Area'
        : 'Outside Area';

      const fd = new FormData();
      const base = { ...formData, city: finalCity, areaTag, leadSource: 'Website' };
      delete base.cityOther;
      // serviceType and description are rolled up from the lines by the server
      // (server/requestItems.js), so the form no longer sends its own.
      delete base.serviceType;
      delete base.description;
      const data = currentUser
        ? { ...base, fullName: currentUser.name, email: currentUser.email, phone: currentUser.phone }
        : base;
      Object.entries(data).forEach(([k, v]) => fd.append(k, v));

      // Files can't nest inside a multipart field, so every photo goes up in
      // line order under `photos` and a count per line says where each line's
      // photos stop. The server slices them back apart.
      fd.append('items', JSON.stringify(lines.map(it => ({
        title: (it.title || '').trim(),
        serviceType: it.serviceType,
        description: (it.description || '').trim(),
      }))));
      fd.append('itemPhotoCounts', JSON.stringify(lines.map(it => (it.files || []).length)));
      files.forEach(f => fd.append('photos', f));

      const headers = { 'Content-Type': 'multipart/form-data' };
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

      await axios.post('/api/requests', fd, { headers });
      showToast(t(currentUser ? 'toast.requestSubmitted' : 'toast.requestSubmittedPortal'));
      setRequestItems([newRequestItem()]);
      setEditingRequestItem(null);
      setFormData({ fullName: '', phone: '', email: '', serviceAddress: '', zip: '', city: '', cityOther: '', billingSame: true, serviceType: '', description: '', urgency: 'Standard – 1 week', bestTime: '8-10 am', smsConsent: false });

      if (currentUser?.role === 'homeowner') {
        await loadHomeownerRequests();
        goTo('screen-homeowner-dashboard');
      } else {
        goTo('screen-home');
      }
    } catch (err) {
      alert('Error submitting request: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Photos picked on a lead's line, uploaded as they are chosen because Julian
  // is signed in — the line then carries Cloudinary URLs and the lead itself
  // posts as plain JSON. Same route the technician's estimate lines use.
  const uploadPhotos = async (files) => {
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const res = await axios.post('/api/uploads', fd, {
        headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.message) showToast(res.data.message);
      return res.data?.urls || [];
    } catch (err) {
      alert('Those photos could not be uploaded: ' + (err.response?.data?.message || err.message));
      return [];
    }
  };

  // Same route as uploadPhotos, but keeps the filename.
  //
  // A photo is identified by looking at it; a document is identified by being
  // called "move_out_inspection.pdf". uploadPhotos returns bare URLs because
  // that is all an estimate line needs — this returns { url, name } because a
  // Cloudinary URL ends in a random id and nobody can tell what it is.
  const uploadDocuments = async (files, meta = {}) => {
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const res = await axios.post('/api/uploads', fd, {
        headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.message) showToast(res.data.message);
      return (res.data?.files || []).map(f => ({ ...f, ...meta }));
    } catch (err) {
      alert('That file could not be uploaded: ' + (err.response?.data?.message || err.message));
      return [];
    }
  };

  // The job whose paperwork is open, as { request, kind } — 'work-order' or
  // 'estimate'. Null when nothing is being looked at.
  const [printDoc, setPrintDoc] = useState(null);
  // The finished document, shown after every save. See components/PaperDoc.
  const [paperDoc, setPaperDoc] = useState(null);
  // The lead whose document is open, as { id }. Held by id rather than by the
  // object, so the sheet re-reads the job out of `requests` after a send or a
  // payment and cannot go on showing a figure that has already changed.
  const [leadDoc, setLeadDoc] = useState(null);

  // The free tool: an estimate or an invoice on its way to the technician's
  // OWN customer, which is a different document from the one they send QPS.
  // { kind, doc }. See components/SendToCustomer.js and server/share.js.
  const [sendCustomer, setSendCustomer] = useState(null);
  // Asking for the bank details at the one moment a technician wants something.
  // Holds the document they were sending, so Not now carries straight on with
  // it rather than making them find the button again.
  const [stripeGate, setStripeGate] = useState(null);
  const [stripeGateBusy, setStripeGateBusy] = useState(false);

  // ── Support: one technician's documents, drafts and all ─────────────────
  //
  // The admin estimate queue deliberately hides drafts — a draft is the
  // tech's private working copy. But a technician who rings QPS stuck is
  // nearly always stuck ON a draft, and support that cannot see the document
  // is support that asks him to read it down the phone.
  //
  // So this is a SEPARATE fetch, made only when Julian opens one technician,
  // and it asks the server for that technician's documents including the
  // drafts. Nothing here widens the queue.
  // Named techEstimates, not vendorDocs: `vendorDocs` already means the
  // compliance files a technician has uploaded, and two things called the same
  // name in one file is how the wrong one gets read.
  // ── Admins, and a way back in for anybody ───────────────────────────────
  //
  // There was no way to create a second admin, so one forgotten password with
  // the mail key missing locked the company out of its own back office. And a
  // technician's set-password link appeared once, on the screen that created
  // them, and nowhere afterwards — which with email off meant no way in at all.
  //
  // Both of these hand the link back to the SCREEN. The email is the copy.
  const [admins, setAdmins] = useState({ list: [], loading: false, error: '' });
  const [adminForm, setAdminForm] = useState({ name: '', email: '', phone: '' });
  const [isSavingAdmin, setIsSavingAdmin] = useState(false);
  // { link, message, who } — whatever link was last produced, kept on screen
  // until it is dismissed, because a link that flashes past is no link at all.
  const [signInLink, setSignInLink] = useState(null);

  const loadAdmins = async () => {
    setAdmins(p => ({ ...p, loading: true, error: '' }));
    try {
      const res = await axios.get('/api/auth/admins', authHeaders());
      setAdmins({ list: res.data || [], loading: false, error: '' });
    } catch (err) {
      setAdmins({ list: [], loading: false, error: err.response?.data?.message || 'Could not load the admin list.' });
    }
  };

  // Handing a named power to one person, or taking it back.
  //
  // The list is replaced wholesale rather than patched, so the request says
  // exactly what that admin should end up with and two people toggling at once
  // cannot leave a half-applied set.
  const toggleAdminPermission = async (admin, permission, on) => {
    const next = new Set(admin.permissions || []);
    if (on) next.add(permission); else next.delete(permission);
    try {
      const res = await axios.patch(
        `/api/auth/admins/${admin._id}/permissions`,
        { permissions: [...next] },
        authHeaders(),
      );
      setAdmins(p => ({ ...p, list: p.list.map(a => (a._id === admin._id ? res.data : a)) }));
      showToast(on
        ? `${admin.name} can now issue refunds.`
        : `${admin.name} can no longer issue refunds.`);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
      // Put the switch back where it was: the server refused, so nothing moved.
      setAdmins(p => ({ ...p }));
    }
  };

  const handleAddAdmin = async () => {
    if (!adminForm.name.trim()) { alert('A name is required.'); return; }
    if (!adminForm.email.trim()) { alert('An email address is required — it is what they sign in with.'); return; }
    setIsSavingAdmin(true);
    try {
      const res = await axios.post('/api/auth/admins', adminForm, authHeaders());
      setAdminForm({ name: '', email: '', phone: '' });
      setSignInLink({ link: res.data.link, message: res.data.message, who: res.data.admin?.name || '' });
      await loadAdmins();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally { setIsSavingAdmin(false); }
  };

  // A fresh set-password link for any account, admin or technician or client.
  // Replaces whatever link was out before: one live link per person.
  const handleSendSignInLink = async (userId, who) => {
    try {
      const res = await axios.post(`/api/auth/users/${userId}/invite`, {}, authHeaders());
      setSignInLink({ link: res.data.link, message: res.data.message, who: who || res.data.email });
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const [techEstimates, setTechEstimates] = useState({ vendorId: null, list: [], loading: false, error: '' });

  const loadTechEstimates = async (vendorId) => {
    openTechRef.current = vendorId || null;
    if (!vendorId) { setTechEstimates({ vendorId: null, list: [], loading: false, error: '' }); return; }
    setTechEstimates((p) => ({ ...p, vendorId, loading: true, error: '' }));
    try {
      const res = await axios.get(`/api/estimates?vendorId=${vendorId}&drafts=1`, authHeaders());
      setTechEstimates({ vendorId, list: res.data || [], loading: false, error: '' });
    } catch (err) {
      setTechEstimates({ vendorId, list: [], loading: false,
        error: err.response?.data?.message || 'Could not load this technician\'s estimates.' });
    }
  };
  // True once the server is serving a newer build than this tab is running —
  // see lib/appVersion.js. The bar it draws is the only thing that reloads,
  // and only when somebody presses it.
  const [newBuild, setNewBuild] = useState(false);
  useEffect(() => watchForNewBuild(() => setNewBuild(true)), []);
  const [isRaisingWorkOrder, setIsRaisingWorkOrder] = useState(false);

  // Raise the work order if this job hasn't got one, then open it. Julian's
  // "Create Work Order" and "View & Print PDF" are one motion in practice: he
  // wants the sheet, and whether it already exists is our problem, not his.
  //
  // The number is minted once, server-side — opening the sheet again never
  // makes a second one.
  const openWorkOrder = async (request) => {
    if (request.workOrder?.number) { setPrintDoc({ request, kind: 'work-order' }); return; }
    setIsRaisingWorkOrder(true);
    try {
      const res = await axios.post(`/api/requests/${request._id}/work-order`, {}, authHeaders());
      setRequests(prev => prev.map(r => (r._id === res.data._id ? { ...r, ...res.data } : r)));
      setPrintDoc({ request: { ...request, ...res.data }, kind: 'work-order' });
      showToast(`Work order ${res.data.workOrder?.number} raised`);
      await refreshAdminAccountDetail();
    } catch (err) {
      alert('Error raising the work order: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsRaisingWorkOrder(false);
    }
  };

  // ── Estimate (Admin) ──────────────────────────────────────

  // Which job on the request Julian is editing the note of while he prices it,
  // and what he has typed: { index, title, description }. Null when the panel
  // is just showing what the customer wrote.
  const [noteEdit, setNoteEdit] = useState(null);
  const [isSavingNote, setIsSavingNote] = useState(false);

  // Saves a corrected note back onto the job. The server keeps the customer's
  // original wording (descriptionOriginal) and re-rolls the request's summary,
  // so the lead card and the estimate can't drift apart.
  const handleSaveNote = async () => {
    if (!noteEdit || !activeRequest) return;
    setIsSavingNote(true);
    try {
      const res = await axios.patch(
        `/api/requests/${activeRequest._id}/notes`,
        { index: noteEdit.index, title: noteEdit.title, description: noteEdit.description },
        authHeaders()
      );
      setActiveRequest(res.data);
      setRequests(prev => prev.map(r => (r._id === res.data._id ? res.data : r)));
      // The estimate row was labelled from this job, so keep the label he is
      // about to send in step with the note he just fixed — but only that row,
      // and only if he hasn't already renamed it himself.
      setEstimateForm(p => {
        const b = [...p.breakdown];
        const was = activeRequest.items?.[noteEdit.index];
        const row = b[noteEdit.index];
        if (row && was && (row.label === was.title || !row.label)) {
          b[noteEdit.index] = { ...row, label: res.data.items?.[noteEdit.index]?.title || row.label };
        }
        return { ...p, breakdown: b };
      });
      setNoteEdit(null);
      showToast('Note updated');
    } catch (err) {
      alert('Error saving note: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsSavingNote(false);
    }
  };

  const estimateTotal = estimateForm.breakdown.reduce((sum, r) => sum + (parseFloat(r.cost) || 0), 0);

  const handleSubmitEstimate = async () => {
    try {
      // Started from a client's page: log the job first, from their own
      // record, then price it. Their name, number, email and address come off
      // the client rather than out of Julian's memory, and the job carries
      // their account so it lands back on their page.
      // A line with a price and no name is dropped on the way out (a nameless
      // line cannot print), so say so rather than letting money vanish
      // quietly between the sheet and the document.
      const nameless = estimateForm.breakdown.find(r => !r.label?.trim() && (parseFloat(r.cost) || 0) > 0);
      if (nameless) { alert('One of the lines has a price but no name. Name it, or remove it.'); return; }

      let requestId = activeRequest?._id;
      if (estimateForClient) {
        const { client } = estimateForClient;
        // The job takes its name and its jobs from the estimate itself. Asking
        // "what is the work?" above the lines was asking the same question
        // twice: the first line already says DRYWALL, and typing it out again
        // before you can price anything is a field in the way of the work.
        const named = estimateForm.breakdown.filter(r => r.label?.trim());
        if (!named.length) { alert('Add a line item first — its name is what the job is called.'); return; }
        const where = [client.address, client.address2].filter(Boolean).join(', ');
        const res = await axios.post('/api/requests/manual', {
          fullName: client.name,
          phone: client.phone || client.phone2 || '',
          email: client.email || '',
          serviceAddress: where || client.city || 'Address pending',
          city: client.city || '',
          areaTag: BRAND.serviceArea.primaryCities.includes(client.city) ? 'Primary Area' : 'Outside Area',
          leadSource: 'Existing client',
          homeownerId: client._id,
          serviceType: 'Other',
          // A job per line, carrying the scope and the photos written on it —
          // so the lead, the work order and the estimate all say the same
          // thing without any of it being typed twice.
          items: named.map(r => ({
            title: r.label.trim(),
            serviceType: 'Other',
            description: (r.description || '').trim(),
            photos: r.photos || [],
          })),
        }, authHeaders());
        requestId = res.data._id;
      }
      await axios.post(`/api/requests/${requestId}/estimate`, {
        amount: estimateTotal,
        // Blank qty/rate are left off entirely rather than saved as empty
        // strings — a row with no workings should look like one.
        breakdown: estimateForm.breakdown.filter(r => r.label).map(r => ({
          label: r.label,
          // The scope, and the photos of what it covers. Both ride on the line
          // rather than the document, so a line moved or dropped takes its own
          // words and pictures with it.
          ...(r.description?.trim() ? { description: r.description.trim() } : {}),
          ...((r.photos || []).length ? { photos: r.photos } : {}),
          cost: parseFloat(r.cost) || 0,
          ...(r.qty !== '' && r.qty != null && !isNaN(parseFloat(r.qty)) ? { qty: parseFloat(r.qty) } : {}),
          ...(r.rate !== '' && r.rate != null && !isNaN(parseFloat(r.rate)) ? { rate: parseFloat(r.rate) } : {}),
        })),
        notes: estimateForm.notes,
        // The date Julian put on the sheet. The server stores it apart from
        // when the estimate was actually sent.
        docDate: estimateForm.docDate || '',
        // Blank means "assign the next one". Typed means he is seeding his
        // own sequence, or correcting a number.
        number: estimateForm.number || '',
      }, authHeaders());
      showToast(estimateForClient
        ? 'Job created and estimate sent'
        : activeRequest?.accountId ? 'Estimate sent to the company' : 'Estimate sent to homeowner!');
      setModal(null);
      setEstimateForm({ breakdown: [{ label: '', description: '', photos: [], cost: '' }], notes: '', docDate: todayInput(), number: '' });
      setEstimateForClient(null);
      setNoteEdit(null);
      await loadAdminData();
      // A property's page reads its jobs out of the account detail, so a price
      // saved from there has to land in the same copy or the row behind the
      // sheet still says no estimate.
      await refreshAdminAccountDetail();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // Julian approving an estimate the customer agreed to in person or on the
  // phone, then assigning it — which is one motion in real life, so the assign
  // sheet opens on the way out rather than making him find the card again.
  const handleAdminApproveEstimate = async (request, approved) => {
    const who = request.homeownerId?.name || request.fullName;
    const ok = window.confirm(approved
      ? `Approve this estimate on behalf of ${who}?\n\n${who} becomes a client with a portal of their own, and a project opens for this job. Nothing is billed — the lines land as you invoice the work.`
      : `Mark this estimate as declined by ${who}?`);
    if (!ok) return;
    try {
      const res = await axios.post(`/api/requests/${request._id}/approve-estimate`, { approved }, authHeaders());
      // What approving actually did, in one line, because three records were
      // just written and a bare "Estimate approved" hides two of them.
      const o = res.data?.opened;
      showToast(
        !approved ? 'Estimate marked declined'
          : o ? `Approved — ${o.clientName} is a client and "${o.projectTitle}" is open`
            : 'Estimate approved',
      );
      await loadAdminData();
      if (approved) {
        setActiveRequest(res.data);
        setAssignVendorId('');
        // What just happened, on one card, with the way into their portal on
        // it. The assign sheet used to open straight away — fine when
        // approving only moved a card, and wrong now that three records were
        // written and one of them carries a link he has seven days to use.
        if (o) setApproved({ ...o, request: res.data });
        else setModal('assign');
      }
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // The rows of an estimate as Julian typed them: `base` where the customer's
  // price has the card fee built into it, and the rows themselves on an
  // estimate written before that existed.
  const estimateRowsOf = (request) => (request.estimate?.base?.breakdown?.length
    ? request.estimate.base.breakdown
    : (request.estimate?.breakdown || []));

  // Opens the estimate sheet on a job that already has one, so it can be read
  // and corrected rather than written from scratch.
  const openEstimate = (request) => {
    setActiveRequest(request);
    setNoteEdit(null);
    // His own numbers, never the customer's. Reloading the customer's price
    // here would build the card fee in a second time the moment he saved.
    const ownRows = estimateRowsOf(request);
    const rows = ownRows.length
      ? ownRows.map(r => ({
          label: r.label || '',
          description: r.description || '',
          photos: r.photos || [],
          cost: r.cost != null ? String(r.cost) : '',
          qty: r.qty != null ? String(r.qty) : '',
          rate: r.rate != null ? String(r.rate) : '',
        }))
      // No estimate yet: one priced row per job the customer listed, already
      // labelled and described in their own words, with their own photos on
      // it — the scope starts as what they asked for and gets priced, rather
      // than being typed out a second time.
      : (request.items?.length
          ? request.items.map(it => ({
              label: it.title || it.serviceType || '',
              description: it.description || '',
              photos: it.photos || [],
              cost: '',
            }))
          : [{ label: '', description: '', photos: [], cost: '' }]);
    // The date on the paper. Whatever is already on the document when there
    // is one, so re-saving an estimate written last Monday does not silently
    // stamp it with today.
    setEstimateForm({
      breakdown: rows,
      notes: request.estimate?.notes || '',
      docDate: dateInputFrom(request.estimate?.docDate || request.estimate?.submittedAt),
      // Whatever it already carries. Blank only on an estimate that has
      // never been numbered, and then the server assigns the next one.
      number: request.estimate?.number || '',
    });
    setEstimateForClient(null);
    setModal('estimate');
  };

  // Create Estimate, on a client's own page. An estimate here is a price on a
  // job, so there has to be a job — but making Julian log a lead first and
  // price it second is two screens for one act, and he is standing in someone's
  // kitchen. So the same sheet asks what the work is, and sending it creates
  // the job and the estimate together, already tied to this client.
  const openEstimateForClient = (client) => {
    setActiveRequest(null);
    setNoteEdit(null);
    setEstimateForm({ breakdown: [{ label: '', description: '', photos: [], cost: '' }], notes: '', docDate: todayInput(), number: '' });
    setEstimateForClient({ client });
    setModal('estimate');
  };

  const handleAssignVendor = async () => {
    if (!assignVendorId) return;
    try {
      await axios.post(`/api/requests/${activeRequest._id}/assign`, { vendorId: assignVendorId }, authHeaders());
      showToast('Vendor assigned!');
      setModal(null);
      setAssignVendorId('');
      await loadAdminData();
      // The same job may be on screen as a row on a property's page, which
      // reads its jobs out of the open account rather than off the board.
      await refreshAdminAccountDetail();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleApproveInvoice = async (reqId) => {
    try {
      await axios.post(`/api/requests/${reqId}/approve-invoice`, {}, authHeaders());
      // "funds marked as held" was a second version of the same lie: nothing
      // is held, because nothing has been collected. Approving says QPS has
      // read the technician's bill and agreed to it, and that is all it says.
      showToast('Invoice approved. The customer still has to pay it.');
      await loadAdminData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // Admin manually logs a lead (Facebook, Nextdoor, referral, phone, etc.)
  const handleAddLead = async () => {
    if (!addLeadForm.fullName || !addLeadForm.phone) { alert('Name and phone are required.'); return; }
    // Two cards reading "sam skanchy" where there was one Sam. The button had
    // no guard, so a second tap while the first was still in the air wrote a
    // second lead — and two cards is two estimates and a customer asked for
    // the same money twice. Add Client has had this since it was written.
    if (isAddingLead) return;
    setIsAddingLead(true);
    try {
      const isOther = addLeadForm.city === 'Other';
      const finalCity = isOther ? addLeadForm.cityOther : addLeadForm.city;
      const areaTag = (!isOther && BRAND.serviceArea.primaryCities.includes(addLeadForm.city))
        ? 'Primary Area'
        : 'Outside Area';
      // Empty rows he opened and never filled in are dropped rather than
      // argued with — the same rule the customer's own form follows.
      const items = (addLeadForm.items || [])
        .filter(it => (it.title || '').trim() || (it.description || '').trim() || (it.photos || []).length)
        .map(it => ({
          title: (it.title || '').trim(),
          // Falls back to the lead's own Service Type, so a line he typed in a
          // hurry still lands in the right trade column.
          serviceType: it.serviceType || addLeadForm.serviceType,
          description: (it.description || '').trim(),
          photos: it.photos || [],
        }));
      const payload = { ...addLeadForm, city: finalCity, areaTag, items };
      delete payload.cityOther;
      await axios.post('/api/requests/manual', payload, authHeaders());
      showToast('Lead added!');
      setModal(null);
      setEditingLeadItem(null);
      setAddLeadForm({ fullName: '', phone: '', email: '', serviceAddress: '', city: '', cityOther: '', serviceType: '', description: '', urgency: 'Standard – 1 week', leadSource: 'Facebook', appointmentAt: '', items: [{ title: '', serviceType: '', description: '', photos: [] }] });
      await loadAdminData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsAddingLead(false);
    }
  };

  // One sheet, both jobs. Pass a client to correct them, nothing to add one —
  // the fields are identical, and a second near-identical form is a second
  // place for the two to drift apart.
  const openClientForm = (client) => {
    // Blank billing on the record means "bill to the service address" — the
    // switch is the same fact read the other way round. When they match, the
    // one address the form shows under BILLING ADDRESS is the service address
    // wearing the other hat, so it is seeded from it and copied back on save.
    const different = Boolean(client?.billingAddress);
    setAddClientForm(client ? {
      _id: client._id,
      name: client.name || '',
      phone: client.phone || '', phone2: client.phone2 || '', email: client.email || '',
      address: client.address || '', address2: client.address2 || '',
      city: client.city || '', state: client.state || '', zip: client.zip || '',
      billingSameAsService: !different,
      billingAddress: different ? client.billingAddress : (client.address || ''),
      billingAddress2: different ? (client.billingAddress2 || '') : (client.address2 || ''),
      billingCity: different ? (client.billingCity || '') : (client.city || ''),
      billingState: different ? (client.billingState || '') : (client.state || ''),
      billingZip: different ? (client.billingZip || '') : (client.zip || ''),
      notes: client.notes || '', sendInvite: false,
    } : {
      name: '', phone: '', phone2: '', email: '',
      address: '', address2: '', city: '', state: '', zip: '',
      billingSameAsService: true,
      billingAddress: '', billingAddress2: '', billingCity: '', billingState: '', billingZip: '',
      notes: '', sendInvite: false,
    });
    setLastClientLink('');
    setModal('addClient');
  };
  const openAddClientForm = () => openClientForm(null);

  // ── Fixing who a job is for ────────────────────────────────────────────
  //
  // The same screen the client list uses, opened on a LEAD. A job arrives with
  // whatever the website form or a phone call gave us, and some of it is
  // wrong. Until this existed none of it could be changed: a job typed with
  // the wrong email had no route back, its invoice went to nobody, and nothing
  // on screen said so.
  const [jobCustomerForm, setJobCustomerForm] = useState(null);
  const [isSavingJobCustomer, setIsSavingJobCustomer] = useState(false);

  const openJobCustomer = (request) => {
    setActiveRequest(request);
    setJobCustomerForm({
      _id: request._id,
      name: request.fullName || '',
      email: request.email === 'not-provided@lead.local' ? '' : (request.email || ''),
      phone: request.phone || '',
      phone2: request.phone2 || '',
      // The billing block is the one always on screen, and with one address it
      // IS the address — so with the switch off it has to be seeded from the
      // job's own, or the editor opens blank and saving wipes what was there.
      address: request.serviceAddress || '',
      address2: request.address2 || '',
      city: request.city || '',
      state: request.state || '',
      zip: request.zip || '',
      billingSameAsService: request.billingSame !== false,
      ...(request.billingSame !== false ? {
        billingAddress: request.serviceAddress || '',
        billingAddress2: request.address2 || '',
        billingCity: request.city || '',
        billingState: request.state || '',
        billingZip: request.zip || '',
      } : {
        billingAddress: request.billingAddress || '',
        billingAddress2: request.billingAddress2 || '',
        billingCity: request.billingCity || '',
        billingState: request.billingState || '',
        billingZip: request.billingZip || '',
      }),
      notes: request.customerNotes || '',
    });
    setModal('jobCustomer');
  };

  const handleSaveJobCustomer = async () => {
    const f = jobCustomerForm;
    if (!f?.name?.trim()) { alert('The job needs a name on it.'); return; }
    if (!f.email?.trim()) { alert('A job needs an email address. That is where the bill and the pay link go.'); return; }
    setIsSavingJobCustomer(true);
    try {
      // With one address the billing block IS the address, which is how the
      // editor presents it. Sent as the service address so the job keeps
      // meaning the same thing everywhere else in the app.
      const same = f.billingSameAsService !== false;
      const payload = {
        fullName: f.name,
        email: f.email,
        phone: f.phone,
        phone2: f.phone2,
        serviceAddress: same ? (f.billingAddress || f.address) : f.address,
        address2: same ? f.billingAddress2 : f.address2,
        city: same ? (f.billingCity || f.city) : f.city,
        state: same ? f.billingState : f.state,
        zip: same ? (f.billingZip || f.zip) : f.zip,
        billingSame: same,
        billingAddress: same ? '' : f.billingAddress,
        billingAddress2: same ? '' : f.billingAddress2,
        billingCity: same ? '' : f.billingCity,
        billingState: same ? '' : f.billingState,
        billingZip: same ? '' : f.billingZip,
        customerNotes: f.notes,
      };
      const res = await axios.patch(`/api/requests/${f._id}/customer`, payload, authHeaders());
      setRequests(prev => prev.map(r => (r._id === f._id ? res.data : r)));
      setActiveRequest(prev => (prev && prev._id === f._id ? res.data : prev));
      showToast('Customer details saved');
      setModal(null);
      setJobCustomerForm(null);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsSavingJobCustomer(false);
    }
  };

  // The + on a client's own page. A repeat customer rings about the next job
  // while you are looking at their record, so the lead starts with everything
  // that record already knows and Julian types only what is new.
  const openLeadForClient = (client) => {
    setAddLeadForm({
      fullName: client.name || '', phone: client.phone || '', email: client.email || '',
      serviceAddress: [client.address, client.address2].filter(Boolean).join(', '),
      // The lead form's city is a dropdown of the service area; a client
      // outside it (most of them) lands on Other with the city written out.
      city: BRAND.serviceArea.primaryCities.includes(client.city) ? client.city : (client.city ? 'Other' : ''),
      cityOther: BRAND.serviceArea.primaryCities.includes(client.city) ? '' : (client.city || ''),
      serviceType: '', description: '', urgency: 'Standard – 1 week',
      leadSource: 'Phone/Text', appointmentAt: '',
      items: [{ title: '', serviceType: '', description: '', photos: [] }],
    });
    setModal('addLead');
  };

  // Admin adds a client directly, rather than waiting for them to file a
  // request — or corrects one already on file.
  const handleAddClient = async () => {
    const f = addClientForm || {};
    if (!f.name?.trim()) { alert('A name is required.'); return; }
    if (!f.phone?.trim() && !f.email?.trim()) { alert('A phone number or an email address is required.'); return; }
    setIsSavingClient(true);
    try {
      const { _id, ...fields } = f;
      // With the switch off there is one address on screen, sitting under
      // BILLING ADDRESS, and it is where the work is as much as where the bill
      // goes. It is stored as the service address; the server blanks the
      // billing one, which is how "same" is written down here.
      if (fields.billingSameAsService !== false) {
        fields.address = fields.billingAddress;
        fields.address2 = fields.billingAddress2;
        fields.city = fields.billingCity;
        fields.state = fields.billingState;
        fields.zip = fields.billingZip;
      }
      const res = _id
        ? await axios.patch(`/api/auth/clients/${_id}`, fields, authHeaders())
        : await axios.post('/api/auth/clients', fields, authHeaders());
      showToast(res.data?.invited
        ? (_id ? 'Client updated and invite sent' : 'Client added and invite sent')
        : (_id ? 'Client updated' : 'Client added'));
      await loadAdminData();
      // Stay on the screen when there is a login link worth passing on —
      // editing only returns one when an email was just put on or changed,
      // which is precisely when they can be let into a portal at last. It
      // reopens on the saved record, so the link sits under the client it
      // belongs to and a second Done doesn't add them twice.
      if (res.data?.link) {
        openClientForm(res.data.client);
        setLastClientLink(res.data.link);
      } else {
        setAddClientForm(null);
        setModal(null);
      }
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsSavingClient(false);
    }
  };

  // Admin sets/updates the in-person estimate appointment for a lead
  const handleSetAppointment = async () => {
    try {
      await axios.post(`/api/requests/${activeRequest._id}/appointment`, { appointmentAt: appointmentInput || null }, authHeaders());
      showToast('Appointment saved!');
      setModal(null);
      await loadAdminData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // Technician submits the partnership application
  // Runs after the admin taps Delete in the Confirm Deletion dialog.
  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    const { type, id } = confirmDelete;
    try {
      if (type === 'lead' || type === 'service request') {
        // The same record under two names: it is a lead on the pipeline board
        // and a service request at a property. Deleting takes the job and
        // everything filed on it — estimate, work order, photos.
        await axios.delete(`/api/requests/${id}`, authHeaders());
        setRequests(prev => prev.filter(r => r._id !== id));
        if (type === 'service request') await refreshAdminAccountDetail();
      } else if (type === 'client') {
        await axios.delete(`/api/auth/clients/${id}`, authHeaders());
        setClients(prev => prev.filter(c => c._id !== id));
      } else if (type === 'vendor') {
        await axios.delete(`/api/vendors/${id}`, authHeaders());
        setVendors(prev => prev.filter(v => v._id !== id));
      } else if (type === 'estimate') {
        const res = await axios.delete(`/api/requests/${id}/estimate`, authHeaders());
        setRequests(prev => prev.map(r => (r._id === id ? { ...r, ...res.data, estimate: null } : r)));
        setActiveRequest(prev => (prev?._id === id ? { ...prev, ...res.data, estimate: null } : prev));
        await refreshAdminAccountDetail();
      } else if (type === 'invoice') {
        // The whole bill, by its number — an invoice is every line raised
        // under it, and half a bill is not a thing anyone asked for.
        await axios.delete(`/api/projects/${id}/invoice/${encodeURIComponent(confirmDelete.number)}`, authHeaders());
        await refreshProjects();
      } else if (type === 'work order') {
        // The job stays; only the sheet raised against it goes. The number is
        // not reused — see the route.
        const res = await axios.delete(`/api/requests/${id}/work-order`, authHeaders());
        // `workOrder: null` last, and on purpose: spreading a response that no
        // longer carries the key would leave the old one in place.
        setRequests(prev => prev.map(r => (r._id === id ? { ...r, ...res.data, workOrder: null } : r)));
        setActiveRequest(prev => (prev?._id === id ? { ...prev, ...res.data, workOrder: null } : prev));
        await refreshAdminAccountDetail();
      }
      showToast(type === 'work order' ? 'Work order deleted'
        : type === 'invoice' ? 'Invoice deleted'
        : type === 'estimate' ? 'Estimate deleted' : 'Deleted');
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setConfirmDelete(null);
    }
  };

  // ══ Recording a licence check ══════════════════════════════════════════
  //
  // QPS looks the licence up on Utah DOPL by hand; this is what writes down
  // that it happened. The value is the record, not the lookup: "we verified
  // this" is only worth anything if it can be produced later with a date and a
  // name on it.
  // ══ The agents ═════════════════════════════════════════════════════════
  //
  // See server/agents/ for what actually runs. On this side there are only
  // three things: the board Julian reads, a technician answering an offer,
  // and a person telling the agents to leave a job alone.

  const [agentBoard, setAgentBoard] = useState(null);
  const [isLoadingBoard, setIsLoadingBoard] = useState(false);
  const [boardBrief, setBoardBrief] = useState(null);

  // `brief` costs a model call, so it is asked for on purpose rather than on
  // every poll. The board underneath it is complete without one.
  const loadAgentBoard = async ({ brief = false } = {}) => {
    setIsLoadingBoard(true);
    try {
      const res = await axios.get(`/api/agents/board${brief ? '?brief=1' : ''}`, authHeaders());
      setAgentBoard(res.data);
      if (res.data.brief) setBoardBrief(res.data.brief);
    } catch (err) {
      setAgentBoard({ rows: [], tally: { jobs: 0 }, error: err.response?.data?.message || err.message });
    } finally { setIsLoadingBoard(false); }
  };

  // A technician says yes or no. Declining is a first-class answer and costs
  // him nothing — a decline in ten seconds is worth far more to QPS than a
  // silence that wastes six hours and becomes a decline anyway.
  const [offerBusy, setOfferBusy] = useState(null);
  const respondToOffer = async (requestId, accept, reason) => {
    setOfferBusy(requestId);
    try {
      await axios.post(`/api/agents/job/${requestId}/respond`,
        { accept, reason }, authHeaders());
      showToast(accept ? 'Job accepted' : 'Thanks, we will pass it on');
      await loadVendorJobs();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally { setOfferBusy(null); }
  };

  // ══ Getting paid, and the W-9 ══════════════════════════════════════════
  //
  // The account number and the taxpayer ID are encrypted server-side (see
  // server/vault.js) and never come back down. Everything here works in terms
  // of the last four, which is all any screen needs.

  const [isSavingPayout, setIsSavingPayout] = useState(false);
  const savePayout = async (form) => {
    setIsSavingPayout(true);
    try {
      const res = await axios.post('/api/vendors/me/payout', form, authHeaders());
      setVendorProfile(res.data);
      showToast('Payment details saved');
      return true;
    } catch (err) {
      alert(err.response?.data?.message || err.message);
      return false;
    } finally { setIsSavingPayout(false); }
  };

  // The certification paragraph, from the server, so what a technician is
  // shown and what is stored against their signature are the same text.
  const [w9Wording, setW9Wording] = useState(null);
  const loadW9Wording = async () => {
    if (w9Wording) return;
    try {
      const res = await axios.get('/api/vendors/w9/form', authHeaders());
      setW9Wording(res.data);
    } catch { /* the form still works; the wording block just does not show */ }
  };

  const [isSigningW9, setIsSigningW9] = useState(false);
  const signW9 = async (form) => {
    setIsSigningW9(true);
    try {
      const res = await axios.post('/api/vendors/me/w9', form, authHeaders());
      setVendorProfile(res.data);
      showToast('W-9 signed');
      return true;
    } catch (err) {
      alert(err.response?.data?.message || err.message);
      return false;
    } finally { setIsSigningW9(false); }
  };

  // ── The admin side ──────────────────────────────────────────────────────
  //
  // Both of these decrypt, and both are written onto the vendor's record as a
  // look. Held in state only for as long as the panel is open.
  const [revealed, setRevealed] = useState(null);
  const revealPayout = async (vendorId) => {
    setRevealed(null);
    try {
      const res = await axios.get(`/api/vendors/${vendorId}/payout`, authHeaders());
      setRevealed({ kind: 'payout', vendorId, ...res.data });
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };
  const revealW9 = async (vendorId) => {
    setRevealed(null);
    try {
      const res = await axios.get(`/api/vendors/${vendorId}/w9`, authHeaders());
      setRevealed({ kind: 'w9', vendorId, ...res.data });
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  // Where uploaded files actually go. Read once when an admin signs in,
  // because the answer decides whether a missing photograph is a bug worth
  // chasing or a file that no longer exists.
  //
  // Render's local disk is wiped on every deploy. If the server is storing
  // there, every photo a technician uploads is lost at the next restart, and
  // no amount of fixing URLs brings one back.
  const [storage, setStorage] = useState(null);
  const loadStorage = async () => {
    try {
      const res = await axios.get('/api/health');
      setStorage(res.data);
    } catch { /* the dashboard works without it */ }
  };

  // The auto-dispatch switch. Ships off, and stays off until Julian turns it
  // on here — nothing about a deploy should start texting technicians.
  const [autoSetup, setAutoSetup] = useState(null);
  const [isSavingAuto, setIsSavingAuto] = useState(false);

  const loadAutoDispatch = async () => {
    try {
      const res = await axios.get('/api/agents/auto-dispatch', authHeaders());
      setAutoSetup(res.data);
    } catch { /* the board still works without the panel */ }
  };

  const saveAutoDispatch = async (patch) => {
    setIsSavingAuto(true);
    try {
      const res = await axios.post('/api/agents/auto-dispatch', patch, authHeaders());
      setAutoSetup(prev => ({ ...(prev || {}), settings: res.data.settings }));
      if ('enabled' in patch) {
        showToast(patch.enabled ? 'Auto-dispatch is on' : 'Auto-dispatch is off');
      }
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally { setIsSavingAuto(false); }
  };

  // Park a job. The reason is required by the server, and shows on the board
  // in place of the problem — a job on hold with no reason is how one goes
  // missing for a month.
  const pauseJob = async (requestId, paused, reason) => {
    try {
      await axios.post(`/api/agents/job/${requestId}/pause`,
        { paused, reason }, authHeaders());
      showToast(paused ? 'On hold' : 'Back on');
      await loadAgentBoard();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const [licenseForm, setLicenseForm] = useState(null);

  const openLicenseCheck = (vendor) => {
    if (!vendor) return;
    const v = vendor.licenseVerification || {};
    setLicenseForm({
      vendorId: vendor._id,
      businessName: vendor.businessName,
      // Seeded from what they told us, which is what gets typed into DOPL.
      licenseNumber: v.licenseNumber || vendor.profile?.licenseNumber || vendor.profile?.qualifierLicenseNumber || '',
      licenseType: v.licenseType || '',
      result: '',
      licenseExpiresAt: '',
      note: '',
    });
    setModal('licenseCheck');
  };

  const handleSaveLicenseCheck = async () => {
    const f = licenseForm;
    if (!f) return;
    if (!f.result) { alert('Pick what DOPL showed.'); return; }
    if (!f.licenseNumber.trim() && f.result !== 'not-required') {
      alert('Enter the licence number you looked up.');
      return;
    }
    setIsSubmittingApp(true);
    try {
      await axios.post(`/api/vendors/${f.vendorId}/license-check`, {
        licenseNumber: f.licenseNumber,
        licenseType: f.licenseType,
        result: f.result,
        licenseExpiresAt: f.licenseExpiresAt || undefined,
        note: f.note,
      }, authHeaders());
      showToast('Licence check recorded');
      setModal(null);
      setLicenseForm(null);
      await loadAdminData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally { setIsSubmittingApp(false); }
  };

  // The logo, or the tech's own photograph. Uploads the moment it is picked —
  // there is no form around it to submit.
  const [isSavingLogo, setIsSavingLogo] = useState(false);
  const handleVendorLogo = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) { alert('Pick an image — a JPEG or a PNG.'); return; }
    setIsSavingLogo(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const res = await axios.post('/api/vendors/me/logo', fd, {
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' },
      });
      setVendorProfile(res.data);
      showToast('Picture updated');
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally { setIsSavingLogo(false); }
  };

  const handleRemoveVendorLogo = async () => {
    if (!window.confirm('Remove your picture? Your initials go back on.')) return;
    setIsSavingLogo(true);
    try {
      const res = await axios.delete('/api/vendors/me/logo', authHeaders());
      setVendorProfile(res.data);
      showToast('Picture removed');
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally { setIsSavingLogo(false); }
  };

  // Open the onboarding wizard again, loaded with what is already on file.
  //
  // A technician's trades are not a thing they declare once. Somebody who
  // signed up doing demolition picks up drywall, moves, changes their phone,
  // forms an LLC. Without this the only way to correct any of it was to ask
  // QPS, and a routing table nobody can edit goes stale quietly.
  // Load what is on file into the form the profile page edits. Named for what
  // it does rather than where it goes, because the profile IS the editor now —
  // there is no second window to open.
  const seedVendorForm = () => {
    const v = vendorProfile || {};
    const p = v.profile || {};
    setVendorAppForm({
      businessName: v.businessName || '',
      phone: v.phone || '',
      address: p.address || '', city: p.city || '', state: p.state || 'UT', zip: p.zip || '',
      // Pulled onto the current spelling on the way in. A trade saved as
      // "Demolition and Junk Removal" is the same trade as "Demolition & Junk
      // Removal", and matching them by string meant the tech opened their own
      // profile and found an empty checklist under a trade they do.
      expertise: canonicalTrades(p.expertise), expertiseOther: p.expertiseOther || '',
      skills: canonicalSkills(p.skills), skillsOther: p.skillsOther || '',
      yearsExperience: p.yearsExperience || '',
      hasTools: p.hasTools || '',
      hasCrew: p.hasCrew || '', crewSize: p.crewSize || '',
      einNumber: p.einNumber || '',
      insurancePolicyNumber: p.insurancePolicyNumber || '',
      workersCompType: p.workersCompType || '',
      hasQualifier: p.hasQualifier || '',
      qualifierName: p.qualifierName || '',
      qualifierLicenseNumber: p.qualifierLicenseNumber || '',
    });
    // Files are never pre-filled — the server keeps whatever is already on
    // file unless a new one is actually attached, so an empty slot here means
    // "unchanged", not "delete it".
    setVendorDocs({ w9: null, insurance: null, workersComp: null, contractorLicense: null });
  };

  // Save from the profile page. No step gate to mirror and nothing to navigate
  // to: it validates nothing beyond what the server will, because a tech
  // correcting a phone number must not be stopped by a trade they have not
  // ticked yet. Returns whether it stuck, so the page knows to drop its
  // unsaved-changes bar.
  const saveVendorProfile = async () => {
    const ok = await postVendorProfile({ validate: false });
    if (ok) showToast(t('toast.profileUpdated'));
    return ok;
  };

  // `finish` is the last button of the wizard. Without it this is a step save:
  // the tech pressed Continue while editing a profile that already exists, and
  // what they typed on the step behind them is written now rather than held in
  // a form until they reach the end.
  //
  // That is the whole of the bug Julian hit. City and ZIP were not on his
  // record, the step would not let him past without them, he typed them, and
  // then left before step three — so nothing was ever saved and the next visit
  // asked again. A form that only saves at the end loses everything a person
  // does not finish in one sitting, and nothing on the screen says so.
  const postVendorProfile = async ({ validate }) => {
    const f = vendorAppForm;
    if (validate) {
      // Mirrors the wizard's own step gates, so nothing can pass three steps and
      // then be rejected here.
      if (!f.businessName || !f.phone) { alert(t('err.appBusiness', 'Business name and phone are required.')); return false; }
      if (!f.address || !f.city || !f.state || !f.zip) { alert(t('err.appAddress2', 'A full business address is required — street, city, state and ZIP.')); return false; }
      if (f.expertise.length === 0) { alert(t('err.appExpertise')); return false; }
      if (f.skills.length === 0) { alert(t('err.appSkills')); return false; }
      if (!f.yearsExperience) { alert(t('err.appYears', 'Years of experience is required.')); return false; }
    }
    setIsSubmittingApp(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => {
        fd.append(k, Array.isArray(v) ? JSON.stringify(v) : v);
      });
      // Each file goes under its own field name, so the server knows which is
      // the W-9 and which is the insurance without opening either.
      Object.entries(vendorDocs).forEach(([field, file]) => {
        if (file) fd.append(field, file);
      });
      const res = await axios.post('/api/vendors/profile', fd, {
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' }
      });
      setVendorProfile(res.data);
      setVendorDocs({ w9: null, insurance: null, workersComp: null, contractorLicense: null });
      // The profile saves even when a document could not be uploaded — say so
      // rather than letting them think the file went through.
      if (res.data.uploadWarning) alert(res.data.uploadWarning);
      return true;
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
      return false;
    } finally {
      setIsSubmittingApp(false);
    }
  };

  // The onboarding wizard's last button. The full gate applies here and
  // nowhere else: this is the moment a technician joins the roster, and a
  // profile with no trades on it is one QPS can never send a job to.
  // A technician about to send their own customer a document, with no way to
  // be paid for it online. Free to join, asked at the money — the same place
  // every marketplace asks.
  const openSendToCustomer = (payload) => {
    const unpaid = currentUser?.role === 'vendor' && !vendorProfile?.chargesEnabled;
    if (unpaid) { setStripeGate(payload); return; }
    setSendCustomer(payload);
  };

  // "Set up payments" from that sheet. Straight to Stripe when they already
  // agreed to the fee during signup, and to the Payments screen when they have
  // not, because the agreement belongs in front of them, not behind a redirect.
  const startStripeFromGate = async () => {
    if (!vendorProfile?.platformTermsAcceptedAt) {
      setStripeGate(null);
      setPaperDoc(null);
      setVendorView('payments');
      return;
    }
    setStripeGateBusy(true);
    try {
      const res = await axios.post('/api/connect/onboard', { acceptTerms: true }, authHeaders());
      window.location.href = res.data.url;
    } catch {
      setStripeGate(null);
      setStripeGateBusy(false);
      setPaperDoc(null);
      setVendorView('payments');
    }
  };

  const handleSubmitVendorApp = async ({ connectStripe } = {}) => {
    const ok = await postVendorProfile({ validate: true });
    if (!ok) return;
    showToast(t('toast.appSubmitted'));

    // Straight on to Stripe, while they are sitting down with their details to
    // hand. The application is already saved, so leaving the site costs them
    // nothing: whatever happens at Stripe, their answers are in.
    if (connectStripe) {
      try {
        const res = await axios.post('/api/connect/onboard', { acceptTerms: true }, authHeaders());
        window.location.href = res.data.url;
        return;
      } catch {
        // Stripe unreachable, or online payments not switched on yet. Land them
        // on the Payments screen rather than throwing an error at somebody who
        // has done nothing wrong — that screen says where things stand and
        // carries the button to try again.
        setVendorView('payments');
        return;
      }
    }

    // Back to the portal. The wizard used to BE the screen until it was
    // finished, so finishing it revealed the portal on its own. Now it is
    // one screen among the others and has to hand back deliberately.
    setVendorView('home');
  };

  // Logged-in user changes their own password
  const handleChangePassword = async () => {
    // Translated, because this screen is no longer admin-only. A technician
    // who works in Spanish should not hit an English wall on the one screen
    // that controls whether they can get back into the app.
    if (passwordForm.next.length < 8) { alert(t('pw.tooShort')); return; }
    if (passwordForm.next !== passwordForm.confirm) { alert(t('pw.mismatch')); return; }
    try {
      await axios.post('/api/auth/change-password', {
        currentPassword: passwordForm.current,
        newPassword: passwordForm.next
      }, authHeaders());
      showToast(t('pw.updated'));
      setModal(null);
      setPasswordForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleChangeEmail = async () => {
    if (!/^\S+@\S+\.\S+$/.test(emailForm.next.trim())) { alert(t('em.invalid')); return; }
    try {
      const res = await axios.post('/api/auth/change-email', {
        currentPassword: emailForm.current,
        newEmail: emailForm.next,
      }, authHeaders());
      // Keep the signed-in user in step, or the app shows the old address
      // until the next sign-out. The session effect writes it to the device.
      if (res.data.user) setCurrentUser(prev => ({ ...prev, ...res.data.user }));
      showToast(t('em.updated'));
      setModal(null);
      setEmailForm({ current: '', next: '' });
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // ── Estimate Approval (Homeowner) ─────────────────────────

  const handleApproveEstimate = async (approved) => {
    try {
      await axios.post(`/api/requests/${activeRequest._id}/approve-estimate`, { approved }, authHeaders());
      showToast(approved ? 'Estimate approved!' : 'Estimate declined.');
      await loadHomeownerRequests();
      goTo('screen-homeowner-dashboard');
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // ── Completion & Invoice (Vendor) ─────────────────────────

  const invoiceTotal = invoiceForm.lineItems.reduce((sum, r) => sum + (parseFloat(r.total) || 0), 0);

  const handleSubmitCompletion = async () => {
    if (completionFiles.length === 0) { alert('Please add at least one photo.'); return; }
    try {
      const fd = new FormData();
      fd.append('note', completionNote);
      completionFiles.forEach(f => fd.append('photos', f));
      await axios.post(`/api/requests/${activeRequest._id}/completion`, fd, {
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' }
      });
      showToast('Completion submitted!');
      setModal(null);
      setCompletionFiles([]);
      setCompletionNote('');
      await loadVendorJobs();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmitInvoice = async () => {
    try {
      const res = await axios.post(`/api/requests/${activeRequest._id}/invoice`, {
        amount: invoiceTotal,
        lineItems: invoiceForm.lineItems.filter(r => r.description),
        notes: invoiceForm.notes,
        // Only used where the job has no usable address. The server ignores it
        // otherwise, so this can fill a gap but never redirect a real bill.
        customerEmail: invoiceForm.customerEmail || '',
      }, authHeaders());

      // ── SAY WHAT ACTUALLY HAPPENED ──────────────────────────────────────
      //
      // "Invoice submitted!" was shown whatever the outcome, including the
      // outcome where the email went to a made-up address and the customer
      // never heard a thing. Three different events, three different lines.
      const d = res.data || {};
      if (d.emailed) {
        showToast(`Invoice sent to ${d.emailTo}`);
      } else if (d.emailProblem === 'no-address') {
        alert('The invoice is saved, but there is no email address on this job, '
          + 'so nothing was sent. Add the customer\u2019s email, or copy the pay '
          + 'link and text it to them.');
      } else {
        alert('The invoice is saved, but the email did not go through'
          + (d.emailTo ? ` to ${d.emailTo}` : '')
          + '. Copy the pay link and send it another way.'
          + (d.emailProblem ? `\n\nWhat the mail service said: ${d.emailProblem}` : ''));
      }
      setModal(null);
      setInvoiceForm({ lineItems: [{ description: '', qty: 1, unitCost: '', total: '' }], notes: '', customerEmail: '' });
      // Two people raise invoices now — the tech closing out his own job, and
      // Julian billing a management company — and they are looking at two
      // different boards. Refresh whichever one the invoice was raised from.
      if (currentUser?.role === 'admin') {
        await loadAdminData();
        await refreshAdminAccountDetail();
      } else {
        await loadVendorJobs();
      }
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // ── Vendor Ledger ─────────────────────────────────────────

  // Opens the new/edit invoice modal. Pass an entry to edit it, nothing to
  // start a fresh row.
  const openLedgerForm = (entry) => {
    setLedgerForm(entry ? {
      _id: entry._id,
      vendorId: entry.vendorId?._id || entry.vendorId || '',
      requestId: entry.requestId?._id || entry.requestId || '',
      invoiceNumber: entry.invoiceNumber || '',
      serviceAddress: entry.serviceAddress || '',
      jobDetails: entry.jobDetails || '',
      invoiceAmount: entry.invoiceAmount ?? '',
      notes: entry.notes || '',
      externalUrl: entry.externalUrl || '',
      status: entry.status || 'open'
    } : {
      _id: '', vendorId: '', requestId: '', invoiceNumber: '',
      serviceAddress: '', jobDetails: '', invoiceAmount: '', notes: '', externalUrl: '', status: 'open'
    });
    setLedgerDocFiles([]);
    setModal('ledgerEntry');
  };

  // Creates a new ledger row, or saves edits to an existing one.
  const handleSaveLedgerEntry = async () => {
    const f = ledgerForm;
    if (!f.invoiceAmount || parseFloat(f.invoiceAmount) <= 0) { alert('Enter the invoice amount.'); return; }
    if (currentUser?.role === 'admin' && !f._id && !f.vendorId) { alert('Pick which vendor this invoice is from.'); return; }
    setIsSavingLedger(true);
    try {
      if (f._id) {
        await axios.patch(`/api/ledger/${f._id}`, {
          invoiceNumber: f.invoiceNumber, serviceAddress: f.serviceAddress,
          jobDetails: f.jobDetails, invoiceAmount: f.invoiceAmount,
          notes: f.notes, externalUrl: f.externalUrl, status: f.status
        }, authHeaders());
        showToast('Ledger entry updated');
      } else {
        const fd = new FormData();
        ['vendorId', 'requestId', 'invoiceNumber', 'serviceAddress', 'jobDetails', 'invoiceAmount', 'notes', 'externalUrl']
          .forEach(k => { if (f[k]) fd.append(k, f[k]); });
        // Lets the server skip the translation call when the writer is already
        // working in English — see server/translate.js.
        fd.append('writerLang', currentLanguage());
        ledgerDocFiles.forEach(file => fd.append('invoiceDocs', file));
        await axios.post('/api/ledger', fd, {
          headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' }
        });
        showToast(currentUser?.role === 'vendor' ? '🧾 Invoice submitted for payment' : 'Ledger entry added');
      }
      setModal(null);
      setLedgerDocFiles([]);
      await refreshData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsSavingLedger(false);
    }
  };

  const handleDeleteLedgerEntry = async (entry) => {
    if (!window.confirm(`Delete invoice ${entry.invoiceNumber}? This can't be undone.`)) return;
    try {
      await axios.delete(`/api/ledger/${entry._id}`, authHeaders());
      showToast('Ledger entry deleted');
      await refreshData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const openRecordPayment = (entry) => {
    setActiveEntry(entry);
    const balance = (entry.invoiceAmount || 0) - (entry.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
    setPaymentForm({
      amount: balance > 0 ? balance.toFixed(2) : '',
      date: new Date().toISOString().slice(0, 10),
      mode: 'Check', reference: '', note: '', overrideReason: ''
    });
    setPaymentBlock(null);
    setModal('payment');
  };

  // Records a payment. If the tech's insurance/licenses have lapsed the server
  // refuses with 409 — we surface the blockers and let the admin either fix the
  // paperwork or deliberately override with a written reason.
  const handleRecordPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) { alert('Enter a payment amount.'); return; }
    try {
      await axios.post(`/api/ledger/${activeEntry._id}/payments`, paymentForm, authHeaders());
      showToast('💵 Payment recorded');
      setModal(null);
      setPaymentBlock(null);
      await refreshData();
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.requiresOverride) {
        setPaymentBlock(err.response.data);
        return; // keep the modal open so they can see why and decide
      }
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDeletePayment = async (entry, payment) => {
    if (!window.confirm(`Remove this $${Number(payment.amount).toFixed(2)} payment from ${entry.invoiceNumber}?`)) return;
    try {
      await axios.delete(`/api/ledger/${entry._id}/payments/${payment._id}`, authHeaders());
      showToast('Payment removed');
      await refreshData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const openProgress = (entry) => {
    setActiveEntry(entry);
    setProgressForm({ note: '', percentComplete: '' });
    setProgressFiles([]);
    setModal('progress');
  };

  // Posts a live progress report (photos + note + % complete) onto an invoice.
  const handleAddProgress = async () => {
    if (!progressForm.note && progressFiles.length === 0) { alert('Add a note or at least one photo.'); return; }
    setIsSavingLedger(true);
    try {
      const fd = new FormData();
      fd.append('note', progressForm.note);
      if (progressForm.percentComplete !== '') fd.append('percentComplete', progressForm.percentComplete);
      fd.append('authorName', currentUser?.name || '');
      progressFiles.forEach(file => fd.append('photos', file));
      await axios.post(`/api/ledger/${activeEntry._id}/progress`, fd, {
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' }
      });
      showToast('📸 Progress update posted');
      setModal(null);
      setProgressFiles([]);
      await refreshData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsSavingLedger(false);
    }
  };

  // Downloads the whole ledger as a CSV laid out like the original spreadsheet.
  const handleExportLedger = async () => {
    try {
      const res = await axios.get('/api/ledger/export/csv', { ...authHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'vendor-ledger.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error exporting: ' + (err.response?.data?.message || err.message));
    }
  };

  // ── Compliance documents ──────────────────────────────────

  // `item` is a row from the compliance panel; vendorId is only passed when an
  // admin is uploading on a tech's behalf.
  const openCredential = (item, vendorId = '') => {
    setCredentialForm({
      vendorId,
      type: item?.type || 'insurance',
      label: item?.type === 'other' ? (item.label || '') : '',
      issuer: item?.issuer || '',
      number: item?.number || '',
      issuedAt: '',
      expiresAt: ''
    });
    setCredentialFile(null);
    setModal('credential');
  };

  const handleSaveCredential = async () => {
    const f = credentialForm;
    // A W-9 has no expiry, so there is nothing to ask for and nothing to
    // remind about. Demanding a date here would block the one document that
    // does not have one.
    if (!neverExpires(f.type) && !f.expiresAt) {
      alert('Enter the expiration date — that\'s what the renewal reminders are based on.');
      return;
    }
    setIsSavingLedger(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (credentialFile) fd.append('document', credentialFile);
      const res = await axios.post('/api/vendors/credentials', fd, {
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' }
      });
      if (currentUser?.role === 'vendor') setVendorProfile(res.data);
      showToast('📄 Document saved');
      setModal(null);
      setCredentialFile(null);
      await refreshData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsSavingLedger(false);
    }
  };

  const handleDeleteCredential = async (item, vendorId = '') => {
    if (!window.confirm(`Remove ${item.label}?`)) return;
    try {
      const qs = vendorId ? `?vendorId=${vendorId}` : '';
      const res = await axios.delete(`/api/vendors/credentials/${item._id}${qs}`, authHeaders());
      if (currentUser?.role === 'vendor') setVendorProfile(res.data);
      showToast('Document removed');
      await refreshData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // The homeowner opened a work order, so everything on it has now been seen.
  //
  // The local copy is stamped straight away rather than waiting for a reload —
  // the dot has to clear as the screen opens, not a poll later. The server call
  // is best-effort: failing to record that someone looked at their own job is
  // not worth an error message.
  const markRequestSeen = async (requestId) => {
    if (!requestId || !['homeowner', 'manager'].includes(currentUser?.role)) return;
    const at = new Date().toISOString();
    setRequests(prev => prev.map(r => (r._id === requestId ? { ...r, homeownerSeenAt: at } : r)));
    try {
      await axios.post(`/api/requests/${requestId}/seen`, {}, authHeaders());
    } catch { /* looked at is not a transaction */ }
  };

  // ── Messages ──────────────────────────────────────────────

  // Opens the conversation for a job. Reading it clears that thread's badge.
  const openThread = async (request) => {
    setThreadFor(request);
    setThread([]);
    setMessageText('');
    setMessageFiles([]);
    setModal('messages');
    setIsLoadingThread(true);
    try {
      const res = await axios.get(`/api/messages/${request._id}`, authHeaders());
      setThread(res.data);
      setUnreadCounts(prev => ({ ...prev, [request._id]: 0 }));
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsLoadingThread(false);
    }
  };

  // Re-pull an open thread in the background. No spinner and no scroll reset —
  // this runs on a timer, so it must be invisible unless something new arrived.
  const refreshOpenThread = async (requestId) => {
    try {
      const res = await axios.get(`/api/messages/${requestId}`, authHeaders());
      setThread(prev => (prev.length === res.data.length ? prev : res.data));
      setUnreadCounts(prev => ({ ...prev, [requestId]: 0 }));
    } catch { /* a timer tick is not worth interrupting anyone over */ }
  };

  // The far side of a job thread is someone holding a phone, so the thread has
  // to move on its own — refreshing the page to see a reply is not a
  // conversation. Polls only while the thread is actually open.
  useEffect(() => {
    if (modal !== 'messages' || !threadFor?._id || !authToken) return;
    const id = setInterval(() => refreshOpenThread(threadFor._id), 10000);
    return () => clearInterval(id);
  }, [modal, threadFor, authToken]); // eslint-disable-line

  // A customer's work orders move without them doing anything — an estimate
  // lands, a visit gets booked — so the list refreshes itself while they have
  // it open. Slower than the unread poll: this is "did anything happen today?",
  // not a conversation.
  useEffect(() => {
    if (!authToken || currentScreen !== 'screen-homeowner-dashboard') return;
    const id = setInterval(loadHomeownerRequests, 60000);
    return () => clearInterval(id);
  }, [authToken, currentScreen]); // eslint-disable-line

  // Unread badges keep counting while a dashboard is open, so a new message
  // announces itself without the page being reloaded.
  useEffect(() => {
    if (!authToken || !currentScreen.endsWith('-dashboard')) return;
    const id = setInterval(loadUnread, 20000);
    return () => clearInterval(id);
  }, [authToken, currentScreen]); // eslint-disable-line

  // Say so when the count goes up. Deliberately not the browser's Notification
  // API: on iOS that only works for an installed PWA, so it would do nothing on
  // the phone Julian actually uses and look like another broken feature.
  const prevUnread = useRef(null);
  useEffect(() => {
    const total = Object.values(unreadCounts).reduce((sum, n) => sum + n, 0);
    // First reading is the baseline, never an alert.
    if (prevUnread.current !== null && total > prevUnread.current) {
      showToast(total - prevUnread.current === 1 ? 'New message' : `${total - prevUnread.current} new messages`);
    }
    prevUnread.current = total;
  }, [unreadCounts]); // eslint-disable-line

  // ── Installing, and working on a bad signal ───────────────
  // The worker is registered on every visit, not only when notifications are
  // switched on: it is also what makes the app open and job data readable with
  // no signal, which matters in the crawlspaces techs actually work in.
  useEffect(() => { registerServiceWorker(); }, []);

  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const online = () => setIsOffline(false);
    const offline = () => setIsOffline(true);
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    return () => {
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
    };
  }, []);

  // Chrome decides when installing is possible and tells us via an event, so
  // the button has to appear and disappear on its own.
  const [canInstall, setCanInstall] = useState(canPromptInstall());
  useEffect(() => onInstallAvailabilityChange(setCanInstall), []);

  const handleInstall = async () => {
    const accepted = await promptInstall();
    if (accepted) showToast('Installed — open it from your home screen');
  };

  // ── Push notifications ────────────────────────────────────
  // The ones that arrive when the site is shut. See client/src/lib/push.js for
  // why iOS makes this conditional on being installed to the Home Screen.
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    if (!authToken) { setPushOn(false); return; }
    isPushOnThisDevice().then(setPushOn);
  }, [authToken]);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushOn) {
        await disablePush(authHeaders());
        setPushOn(false);
        showToast('Notifications turned off on this device');
      } else {
        await enablePush(authHeaders());
        setPushOn(true);
        showToast('Notifications are on for this device');
      }
    } catch (err) {
      alert(err.message || 'Could not change notifications');
    } finally {
      setPushBusy(false);
    }
  };

  // Proves the whole round trip, which you otherwise cannot check without
  // waiting for a real customer to text you.
  const sendTestPush = async () => {
    try {
      const res = await axios.post('/api/push/test', {}, authHeaders());
      showToast(res.data.sent > 0
        ? `Test sent to ${res.data.sent} device${res.data.sent > 1 ? 's' : ''}`
        : 'No devices are signed up for notifications yet');
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // Every inbound text Twilio handed us and what became of it. Answers "did my
  // reply arrive?" without a Render login.
  const refreshSmsStatus = async () => {
    setIsLoadingSms(true);
    try {
      const res = await axios.get('/api/messages/sms-status', authHeaders());
      setSmsStatus({ ...res.data, loaded: true });
    } catch (err) {
      setSmsStatus({ enabled: false, events: [], loaded: true, error: err.response?.data?.message || err.message });
    } finally {
      setIsLoadingSms(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() && messageFiles.length === 0) return;
    setIsSendingMessage(true);
    try {
      const fd = new FormData();
      fd.append('body', messageText);
      messageFiles.forEach(f => fd.append('attachments', f));
      const res = await axios.post(`/api/messages/${threadFor._id}`, fd, {
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' }
      });
      setThread(prev => [...prev, res.data]);
      setMessageText('');
      setMessageFiles([]);
      // The message saved but a file was refused — say which, or they'll think
      // the photo went out. Cloudinary's own wording is the useful part.
      if (res.data.uploadWarning) {
        alert(`Your message was sent, but the file could not be attached: ${res.data.uploadWarning}\n\nTry a smaller file, or a photo instead of a video.`);
      }
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsSendingMessage(false);
    }
  };

  // ── Projects ──────────────────────────────────────────────

  const refreshProjects = async () => {
    try {
      const res = await axios.get('/api/projects', authHeaders());
      setProjects(res.data);
      // Keep the open project in sync after an edit.
      if (activeProject) {
        const fresh = res.data.find(p => p._id === activeProject._id);
        if (fresh) setActiveProject(fresh);
      }
    } catch { /* silently fail */ }
  };

  const openProjectForm = (project) => {
    setProjectForm(project ? {
      _id: project._id, title: project.title || '',
      homeownerId: project.homeownerId?._id || project.homeownerId || '',
      clientName: project.clientName || '', address: project.address || '',
      status: project.status || 'active', notes: project.notes || ''
    } : { _id: '', title: '', homeownerId: '', clientName: '', address: '', status: 'active', notes: '' });
    setModal('project');
  };

  const handleSaveProject = async () => {
    if (!projectForm.title) { alert('Give the project a name.'); return; }
    setIsSavingProject(true);
    try {
      const body = { ...projectForm };
      delete body._id;
      if (projectForm._id) await axios.patch(`/api/projects/${projectForm._id}`, body, authHeaders());
      else await axios.post('/api/projects', body, authHeaders());
      showToast(projectForm._id ? 'Project updated' : 'Project created');
      setModal(null);
      await refreshProjects();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally { setIsSavingProject(false); }
  };

  const handleDeleteProject = async (project) => {
    if (!window.confirm(`Delete "${project.title}" and everything on it?`)) return;
    try {
      await axios.delete(`/api/projects/${project._id}`, authHeaders());
      setActiveProject(null);
      showToast('Project deleted');
      await refreshProjects();
    } catch (err) { alert('Error: ' + (err.response?.data?.message || err.message)); }
  };

  const openProjectLine = (project, line) => {
    setActiveProject(project);
    // ── THE DATE THE WORK WAS BILLED ────────────────────────────────────
    //
    // This sheet never asked for one, so every line a statement was built
    // from got stamped with the day it was typed in. Julian loading six
    // months of the Skanchy basement had demolition from April reading
    // 31 October on the document Olivia reads. The server has always
    // accepted the field; only the form was missing it.
    const asInput = (d) => {
      if (!d) return todayInput();
      const t = new Date(d);
      return Number.isNaN(t.getTime()) ? todayInput()
        : `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
    };
    setLineForm(line ? {
      _id: line._id, trade: line.trade || '', invoiceNumber: line.invoiceNumber || '',
      invoiceUrl: line.invoiceUrl || '', amount: line.amount ?? '',
      invoiceDate: asInput(line.invoiceDate),
      vendorId: line.vendorId || '', techCost: line.techCost ?? ''
    } : {
      _id: '', trade: '', invoiceNumber: '', invoiceUrl: '', amount: '',
      invoiceDate: todayInput(), vendorId: '', techCost: '',
    });
    setModal('projectLine');
  };

  // ── Invoicing a client ────────────────────────────────────────────────
  // An invoice here is a dated, numbered set of lines on that client's project
  // statement — the same statement the homeowner reads in their portal and the
  // one Customer Snapshots add up. One sheet serves both ways in: a blank
  // invoice from their page, and an estimate converted into one.
  const [clientInvoiceForm, setClientInvoiceForm] = useState(null);
  const [isSavingInvoice, setIsSavingInvoice] = useState(false);

  const todayInput = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  // A stored date as the value an <input type="date"> wants. Anything it
  // cannot read comes back empty, so the field is blank rather than showing
  // the browser's idea of Invalid Date.
  const dateInputFrom = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  // ── More, on a document ────────────────────────────────────────────────
  // Duplicate is the same "new" flow the buttons already open, arriving with
  // last time's lines in it. A repeat customer's third turn is the second one
  // with two lines changed, and retyping it is how a price drifts.
  const duplicateEstimate = (request) => {
    const client = {
      _id: request.homeownerId?._id || request.homeownerId,
      name: request.homeownerId?.name || request.fullName,
      phone: request.phone, email: request.email,
      address: request.serviceAddress, city: request.city,
    };
    setPrintDoc(null);
    openEstimateForClient(client);
    setEstimateForm({
      breakdown: estimateRowsOf(request).map(r => ({
        label: r.label || '',
        description: r.description || '',
        photos: r.photos || [],
        cost: r.cost != null ? String(r.cost) : '',
        qty: r.qty != null ? String(r.qty) : '',
        rate: r.rate != null ? String(r.rate) : '',
      })),
      notes: request.estimate?.notes || '',
    });
  };

  const duplicateInvoice = (invoice) => {
    if (!invoice?.client) return;
    setPrintDoc(null);
    openClientInvoice(invoice.client);
    setClientInvoiceForm(p => ({
      ...p,
      projectId: invoice.project?._id || '',
      title: invoice.project?.title || '',
      lines: (invoice.lines || [])
        .filter(l => !['discount', 'tax'].includes((l.trade || '').trim().toLowerCase()))
        .map(l => ({
          label: l.trade || '',
          description: l.notes || '',
          photos: [],
          cost: l.amount != null ? String(l.amount) : '',
          qty: l.qty != null ? String(l.qty) : '',
          rate: l.rate != null ? String(l.rate) : '',
        })),
    }));
  };

  const askDeleteEstimate = (request) => {
    setPrintDoc(null);
    setConfirmDelete({
      type: 'estimate', id: request._id,
      label: [request.items?.[0]?.title || request.serviceType, fmtMoney(request.estimate?.amount)].filter(Boolean).join(' · '),
    });
  };

  const askDeleteInvoice = (invoice) => {
    setPrintDoc(null);
    setConfirmDelete({
      type: 'invoice', id: invoice.project?._id, number: invoice.number,
      label: [invoice.number ? `#${invoice.number}` : 'This invoice', fmtMoney(invoice.total)].filter(Boolean).join(' · '),
    });
  };

  // One invoice, as the sheet of paper the customer gets.
  //
  // Every invoice prints with its pay link and a QR code for it. Projects
  // billed before online payment existed have no link yet, so one is minted on
  // the way — and if that fails the invoice still opens, just without it.
  const openInvoiceDoc = async (invoice) => {
    let payUrl = null;
    if (invoice.project?._id) {
      try {
        payUrl = await getPayLink({ projectId: invoice.project._id, token: invoice.project.payToken, auth: authHeaders() });
      } catch { /* print without it */ }
    }
    setPrintDoc({ invoice: { ...invoice, payUrl }, kind: 'invoice' });
  };

  // A job's invoice as the sheet of paper it is, rather than the form it was
  // typed into. Julian clicked "View / edit invoice" and got an editor; there
  // was no way to look at the bill, print it, or save it as a PDF.
  //
  // Same document component as every other printed thing, so it carries the
  // letterhead, the pay link and the QR code without a second layout to keep
  // in step.
  // Moving a job's card between the two stages that are pure bookkeeping.
  // Everything else on that board is the result of an action and has its own
  // route; the server refuses the rest in words rather than silently obliging.
  const setLeadStatus = async (request, status) => {
    try {
      const res = await axios.patch(`/api/requests/${request._id}/stage`, { status }, authHeaders());
      setRequests(prev => prev.map(r => (r._id === request._id ? res.data : r)));
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  // Clicking a lead on the board. Julian asked to land on the actual estimate,
  // with everything he can do to it in a row above it — not on the form it was
  // typed into, which is where a line priced at $0.00 goes unnoticed.
  const openLeadDoc = (request) => {
    setActiveRequest(request);
    setLeadDoc({ id: request._id });
  };

  // What a send or a recorded payment hands back: the whole job, re-read. One
  // place, so the board, the card and the open sheet can never disagree about
  // whether an invoice is paid.
  const onLeadSaved = (updated) => {
    setRequests(prev => prev.map(r => (r._id === updated._id ? updated : r)));
    setActiveRequest(prev => (prev && prev._id === updated._id ? updated : prev));
  };

  const openJobInvoiceDoc = async (request) => {
    const inv = request?.invoice;
    if (!inv?.amount && inv?.amount !== 0) { alert('There is no invoice on this job yet.'); return; }

    let payUrl = null;
    try {
      payUrl = await getPayLink({ requestId: request._id, token: request.payToken, auth: authHeaders() });
    } catch { /* print it without one rather than not at all */ }

    const lines = (inv.lineItems || []).length
      ? inv.lineItems.map((l, i) => ({
        _id: `l${i}`,
        trade: l.description || 'Work',
        qty: l.qty,
        rate: l.unitCost,
        amount: l.total,
      }))
      // An invoice raised as a single figure still has to print as a line.
      : [{ _id: 'l0', trade: request.serviceType || 'Work', amount: inv.amount }];

    setPrintDoc({
      kind: 'invoice',
      invoice: {
        client: {
          name: request.fullName,
          email: request.email,
          phone: request.phone,
          address: request.serviceAddress,
          address2: request.address2,
          city: request.city,
          state: request.state,
          zip: request.zip,
          // Bill To follows the billing address when there is one.
          billingAddress: request.billingSame === false ? request.billingAddress : '',
          billingAddress2: request.billingSame === false ? request.billingAddress2 : '',
          billingCity: request.billingSame === false ? request.billingCity : '',
          billingState: request.billingSame === false ? request.billingState : '',
          billingZip: request.billingSame === false ? request.billingZip : '',
        },
        lines,
        total: inv.amount,
        // A job's invoice has no number of its own, so the work order number
        // is what both sides can quote down the phone. A dash rather than an
        // invented number when there is neither.
        number: request.workOrder?.number || '',
        date: inv.submittedAt || request.updatedAt,
        project: { title: request.serviceType || '' },
        notes: inv.notes,
        payUrl,
      },
    });
  };

  const openClientInvoice = (client) => {
    setClientInvoiceForm({
      client,
      // ── EMAILING IT STARTS OFF ────────────────────────────────────────
      // Julian's call, and it matches how he works: he raises a bill, looks
      // at it, and sends the customer a pay link himself. Most of what he
      // enters here is the statement catching up on work already agreed and
      // often already paid, and an email demanding money for it is the one
      // mistake the customer sees.
      //
      // The SERVER still defaults to sending when `notify` is absent, so an
      // API caller that knows nothing about this switch cannot silence a
      // real bill. Only this form starts it off.
      notify: false,
      requestId: '',
      // Blank means a new project. Most invoices belong to one already —
      // billing a basement finish in five trades is five invoices on one
      // statement, not five projects.
      projectId: '',
      title: '',
      invoiceNumber: '',
      invoiceDate: todayInput(),
      discount: '',
      tax: '',
      lines: [{ label: '', description: '', photos: [], cost: '' }],
    });
    setModal('clientInvoice');
  };

  // The Invoice button on an estimate: the price that was agreed, carried over
  // line for line so nothing is retyped and nothing drifts from what the
  // customer said yes to. Still editable — the job on site is rarely the job
  // on paper to the penny.
  const openInvoiceFromEstimate = (client, request) => {
    const rows = (request.estimate?.breakdown || []).filter(r => r.label || r.cost);
    setClientInvoiceForm({
      client,
      // ── EMAILING IT STARTS OFF ────────────────────────────────────────
      // Julian's call, and it matches how he works: he raises a bill, looks
      // at it, and sends the customer a pay link himself. Most of what he
      // enters here is the statement catching up on work already agreed and
      // often already paid, and an email demanding money for it is the one
      // mistake the customer sees.
      //
      // The SERVER still defaults to sending when `notify` is absent, so an
      // API caller that knows nothing about this switch cannot silence a
      // real bill. Only this form starts it off.
      notify: false,
      // On by default here, and only here. An estimate with several trades on
      // it is exactly the case Julian splits by hand every time; a bill typed
      // from scratch is usually one thing and stays one bill.
      split: rows.length > 1,
      requestId: request._id,
      projectId: '',
      title: request.items?.[0]?.title || request.serviceType || 'Work',
      invoiceNumber: '',
      invoiceDate: todayInput(),
      discount: '',
      tax: '',
      // Line for line, scope and photos included: the customer agreed to a
      // description as much as to a number, and the bill should say the same
      // thing the estimate did.
      lines: rows.length
        ? rows.map(r => ({
            label: r.label || request.serviceType || 'Work',
            description: r.description || '',
            photos: r.photos || [],
            cost: String(r.cost ?? ''),
          }))
        : [{
            label: request.items?.[0]?.title || request.serviceType || 'Work',
            description: request.items?.[0]?.description || '',
            photos: [],
            cost: String(request.estimate?.amount ?? ''),
          }],
    });
    setModal('clientInvoice');
  };

  const handleSaveClientInvoice = async () => {
    const f = clientInvoiceForm;
    if (!f) return;
    const lines = f.lines
      .map(l => ({
        trade: (l.label || '').trim(),
        amount: parseFloat(l.cost) || 0,
        // The scope goes onto the statement line's notes, which is where the
        // homeowner's own statement already reads it from.
        notes: (l.description || '').trim(),
      }))
      .filter(l => l.trade || l.amount);
    if (!lines.length) { alert('An invoice needs at least one line.'); return; }
    if (!f.projectId && !f.title.trim()) { alert('Name the project this invoice belongs to.'); return; }
    setIsSavingInvoice(true);
    try {
      // A date input gives a bare day, which the server stores as UTC midnight
      // — and Utah is six hours behind, so the statement would print the day
      // before. Sent as noon that day instead, it reads the same whether the
      // page reads it in local time or in UTC (which the statement does).
      const [y, m, d] = (f.invoiceDate || '').split('-').map(Number);
      const invoiceDate = y ? new Date(y, m - 1, d, 12).toISOString() : undefined;
      // A discount and the tax are lines of their own on the statement, under
      // the same invoice number as the work. Kept as lines rather than as
      // fields on the invoice because everything downstream — the statement,
      // what they owe, the Customer Snapshots — already adds lines up, and a
      // total that only some screens know how to work out is a total that
      // disagrees with itself.
      // Not when splitting: a discount raised as its own invoice is a bill
      // for -$200 with a Pay button on it. The sheet hides both fields in
      // that mode and the server refuses them, so this is the third guard.
      const discount = f.split ? 0 : parseFloat(f.discount) || 0;
      const tax = f.split ? 0 : parseFloat(f.tax) || 0;
      if (discount > 0) lines.push({ trade: 'Discount', amount: -discount, notes: '' });
      if (tax > 0) lines.push({ trade: 'Tax', amount: tax, notes: '' });

      const res = await axios.post('/api/projects/client-invoice', {
        homeownerId: f.client._id,
        requestId: f.requestId || null,
        projectId: f.projectId || null,
        title: f.title.trim(),
        invoiceNumber: f.invoiceNumber.trim(),
        invoiceDate,
        // One bill per trade, each payable on its own. See the comment on the
        // route: this is the twenty minutes of scissors per project that
        // Julian was doing by hand so a customer pays for demolition when
        // demolition is done.
        split: Boolean(f.split),
        // Off ONLY when Julian has turned the switch off, to catch the
        // statement up on work already paid for elsewhere. Anything else,
        // including a form written before the switch existed, still sends.
        notify: f.notify !== false,
        lines,
      }, authHeaders());
      const raised = res.data?.raised;
      const number = res.data?.lines?.[res.data.lines.length - 1]?.invoiceNumber;
      if (raised?.split) {
        const first = raised.numbers[0];
        const last = raised.numbers[raised.numbers.length - 1];
        showToast(`${raised.count} invoices raised, ${first} to ${last}`);
      } else {
        showToast(number ? `Invoice ${number} raised` : 'Invoice raised');
      }
      setModal(null);
      setClientInvoiceForm(null);

      // The same sheet the estimate lands on, with QPS on the letterhead this
      // time and the customer as who it is prepared for. The discount and the
      // tax ride as adjustments, because on this document they are real money
      // that has to show between the subtotal and the total.
      //
      // Not on a split: there is no single document to show. Nineteen bills
      // went out, and a paper sheet claiming to be one of them with every
      // line on it would be the twentieth, which exists nowhere. The project
      // statement is the thing to read, and the toast names the range.
      const c = f.client || {};
      if (!raised?.split) setPaperDoc({
        kind: 'invoice',
        number: number || f.invoiceNumber,
        date: invoiceDate || new Date(),
        status: 'unpaid',
        from: {
          name: BRAND.company.legalName,
          lines: [
            BRAND.company.phone ? `Phone: ${BRAND.company.phone}` : '',
            BRAND.company.billingEmail ? `Email: ${BRAND.company.billingEmail}` : '',
          ].filter(Boolean),
        },
        to: {
          name: c.name,
          lines: [
            c.billingAddress || c.address,
            [c.billingCity || c.city, c.billingState || c.state].filter(Boolean).join(', ')
              + ((c.billingZip || c.zip) ? ` ${c.billingZip || c.zip}` : ''),
            c.phone,
          ].map(l => String(l || '').trim()).filter(Boolean),
        },
        serviceAddress: f.client?.address
          ? [f.client.address, [f.client.city, f.client.state].filter(Boolean).join(', ')].filter(Boolean).join(', ')
          : '',
        items: (f.lines || [])
          .filter(l => (l.label || '').trim() || l.cost)
          .map(l => ({ name: l.label, description: l.description, qty: 1, unitCost: l.cost })),
        adjustments: [
          { label: 'Discount', amount: -(parseFloat(f.discount) || 0) },
          { label: 'Tax', amount: parseFloat(f.tax) || 0 },
        ],
        terms: BRAND.company.paymentTerms ? `Payment terms: ${BRAND.company.paymentTerms}` : '',
      });

      await loadAdminData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally { setIsSavingInvoice(false); }
  };

  const handleSaveProjectLine = async () => {
    if (!lineForm.trade) { alert('Name the trade or work.'); return; }
    setIsSavingProject(true);
    try {
      const body = { ...lineForm };
      delete body._id;
      // A date input gives a bare day, which stored as UTC midnight reads as
      // the day before in Utah. Sent as noon so the statement prints the day
      // he typed, the same fix the client invoice sheet makes.
      const [yy, mm, dd] = (lineForm.invoiceDate || '').split('-').map(Number);
      body.invoiceDate = yy ? new Date(yy, mm - 1, dd, 12).toISOString() : undefined;
      if (lineForm._id) await axios.patch(`/api/projects/${activeProject._id}/lines/${lineForm._id}`, body, authHeaders());
      else await axios.post(`/api/projects/${activeProject._id}/lines`, body, authHeaders());
      showToast('Statement updated');
      setModal(null);
      await refreshProjects();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally { setIsSavingProject(false); }
  };

  const handleDeleteProjectLine = async (project, line) => {
    if (!window.confirm(`Remove "${line.trade}" from the statement?`)) return;
    try {
      await axios.delete(`/api/projects/${project._id}/lines/${line._id}`, authHeaders());
      showToast('Line removed');
      await refreshProjects();
    } catch (err) { alert('Error: ' + (err.response?.data?.message || err.message)); }
  };

  // One sheet, two jobs: recording a payment and correcting one. A second
  // near-identical form would be a second place for the two to drift apart.
  const openProjectPayment = (project, line, payment) => {
    setActiveProject(project);
    const asInput = (d) => {
      const t = d ? new Date(d) : new Date();
      return Number.isNaN(t.getTime()) ? new Date().toISOString().slice(0, 10)
        : `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
    };
    setClientPayForm(payment ? {
      _id: payment._id,
      lineId: payment.lineId || '',
      amount: payment.amount != null ? Number(payment.amount).toFixed(2) : '',
      date: asInput(payment.date),
      method: payment.method || 'Check',
      reference: payment.reference || '',
      note: payment.note || '',
    } : {
      _id: '',
      lineId: line?._id || '',
      amount: line?.owed > 0 ? Number(line.owed).toFixed(2) : '',
      date: asInput(),
      method: 'Check', reference: '', note: '',
    });
    setModal('clientPayment');
  };

  const handleSaveClientPayment = async () => {
    const f = clientPayForm;
    const amount = parseFloat(f.amount);
    if (!amount || amount <= 0) { alert('Enter an amount.'); return; }

    // ── OVERPAYING A LINE ───────────────────────────────────────────────
    //
    // Entering the Skanchy basement, one cheque went on twice and Framing
    // ended up paid $2,475 against a $3,025 bill with $1,237.50 of it
    // doubled. Nothing said a word. An overpayment is legitimate now and
    // then, so this asks rather than refuses — but it says the figure and
    // the trade, which is what makes a duplicate obvious.
    const line = (activeProject?.lines || []).find(l => String(l._id) === String(f.lineId));
    if (line) {
      // Editing: this payment's own current amount is not an overpayment of
      // itself, so it comes off what is already down against the line.
      const mine = f._id
        ? Number((activeProject.payments || []).find(p => String(p._id) === String(f._id))?.amount) || 0
        : 0;
      const left = Math.round(((Number(line.owed) || 0) + mine) * 100) / 100;
      const over = Math.round((amount - left) * 100) / 100;
      if (over > 0.004) {
        const ok = window.confirm(
          `This will overpay ${line.trade} by ${fmtMoney(over)}.\n\n`
          + `${line.trade} has ${fmtMoney(left)} left on it and you have typed ${fmtMoney(amount)}.\n\n`
          + 'Anything over spreads across the other open lines, and whatever is left after '
          + 'that shows as a credit. Save it anyway?',
        );
        if (!ok) return;
      }
    }

    setIsSavingProject(true);
    try {
      const body = { ...f };
      delete body._id;
      // A date input gives a bare day, which stored as UTC midnight reads as
      // the day before in Utah. Noon instead, the same fix the other sheets make.
      const [yy, mm, dd] = (f.date || '').split('-').map(Number);
      body.date = yy ? new Date(yy, mm - 1, dd, 12).toISOString() : undefined;
      if (f._id) {
        await axios.patch(`/api/projects/${activeProject._id}/payments/${f._id}`, body, authHeaders());
        showToast('Payment corrected');
      } else {
        await axios.post(`/api/projects/${activeProject._id}/payments`, body, authHeaders());
        showToast('Payment recorded');
      }
      setModal(null);
      await refreshProjects();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally { setIsSavingProject(false); }
  };

  // ── Typing straight into the statement ────────────────────────────────
  //
  // Julian works his spreadsheet by clicking a cell and typing, and asked
  // for the statement to behave the same way. One cell, one field, one
  // request — the sheets are still there for anything bigger, and the
  // server logs the change either way.
  const saveStatementCell = async ({ kind, id, field, value }) => {
    const project = activeProject;
    if (!project) return;
    const url = kind === 'payment'
      ? `/api/projects/${project._id}/payments/${id}`
      : `/api/projects/${project._id}/lines/${id}`;
    try {
      const res = await axios.patch(url, { [field]: value }, authHeaders());
      const next = res.data;
      setProjects(prev => prev.map(p => (p._id === next._id ? next : p)));
      setActiveProject(next);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
      await refreshProjects();
      throw err;
    }
  };

  const handleDeleteClientPayment = async (project, payment) => {
    // Named, not "this payment". Deleting the wrong row off a statement is
    // the mistake this confirmation exists to stop.
    const line = (project.lines || []).find(l => String(l._id) === String(payment.lineId));
    const what = [fmtMoney(payment.amount), payment.method, payment.reference && `#${payment.reference}`,
      line && `on ${line.trade}`].filter(Boolean).join(' ');
    if (!window.confirm(`Delete this payment?\n\n${what}\n\nThe balance goes back up by `
      + `${fmtMoney(payment.amount)}. This is written to the project's history.`)) return;
    try {
      await axios.delete(`/api/projects/${project._id}/payments/${payment._id}`, authHeaders());
      showToast('Payment deleted');
      await refreshProjects();
    } catch (err) { alert('Error: ' + (err.response?.data?.message || err.message)); }
  };

  const openProjectExpense = (project) => {
    setActiveProject(project);
    setExpenseForm({
      lineId: '', description: '', supplier: '', amount: '', paymentDetails: '', chargeToVendorId: '',
      date: new Date().toISOString().slice(0, 10),
    });
    setReceiptFile(null);
    setModal('projectExpense');
  };

  // ── Loading a project off a spreadsheet ───────────────────────────────
  //
  // Two passes, always. The preview is parsed by the SERVER and shown back,
  // so what he approves is what the server read — a browser-side preview
  // could disagree with the write and nobody would know until the totals
  // were wrong.
  const [importForm, setImportForm] = useState(null);
  const [importBusy, setImportBusy] = useState(false);

  const openProjectImport = (project) => {
    setActiveProject(project);
    setImportForm({ linesCsv: '', paymentsCsv: '', preview: null });
    setModal('projectImport');
  };

  const runImportPreview = async () => {
    const f = importForm;
    if (!f?.linesCsv.trim() && !f?.paymentsCsv.trim()) { alert('Paste a sheet first.'); return; }
    setImportBusy(true);
    try {
      const res = await axios.post(`/api/projects/${activeProject._id}/import`, {
        linesCsv: f.linesCsv, paymentsCsv: f.paymentsCsv, preview: true,
      }, authHeaders());
      setImportForm(p => ({ ...p, preview: res.data.preview }));
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally { setImportBusy(false); }
  };

  const confirmImport = async () => {
    const f = importForm;
    setImportBusy(true);
    try {
      const res = await axios.post(`/api/projects/${activeProject._id}/import`, {
        linesCsv: f.linesCsv, paymentsCsv: f.paymentsCsv, preview: false,
      }, authHeaders());
      const i = res.data.imported;
      showToast(`${i.lines} invoice line${i.lines === 1 ? '' : 's'} and ${i.payments} payment${i.payments === 1 ? '' : 's'} imported`);
      setModal(null);
      setImportForm(null);
      await refreshProjects();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally { setImportBusy(false); }
  };

  const handleSaveExpense = async () => {
    if (!expenseForm.amount || parseFloat(expenseForm.amount) <= 0) { alert('Enter the amount spent.'); return; }
    setIsSavingProject(true);
    try {
      const fd = new FormData();
      Object.entries(expenseForm).forEach(([k, v]) => { if (v && k !== 'date') fd.append(k, v); });
      // Noon, not the bare day: stored as UTC midnight a receipt reads as the
      // day before in Utah.
      const [yy, mm, dd] = (expenseForm.date || '').split('-').map(Number);
      if (yy) fd.append('date', new Date(yy, mm - 1, dd, 12).toISOString());
      if (receiptFile) fd.append('receipt', receiptFile);
      await axios.post(`/api/projects/${activeProject._id}/expenses`, fd, {
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' }
      });
      showToast('Expense logged');
      setModal(null);
      setReceiptFile(null);
      await refreshProjects();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally { setIsSavingProject(false); }
  };

  const handleDeleteProjectExpense = async (project, expense) => {
    if (!window.confirm('Remove this expense?')) return;
    try {
      await axios.delete(`/api/projects/${project._id}/expenses/${expense._id}`, authHeaders());
      await refreshProjects();
    } catch (err) { alert('Error: ' + (err.response?.data?.message || err.message)); }
  };

  // ── Technician estimates ──────────────────────────────────

  const estimateItemsTotal = estimateForm2.items.reduce(
    (t, i) => t + (parseFloat(i.qty) || 0) * (parseFloat(i.unitCost) || 0), 0
  );

  // Opened from a job, so the address, the trade and the property photo are
  // already on it. A tech standing in a driveway should not be retyping an
  // address they were sent this morning, and an estimate typed free-hand ties
  // to nothing — the office cannot tell which job it belongs to.
  const openEstimateForm = (request = null) => {
    setEstimateForm2({
      _id: '',
      // Which state the record is in, so a save knows whether it is keeping a
      // draft a draft or leaving a sent estimate sent.
      status: 'draft',
      trade: request?.serviceType || '',
      serviceAddress: request?.serviceAddress || '',
      city: request?.city || '', state: request?.state || '', zip: request?.zip || '',
      requestId: request?._id || '',
      // "Alpine, UT 84004" — a comma after the city, a space before the ZIP.
      // Joining all three with commas gives "Alpine, UT, 84004", which is not
      // how an address is written anywhere.
      cityLine: [
        [request?.city, request?.state].filter(Boolean).join(', '),
        request?.zip,
      ].filter(Boolean).join(' '),
      notes: '',
      externalUrl: '',
      documents: [],
      // Today, until the technician says otherwise.
      docDate: new Date().toISOString(),
      // No seeded blank line. One used to sit here, so every new estimate
      // opened showing an "Untitled line" worth $0.00 that the tech had not
      // written — and the empty state that explains what a line is for could
      // never appear. Add Item is the way in.
      items: [],
    });
    setModal('techEstimate');
  };

  // ══ The technician's invoice ═══════════════════════════════════════════
  //
  // The same builder as the estimate. Julian's rule, and it is the right one:
  // "the only difference between the estimate and the invoices is the label."
  // A technician who has learned one screen has learned both, and the people
  // using this are not going to forgive a second layout for the same job.
  //
  // It carries its own form state rather than sharing the estimate's, because
  // both can be open in a session and a shared one would have each clobbering
  // the other's lines.
  const [invoiceForm2, setInvoiceForm2] = useState({
    _id: '', invoiceNumber: '', serviceAddress: '', city: '', state: '', zip: '',
    requestId: '', estimateId: '',
    cityLine: '', notes: '', externalUrl: '', documents: [], items: [],
  });
  const invoiceItemsTotal = (invoiceForm2.items || []).reduce(
    (t, i) => t + (parseFloat(i.qty) || 0) * (parseFloat(i.unitCost) || 0), 0
  );

  const openInvoiceForm = (request = null) => {
    setInvoiceForm2({
      _id: '',
      invoiceNumber: '',
      serviceAddress: request?.serviceAddress || '',
      city: request?.city || '', state: request?.state || '', zip: request?.zip || '',
      requestId: request?._id || '',
      estimateId: '',
      cityLine: [
        [request?.city, request?.state].filter(Boolean).join(', '),
        request?.zip,
      ].filter(Boolean).join(' '),
      notes: '', externalUrl: '', documents: [], items: [], customer: null,
      docDate: new Date().toISOString(),
    });
    setModal('techInvoice');
  };

  // Reopen an invoice that already exists. An older one has no lines of its
  // own — it was raised when an invoice was a single figure — so the estimate
  // it came from supplies them, and saving writes them onto the invoice where
  // they should have been.
  // ── Who a technician's own document is for ──────────────────────────────
  //
  // Held apart from `modal`, because this screen opens ON TOP of the estimate
  // or invoice sheet and closing it has to land back on the document with
  // everything still typed into it. Using `modal` would unmount the sheet
  // underneath and lose the lines.
  const [docClientForm, setDocClientForm] = useState(null);   // { kind, ...client }
  const [vendorClients, setVendorClients] = useState([]);

  // Their own client book, for the My Clients picker on that screen. Fetched
  // when the screen opens rather than on every render: most documents are for
  // somebody already in it, and retyping an address into a phone is the step
  // technicians give up on.
  const loadVendorClients = async () => {
    try {
      const res = await axios.get('/api/vendor-clients', authHeaders());
      setVendorClients(Array.isArray(res.data) ? res.data : (res.data?.clients || []));
    } catch { /* the picker is a convenience; typing it still works */ }
  };

  // ClientEditor speaks `billingSameAsService`, and with the switch OFF the one
  // address on screen sits in the BILLING block. A document's customer speaks
  // `billingSame` and keeps its one address in the SERVICE fields. These two
  // functions are the whole translation, in one place, because getting it
  // wrong leaves a technician looking at blank address boxes and saving an
  // empty address over a good one.
  const clientToForm = (c = {}) => {
    const same = c.billingSame !== false;
    return {
      name: c.name || '', email: c.email || '', phone: c.phone || '', phone2: c.phone2 || '',
      billingSameAsService: same,
      // One address: it goes in the billing block, which is the block on screen.
      billingAddress: same ? (c.address || '') : (c.billingAddress || ''),
      billingAddress2: same ? (c.address2 || '') : (c.billingAddress2 || ''),
      billingCity: same ? (c.city || '') : (c.billingCity || ''),
      billingState: same ? (c.state || '') : (c.billingState || ''),
      billingZip: same ? (c.zip || '') : (c.billingZip || ''),
      // Two addresses: the service block appears underneath and holds its own.
      address: same ? '' : (c.address || ''),
      address2: same ? '' : (c.address2 || ''),
      city: same ? '' : (c.city || ''),
      state: same ? '' : (c.state || ''),
      zip: same ? '' : (c.zip || ''),
      vendorClientId: c.vendorClientId || undefined,
    };
  };

  const formToClient = (f = {}) => {
    const same = f.billingSameAsService !== false;
    return {
      name: (f.name || '').trim(),
      email: (f.email || '').trim(),
      phone: (f.phone || '').trim(),
      phone2: (f.phone2 || '').trim(),
      address: same ? f.billingAddress : f.address,
      address2: same ? f.billingAddress2 : f.address2,
      city: same ? f.billingCity : f.city,
      state: same ? f.billingState : f.state,
      zip: same ? f.billingZip : f.zip,
      billingSame: same,
      billingAddress: same ? '' : f.billingAddress,
      billingAddress2: same ? '' : f.billingAddress2,
      billingCity: same ? '' : f.billingCity,
      billingState: same ? '' : f.billingState,
      billingZip: same ? '' : f.billingZip,
      vendorClientId: f.vendorClientId || undefined,
    };
  };

  // The date the technician put on the document. Held as an ISO string on the
  // form and sent as the yyyy-mm-dd the field gave us; the server turns it
  // into midday UTC so it lands on the right calendar day either side of the
  // date line (server/lib/docDate.js).
  const setDocDate = (kind, value) => {
    const patch = { docDate: value || null };
    if (kind === 'invoice') setInvoiceForm2(p => ({ ...p, ...patch }));
    else setEstimateForm2(p => ({ ...p, ...patch }));
  };

  const openDocClient = (kind) => {
    const current = kind === 'invoice' ? invoiceForm2.customer : estimateForm2.customer;
    setDocClientForm({ kind, ...clientToForm(current || {}) });
    loadVendorClients();
  };

  // Picking somebody out of the book. Their record fills the screen; nothing
  // is saved to the book from here, so correcting an address for one job does
  // not quietly rewrite the customer's file.
  const pickDocClient = (c) => {
    setDocClientForm(p => ({ ...p, ...clientToForm({ ...c, vendorClientId: c._id }) }));
  };

  // Done. Written onto the document in memory, not to the server: the document
  // is saved as a whole, with its lines, by the button that already does that.
  const saveDocClient = () => {
    const f = docClientForm;
    if (!f) return;
    if (!f.name?.trim()) { alert('Put their name on it.'); return; }
    const customer = formToClient(f);
    // The client's address IS the service address. The sheet used to ask for
    // it twice — once at the top of the document and once inside the client —
    // and a technician standing in a driveway typed it twice, or typed it
    // once and left the other blank. Nothing on screen said which one the
    // work was at.
    //
    // The top one is gone. The field behind it is not: the statement, the
    // printed document, the row titles and the approval route all read
    // serviceAddress, so it is filled from the client here. Only when the
    // client carries one, so saving a customer with no address does not wipe
    // an address that is already on the document.
    const where = serviceAddressFrom(customer);
    if (f.kind === 'invoice') setInvoiceForm2(p => ({ ...p, customer, ...where }));
    else setEstimateForm2(p => ({ ...p, customer, ...where }));
    setDocClientForm(null);
  };

  const openInvoiceEditor = (entry) => {
    if (!entry) return;
    const est = entry.estimateId && typeof entry.estimateId === 'object' ? entry.estimateId : null;
    const source = (entry.items || []).length ? entry.items : (est?.items || []);
    const job = (requests || []).find(r => String(r._id) === String(entry.requestId?._id || entry.requestId));
    setInvoiceForm2({
      _id: entry._id,
      invoiceNumber: entry.invoiceNumber || '',
      serviceAddress: entry.serviceAddress || '',
      city: entry.city || '', state: entry.state || '', zip: entry.zip || '',
      requestId: entry.requestId?._id || entry.requestId || '',
      estimateId: entry.estimateId?._id || entry.estimateId || '',
      cityLine: [
        [job?.city, job?.state].filter(Boolean).join(', '),
        job?.zip,
      ].filter(Boolean).join(' '),
      notes: entry.notes || '',
      externalUrl: entry.externalUrl || '',
      documents: entry.documents || [],
      customer: entry.customer || null,
      // The date ON the document, which the technician sets, falling back to
      // the day the row was written for everything made before that existed.
      docDate: entry.docDate || entry.createdAt || null,
      items: source.map(i => ({
        name: i.name || '', description: i.description || '', kind: i.kind || 'labor',
        qty: i.qty ?? 1, unitCost: i.unitCost ?? '', photos: i.photos || [], files: i.files || [],
      })),
    });
    setModal('techInvoice');
  };

  const handleSaveTechInvoice = async () => {
    const f = invoiceForm2;
    const items = (f.items || []).filter(i => i.description || i.name);
    if (items.length === 0) { alert('Add at least one line before sending this invoice.'); return; }
    setIsSavingLedger(true);
    try {
      const body = {
        invoiceNumber: f.invoiceNumber,
        requestId: f.requestId || undefined,
        estimateId: f.estimateId || undefined,
        serviceAddress: f.serviceAddress,
        city: f.city, state: f.state, zip: f.zip,
        // One line per job on the statement QPS reads, the same summary the
        // list row shows.
        jobDetails: items.map(i => i.name || String(i.description || '').split('\n')[0]).filter(Boolean).join(' · '),
        notes: f.notes,
        externalUrl: f.externalUrl,
        items,
        documents: f.documents || [],
        // Who it is for. The estimate's save spreads the whole form, so it
        // travels on its own; this one names every field, so leaving it out
        // would silently drop the customer on every invoice save.
        customer: f.customer || undefined,
        docDate: f.docDate || undefined,
        writerLang: currentLanguage(),
      };
      const res = f._id
        ? await axios.patch(`/api/ledger/${f._id}`, body, authHeaders())
        : await axios.post('/api/ledger', body, authHeaders());
      showToast(f._id ? 'Invoice updated' : 'Invoice sent to QPS Hub');
      setModal(null);
      openInvoicePaper(res.data);
      setVendorView('invoices');
      await refreshData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally { setIsSavingLedger(false); }
  };

  // The letterhead on a technician's own document: their business, quoting
  // QPS. Not QPS's letterhead and not the homeowner's name — a tech's estimate
  // is their bill to us, and the customer's side of it is none of their
  // business by design.
  // `who` is the business the document belongs to. It defaults to the signed-in
  // technician, and QPS passes the technician's own record when reading or
  // sending somebody else's document — a support call must show Julian exactly
  // the sheet the customer is looking at, not QPS's name on the top of it.
  const techLetterhead = (who) => {
    const v = who || vendorProfile || {};
    const p = v.profile || {};
    return {
      from: {
        name: v.businessName || currentUser?.name || 'My business',
        logoUrl: p.logoUrl || '',
        lines: [
          p.address,
          [p.city, p.state].filter(Boolean).join(', ') + (p.zip ? ` ${p.zip}` : ''),
          v.phone ? `Phone: ${v.phone}` : '',
          v.email ? `Email: ${v.email}` : '',
        ].map(l => String(l || '').trim()).filter(Boolean),
      },
      to: {
        name: BRAND.company.legalName,
        lines: [
          BRAND.company.phone,
          BRAND.company.billingEmail,
        ].filter(Boolean),
      },
    };
  };

  // ── Who a technician's document is actually prepared for ────────────────
  //
  // techLetterhead() always said Quick Property Services, which is right for a
  // document a technician sends QPS and WRONG for one they write for their own
  // homeowner — the free tool. A technician typed the owner's name and email
  // into "Who is this for?", saved, and the finished sheet told him it was
  // going to QPS. That is the whole reason this exists.
  //
  // A document with its own customer on it is for that customer. One with none
  // is a price to QPS, which is what every document written before the
  // customer block existed was.
  const docRecipient = (doc, fallbackTo) => {
    const c = doc?.customer;
    if (!c?.name) return fallbackTo;
    const billing = c.billingSame === false;
    return {
      name: c.name,
      lines: [
        billing ? c.billingAddress : c.address,
        billing ? c.billingAddress2 : c.address2,
        [
          [billing ? c.billingCity : c.city, billing ? c.billingState : c.state].filter(Boolean).join(', '),
          billing ? c.billingZip : c.zip,
        ].filter(Boolean).join(' '),
        c.phone,
        c.email,
      ].map(l => String(l || '').trim()).filter(Boolean),
    };
  };

  // Every save lands on the finished sheet. The form is where you type and the
  // sheet is where you notice the line you priced at $0.00, so the document
  // gets read at least once before anybody sends it.
  const showEstimatePaper = (saved, form, keepEditing) => {
    const { from, to } = techLetterhead();
    const state = saved?.status || form.status || 'draft';
    setPaperDoc({
      kind: 'estimate',
      id: saved?._id || form._id,
      number: saved?.estimateNumber || form.estimateNumber,
      date: saved?.docDate || saved?.createdAt || new Date(),
      status: estimateBand(state),
      from,
      // The service address belongs under who it is for: it is the one thing
      // on the page that says WHICH job this price is for.
      to: docRecipient(saved || form, to),
      serviceAddress: fullAddress(form),
      items: form.items || [],
      notes: form.notes,
      // The saved copy where there is one: the server puts the scheme back on
      // a link and drops anything that is not http(s).
      externalUrl: saved?.externalUrl ?? form.externalUrl,
      documents: saved?.documents || form.documents || [],
      terms: ESTIMATE_TERMS,
      // Saving leaves the builder open behind this sheet, so Edit is one tap
      // back to it. Sending closes the builder, and there is nothing to go
      // back to.
      onEdit: keepEditing ? () => setPaperDoc(null) : undefined,
      // Only something QPS has not agreed to yet can be sent from here.
      onSend: keepEditing && state !== 'approved'
        ? () => handleSaveTechEstimate('submitted')
        : undefined,
      // The moment he has just finished pricing it is the moment he wants to
      // send it. Needs the saved record, because sending is a call against an
      // id the server knows about.
      share: saved?.share,
      customerFor: saved?._id && (form.items || []).length ? saved : null,
    });
  };

  // Street line plus "Alpine, UT 84004". A record written before the address
  // was split has the whole thing on the street line and the rest empty, and
  // comes back exactly as it was stored.
  const fullAddress = (r) => [
    r?.serviceAddress,
    [[r?.city, r?.state].filter(Boolean).join(', '), r?.zip].filter(Boolean).join(' '),
  ].map(x => String(x || '').trim()).filter(Boolean).join(', ');

  const ESTIMATE_TERMS = 'Prices cover the work described above. Anything found once work starts is quoted separately before it is done.';

  // Status on a saved estimate → the word on the band.
  const estimateBand = (status) => (
    status === 'draft' ? 'draft'
      : status === 'approved' ? 'approved'
      : status === 'rejected' ? 'declined'
      : 'pending'
  );

  // A tech who wrote in Spanish reads their own words back, not a round trip
  // of them. The English is what QPS stores and what the office reads; which
  // one appears here is decided by the language the reader has selected, the
  // same rule every other screen in the portal follows.
  const ownWords = (items, sourceLang) => (items || []).map(i => ({
    ...i,
    description: localized(i, 'description', currentLanguage(), sourceLang),
  }));

  // Open a SAVED estimate as the document it is. Tapping a row used to give a
  // summary card; a tech reading back what they quoted wants the sheet the
  // office is reading, letterhead and all.
  const openEstimatePaper = (estimate) => {
    if (!estimate) return;
    // QPS reading a technician's document gets it populated, so the sheet can
    // carry that technician's letterhead. A technician reading their own gets
    // a bare id here and falls through to their own profile.
    const owner = estimate.vendorId && typeof estimate.vendorId === 'object' ? estimate.vendorId : null;
    const { from, to } = techLetterhead(owner);
    const isDraft = estimate.status === 'draft';
    const asAdmin = currentUser?.role === 'admin';
    setPaperDoc({
      kind: 'estimate',
      number: estimate.estimateNumber,
      date: estimate.docDate || estimate.createdAt || estimate.submittedAt,
      status: estimateBand(estimate.status),
      from,
      to: docRecipient(estimate, to),
      serviceAddress: fullAddress(estimate),
      items: ownWords(estimate.items, estimate.sourceLang),
      notes: localized(estimate, 'notes', currentLanguage()),
      externalUrl: estimate.externalUrl,
      documents: estimate.documents || [],
      terms: ESTIMATE_TERMS,
      // Editable right up until QPS approves it. It was drafts only, which was
      // stricter than the server has ever been and stricter than the job: a
      // tech who spots a wrong price on an estimate the office has not acted
      // on should fix it, not ring somebody. Once approved it is a number QPS
      // is carrying, and changing it is a conversation.
      // A technician may revise their own right up until QPS approves it.
      // QPS may always edit, which is the whole point of being able to help
      // somebody who has rung up stuck — including on an approved one, where
      // the server already allows it.
      onEdit: asAdmin || estimate.status !== 'approved'
        ? () => { setPaperDoc(null); openEstimateDraft(estimate); }
        : undefined,
      onSend: isDraft ? () => sendEstimateRecord(estimate) : undefined,
      // Sent / opened / answered, drawn under the toolbar. His copy only.
      share: estimate.share,
      // The free tool. Offered on ANY estimate with lines on it, draft
      // included: an estimate to his own customer has nothing to do with
      // whether QPS has seen it, and most of them never will.
      customerFor: (estimate.items || []).length ? estimate : null,
      // Joist's INVOICE button, on the document rather than beside it.
      //
      // Offered on a sent estimate as well as an approved one. Waiting for
      // approval is the tidier rule and it is not the one Julian asked for:
      // most of these jobs are small, the tech is standing in the driveway
      // having finished, and an invoice is a request to be paid rather than a
      // payment. QPS still reads it before any money moves.
      //
      // Not on a draft, which QPS has never seen, and not on a declined one,
      // which they have seen and said no to. And not at all once the estimate
      // has been invoiced — the server refuses a second one, and a button that
      // exists only to be refused is worse than no button.
      invoiceFrom: ['submitted', 'approved'].includes(estimate.status)
        && !(ledger || []).some(e => String(e.estimateId?._id || e.estimateId) === String(estimate._id))
        ? estimate
        : null,
    });
  };

  // Send a draft straight from the sheet, without reopening the builder.
  const sendEstimateRecord = async (estimate) => {
    if (!estimate?._id) return;
    if (!(estimate.items || []).length) { alert('Add at least one line before sending this estimate.'); return; }
    setIsSavingProject(true);
    try {
      const res = await axios.patch(`/api/estimates/${estimate._id}`,
        { status: 'submitted', writerLang: currentLanguage() }, authHeaders());
      showToast('Estimate sent to QPS Hub');
      openEstimatePaper(res.data || { ...estimate, status: 'submitted' });
      await refreshData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally { setIsSavingProject(false); }
  };

  // The ledger card behind an invoice's paper: payments, progress reports, the
  // tech's own uploaded file. Held in App state so the paper can reach it
  // without the list screen having to stay mounted underneath.
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const openInvoiceDetail = (entry) => setInvoiceDetail(entry);

  // The same, for an invoice the tech has already raised.
  //
  // Its lines come from the estimate it was generated from, not from a copy
  // kept on the invoice — that is the whole point of storing `estimateId`, and
  // it is why the two can never disagree about what was billed. An invoice
  // raised without one falls back to its own one-line description.
  const openInvoicePaper = (entry) => {
    if (!entry) return;
    const owner = entry.vendorId && typeof entry.vendorId === 'object' ? entry.vendorId : null;
    const { from, to } = techLetterhead(owner);
    const est = entry.estimateId && typeof entry.estimateId === 'object' ? entry.estimateId : null;
    const amount = Number(entry.invoiceAmount) || 0;
    const paid = (entry.payments || []).reduce((t, p) => t + (Number(p.amount) || 0), 0);
    setPaperDoc({
      kind: 'invoice',
      number: entry.invoiceNumber,
      date: entry.docDate || entry.createdAt,
      // The words an invoice is actually described by. It borrowed the
      // estimate's ("pending", "approved"), and a part-paid invoice reading
      // PENDING says nothing about the $1,200 still outstanding.
      status: amount > 0 && paid >= amount ? 'paid' : paid > 0 ? 'part paid' : 'unpaid',
      from,
      to: docRecipient(entry, to),
      serviceAddress: fullAddress(entry),
      // The invoice's OWN lines first. This read the estimate's and nothing
      // else, so an invoice edited line by line still printed as one row
      // reading "DOOR KNOBS · DRYWALL · HINGES" at the whole total — the
      // builder showed three prices and the document showed none of them.
      // The estimate is the fallback for an invoice raised before invoices
      // had lines, and the one-row summary is the fallback for one with no
      // estimate either.
      items: (entry.items || []).length
        ? ownWords(entry.items, entry.sourceLang)
        : (est?.items || []).length
          ? ownWords(est.items, est.sourceLang || entry.sourceLang)
          : [{ name: localized(entry, 'jobDetails', currentLanguage()) || est?.trade || 'Work', description: '', qty: 1, unitCost: amount }],
      notes: entry.notes,
      externalUrl: entry.externalUrl,
      documents: [
        ...(entry.documents || []),
        ...(entry.invoiceDocs || []).map(u => ({ url: u, name: 'Invoice file' })),
      ],
      // Names the estimate when there is one. It claimed an approval on every
      // invoice, including ones raised off an estimate QPS has not looked at
      // and ones with no estimate behind them at all.
      terms: est?.estimateNumber ? `Billed against estimate ${est.estimateNumber}.` : '',
      settlement: { paid, balance: amount - paid },
      // Correctable until money moves against it. The server holds the same
      // line, so an invoice that has been part-paid comes back with a reason
      // rather than silently changing.
      // Locked for the technician once money has moved against it; never
      // locked for QPS, who is the one they ring when it is wrong.
      onEdit: paid === 0 || currentUser?.role === 'admin'
        ? () => { setPaperDoc(null); openInvoiceEditor(entry); }
        : undefined,
      share: entry.share,
      customerFor: entry,
      progressFor: entry,
      // The paper is the document; the card behind it is the working record —
      // payment history, the progress reports, the tech's own uploaded
      // invoice file. One tap, rather than two screens competing to be the
      // thing a row opens.
      detailFor: entry,
    });
  };

  // Save a draft, or send it. Both write the same record; `status` is the only
  // difference, and an estimate that already has an _id is PATCHed rather than
  // POSTed — otherwise saving twice would leave two estimates for one job and
  // QPS would have to work out which is real.
  const handleSaveTechEstimate = async (status = 'submitted') => {
    const f = estimateForm2;
    const items = (f.items || []).filter(i => i.description || i.name);
    if (status === 'submitted' && items.length === 0) {
      alert('Add at least one line before sending this estimate.');
      return;
    }
    // Correcting an estimate QPS already has is not the same act as saving a
    // draft, and it must not put one back into a drawer QPS cannot see. So a
    // save on a sent estimate sends no status at all and leaves it where it
    // is; only a real draft is saved as a draft.
    const wasSent = f.status && f.status !== 'draft';
    const keepEditing = status === 'draft';
    setIsSavingProject(true);
    try {
      // The form carries the record's current status so the UI can reason
      // about it; the request must not, or the spread quietly sends it back
      // and the server is deciding a transition from a field the client just
      // echoed at it. The one status this request means to set is set below.
      const { status: _formStatus, ...rest } = f;
      const body = { ...rest, items, writerLang: currentLanguage() };
      if (!(keepEditing && wasSent)) body.status = status;
      const res = f._id
        ? await axios.patch(`/api/estimates/${f._id}`, body, authHeaders())
        : await axios.post('/api/estimates', body, authHeaders());

      const saved = res.data || {};
      const form = { ...f, items, _id: saved._id || f._id };
      if (keepEditing) {
        // The builder stays open underneath and remembers the id, so a second
        // Save updates this record instead of starting another one. Edit on
        // the paper takes the sheet back with everything still typed into it.
        setEstimateForm2(p => ({ ...p, _id: saved._id || p._id, status: saved.status || p.status }));
        showToast(wasSent ? 'Estimate updated' : 'Draft saved');
      } else {
        showToast('Estimate sent to QPS Hub');
        setModal(null);
      }
      showEstimatePaper(saved, form, keepEditing);
      await refreshData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally { setIsSavingProject(false); }
  };

  const handleSubmitTechEstimate = () => handleSaveTechEstimate('submitted');

  // Re-open a saved draft exactly where it was left.
  const openEstimateDraft = (estimate) => {
    if (!estimate) return;
    const requestId = estimate.requestId?._id || estimate.requestId || '';
    // The city line and the property photo live on the job, not on the
    // estimate. Without this the banner comes back bare, and a tech reopening
    // their own draft cannot tell at a glance that it is the same document.
    const job = (requests || []).find(r => String(r._id) === String(requestId));
    setEstimateForm2({
      _id: estimate._id,
      status: estimate.status || 'draft',
      estimateNumber: estimate.estimateNumber || '',
      trade: estimate.trade || '',
      serviceAddress: estimate.serviceAddress || '',
      city: estimate.city || '', state: estimate.state || '', zip: estimate.zip || '',
      requestId,
      cityLine: [
        [job?.city, job?.state].filter(Boolean).join(', '),
        job?.zip,
      ].filter(Boolean).join(' '),
      notes: estimate.notes || '',
      externalUrl: estimate.externalUrl || '',
      documents: estimate.documents || [],
      customer: estimate.customer || null,
      docDate: estimate.docDate || estimate.createdAt || null,
      items: (estimate.items || []).map(i => ({
        name: i.name || '', description: i.description || '', kind: i.kind || 'labor',
        qty: i.qty ?? 1, unitCost: i.unitCost ?? '', photos: i.photos || [], files: i.files || [],
      })),
    });
    setModal('techEstimate');
  };

  // Turn an APPROVED estimate into an invoice. The vendor priced the job once
  // and QPS approved that number, so the invoice is generated from it rather
  // than retyped: an invoice built this way cannot disagree with what was
  // approved, and `estimateId` keeps the line detail — names, descriptions,
  // photos — readable from the estimate instead of copied into a second place
  // where the two can drift.
  const handleInvoiceFromEstimate = async (estimate) => {
    if (!estimate) return;
    const total = (estimate.items || []).reduce(
      (t, i) => t + (parseFloat(i.qty) || 0) * (parseFloat(i.unitCost) || 0), 0
    ) || Number(estimate.techTotal) || 0;
    const lines = (estimate.items || [])
      .map(i => i.name || String(i.description || '').split('\n')[0])
      .filter(Boolean)
      .join(' · ');

    setIsSavingProject(true);
    try {
      const fd = new FormData();
      fd.append('estimateId', estimate._id);
      // The estimate's lines become the invoice's own, rather than being read
      // through the reference forever: the tech can then correct the invoice
      // without touching the estimate QPS approved.
      fd.append('items', JSON.stringify(estimate.items || []));
      if (estimate.requestId) fd.append('requestId', estimate.requestId._id || estimate.requestId);
      fd.append('serviceAddress', estimate.serviceAddress || '');
      fd.append('jobDetails', lines || estimate.trade || '');
      fd.append('invoiceAmount', String(total));
      fd.append('writerLang', currentLanguage());
      const res = await axios.post('/api/ledger', fd, {
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' },
      });
      showToast('Invoice sent to QPS Hub');

      // Into the Invoices section, which is where a tech goes looking for it
      // afterwards. The sheet opens on top of that list rather than on top of
      // the estimate it came from, so closing it lands somewhere that makes
      // sense.
      setVendorView('invoices');

      // Straight onto the sheet, same as an estimate. This invoice is
      // generated rather than typed, which is exactly why it should be read:
      // nobody proofreads a document they never saw.
      const { from, to } = techLetterhead();
      setPaperDoc({
        kind: 'invoice',
        number: res.data?.invoiceNumber || '',
        date: res.data?.docDate || res.data?.createdAt || new Date(),
        status: 'unpaid',
        from,
        // The invoice raised off an approved estimate inherits whoever that
        // estimate was for.
        to: docRecipient(res.data || estimate, to),
        serviceAddress: fullAddress(estimate),
        items: estimate.items || [],
        // Says which estimate, not that it was approved — this one can be
        // raised off an estimate still waiting on QPS, and a document that
        // claims an approval nobody gave is worse than one that claims
        // nothing.
        terms: estimate.estimateNumber
          ? `Billed against estimate ${estimate.estimateNumber}.`
          : 'Billed against the estimate for this job.',
        settlement: { paid: 0, balance: Number(res.data?.invoiceAmount) || total },
        onEdit: res.data ? () => { setPaperDoc(null); openInvoiceEditor(res.data); } : undefined,
        detailFor: res.data || null,
        progressFor: res.data || null,
        share: res.data?.share,
        customerFor: res.data || null,
      });

      await refreshData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally { setIsSavingProject(false); }
  };

  // Take an estimate back. Asks first, because a swipe is easy to do by
  // accident and a priced job with photos on it is a morning's work.
  const handleDeleteTechEstimate = async (estimate) => {
    if (!estimate?._id) return;
    const what = estimate.estimateNumber || estimate.serviceAddress || 'this estimate';
    if (!window.confirm(`Delete ${what}? This cannot be undone.`)) return;
    try {
      await axios.delete(`/api/estimates/${estimate._id}`, authHeaders());
      showToast('Estimate deleted');
      await refreshData();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const handleApproveEstimateToProject = async (estimate, projectId, invoiceNumber, invoiceUrl) => {
    try {
      const res = await axios.post(`/api/estimates/${estimate._id}/approve`,
        { projectId, invoiceNumber, invoiceUrl }, authHeaders());
      const where = res.data?.project?.title;
      showToast(where ? `Approved — it is a line on ${where} now` : 'Approved — added to the project statement');
      await refreshData();
    } catch (err) { alert('Error: ' + (err.response?.data?.message || err.message)); }
  };

  // Saying no. The route has always been there and nothing on screen called
  // it, so the only way to clear an estimate Julian did not want was to
  // approve it onto a project and then delete the line — which bills a
  // homeowner for work nobody agreed to, for as long as it takes him to
  // notice.
  const handleDeclineEstimate = async (estimate) => {
    const who = estimate.vendorId?.businessName || 'the technician';
    const reason = window.prompt(
      `Decline this estimate? ${who} sees the reason you give here.`, '');
    if (reason === null) return;          // they backed out
    try {
      await axios.post(`/api/estimates/${estimate._id}/reject`,
        { reason: reason.trim() }, authHeaders());
      showToast('Declined');
      await refreshData();
    } catch (err) { alert('Error: ' + (err.response?.data?.message || err.message)); }
  };

  // ══ Commercial Accounts ═══════════════════════════════════════════════
  //
  // The fourth portal. All of it hangs off one membership resolved at sign-in,
  // and every read is already scoped to that account by the server, so nothing
  // here filters for access — only for display.

  const [account, setAccount] = useState(null);
  const [accountMembers, setAccountMembers] = useState([]);
  const [accountView, setAccountView] = useState('jobs');
  const [properties, setProperties] = useState([]);
  const [propertyTotal, setPropertyTotal] = useState(0);
  const [propertySearch, setPropertySearch] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [propertyHistory, setPropertyHistory] = useState([]);
  const [propertyForm, setPropertyForm] = useState(null);
  const [unitForm, setUnitForm] = useState(null);
  const [memberForm, setMemberForm] = useState(null);
  const [woForm, setWoForm] = useState(null);
  const [importState, setImportState] = useState(null);
  const [formBusy, setFormBusy] = useState(false);
  const [formError, setFormError] = useState('');

  // Julian's own view of the commercial accounts. Separate from the manager's
  // state above because it is a different question: that one is "my company",
  // this one is "all of them".
  const [adminAccounts, setAdminAccounts] = useState([]);
  const [adminAccountDetail, setAdminAccountDetail] = useState(null);

  const loadAdminAccounts = async () => {
    try {
      const res = await axios.get('/api/accounts', authHeaders());
      setAdminAccounts(res.data);
    } catch { /* silently fail */ }
  };

  // Re-read whichever account is open, if one is. Called after anything that
  // changes a job at one of its properties.
  const refreshAdminAccountDetail = async () => {
    const id = adminAccountDetail?.account?._id || adminAccountDetail?.account?.id;
    if (id) await openAdminAccount(id);
  };

  const openAdminAccount = async (id) => {
    if (!id) { setAdminAccountDetail(null); return; }
    try {
      const res = await axios.get(`/api/accounts/${id}`, authHeaders());
      setAdminAccountDetail(res.data);
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  // Julian setting a commercial account up himself.
  //
  // Self-signup at /commercial is for the company that finds us. This is for the
  // one he is already working with, who will not go and fill in a web form
  // because he asked them to — he takes their details down the phone and gets
  // them a login before the call ends.
  const [accountForm, setAccountForm] = useState(null);
  const [accountMemberForm, setAccountMemberForm] = useState(null);
  // The set-password link from the last invite. Kept on screen because reading
  // it down the phone is the most reliable way to get somebody into a portal —
  // more reliable than an email that lands in a spam folder nobody checks.
  const [lastInviteLink, setLastInviteLink] = useState('');

  const openAccountForm = () => {
    setAccountForm({
      name: '', type: 'property-management', phone: '', email: '',
      billingEmail: '', billingAddress: '', defaultNteAmount: '',
      ownerName: '', ownerEmail: '', ownerPhone: '', sendInvite: true,
    });
    setLastInviteLink('');
    setModal('adminAccount');
  };

  const handleCreateAccount = async () => {
    const f = accountForm || {};
    if (!f.name?.trim()) { alert('A company name is required.'); return; }
    try {
      const res = await axios.post('/api/accounts', f, authHeaders());
      await loadAdminAccounts();
      if (res.data?.warning) {
        alert(res.data.warning);
      } else {
        showToast(res.data?.owner ? 'Company created and invite sent' : 'Company created');
      }
      // Keep the modal open when there is a link worth reading out; otherwise
      // get out of the way.
      if (res.data?.owner?.link) {
        setLastInviteLink(res.data.owner.link);
        setAccountForm(null);
      } else {
        setAccountForm(null);
        setModal(null);
      }
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const openAccountMemberForm = (account, member) => {
    setAccountMemberForm({
      accountId: account.id || account._id,
      accountName: account.name,
      userId: member?.userId || null,
      name: member?.name || '',
      email: member?.email || '',
      phone: member?.phone || '',
      level: member?.level || 'coordinator',
      sendInvite: true,
    });
    setLastInviteLink('');
    setModal('adminAccountMember');
  };

  const handleSaveAccountMember = async () => {
    const f = accountMemberForm || {};
    try {
      if (f.userId) {
        await axios.patch(`/api/accounts/${f.accountId}/members/${f.userId}`, { level: f.level }, authHeaders());
        showToast('Access level updated');
        setModal(null);
      } else {
        if (!f.name?.trim() || !f.email?.trim()) { alert('A name and email are both needed.'); return; }
        const res = await axios.post(`/api/accounts/${f.accountId}/members`, f, authHeaders());
        showToast(f.sendInvite === false ? 'Added — send them the link' : 'Added and invite sent');
        setLastInviteLink(res.data?.link || '');
        if (!res.data?.link) setModal(null);
      }
      setAccountMemberForm(null);
      await openAdminAccount(f.accountId);
      await loadAdminAccounts();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // Julian adding a property to a company himself.
  //
  // The company can do this from their own portal, but on the day an account is
  // brought on board nobody there has signed in yet — he takes the buildings
  // down the phone with the rest of their details. The modal asks for the
  // address and the things a tech is stuck without; everything else on the
  // property record they can fill in later from their side.
  const [adminPropertyForm, setAdminPropertyForm] = useState(null);

  const openAdminPropertyForm = (account) => {
    setAdminPropertyForm({
      accountId: account.id || account._id,
      accountName: account.name,
      addressLine1: '', addressLine2: '', city: '', state: 'UT', zip: '',
      propertyType: 'single-family', label: '', externalId: '', nteAmount: '',
      access: { gateCode: '', lockboxCode: '' },
    });
    setModal('adminProperty');
  };

  const handleCreateAdminProperty = async () => {
    const f = adminPropertyForm || {};
    if (!f.addressLine1?.trim()) { alert('A street address is required.'); return; }
    try {
      const { accountId, accountName, ...body } = f;
      await axios.post(`/api/accounts/${accountId}/properties`, body, authHeaders());
      setAdminPropertyForm(null);
      setModal(null);
      showToast('Property added');
      // The detail screen counts and lists them; the board behind it shows the
      // property count on the card, so both are stale until they're re-read.
      await openAdminAccount(accountId);
      await loadAdminAccounts();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // ── A property's own page ────────────────────────────────────────────
  //
  // Tapping a property opens what has happened at that building: the access
  // codes, the units, and every job with its price. It is also where a job
  // STARTS, because most commercial work never comes in through the portal —
  // Julian walks a turn with the manager and quotes it on the spot.
  //
  // Only the id is kept. The property and its jobs are read back out of
  // adminAccountDetail, so a refresh after saving an estimate updates this
  // screen without a second copy of the same record drifting behind it.
  const [adminPropertyId, setAdminPropertyId] = useState(null);

  // What a job needs before it can be priced. Deliberately short: the address,
  // the unit and the limit all come off the property, so the only thing left is
  // what the work IS — and Julian is standing in front of it.
  const [propertyJobForm, setPropertyJobForm] = useState(null);

  const openPropertyJobForm = (account, property, next) => {
    setPropertyJobForm({
      accountId: account.id || account._id,
      propertyId: property._id,
      propertyLabel: property.label || property.addressLine1,
      units: (property.units || []).filter((u) => u.isActive !== false),
      unitId: '',
      serviceType: 'Handyman – General Repair',
      description: '',
      priority: 'routine',
      // Which sheet opens once the job exists — the price, or the bill.
      next,
    });
    setModal('adminPropertyJob');
  };

  // Creates the job, then hands straight over to the estimate or invoice sheet.
  // Two saves in a row is the honest shape of it — a price belongs to a job —
  // but it should feel like one action, so the sheet opens itself.
  const handleCreatePropertyJob = async () => {
    const f = propertyJobForm || {};
    if (!f.description?.trim()) { alert('Say what the work is.'); return; }
    try {
      const res = await axios.post(
        `/api/accounts/${f.accountId}/properties/${f.propertyId}/jobs`,
        { unitId: f.unitId || undefined, serviceType: f.serviceType, description: f.description, priority: f.priority },
        authHeaders()
      );
      setPropertyJobForm(null);
      await loadAdminData();
      await openAdminAccount(f.accountId);
      if (f.next === 'invoice') openAdminInvoice(res.data);
      else if (f.next === 'estimate') openEstimate(res.data);
      // A plain service request: the job is the whole point of it. It lands on
      // the board like any other and gets priced when it gets priced.
      else { setModal(null); showToast('Service request logged'); }
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  // The invoice sheet, opened by Julian rather than by a tech closing out.
  //
  // `seed` decides what is already typed in it:
  //   · an invoice that exists — read and correct it,
  //   · 'estimate' — the estimate's own rows, which is what "convert" means:
  //     the price they agreed becomes the bill, line for line, still editable
  //     because the job that got done is never exactly the job that got quoted,
  //   · nothing — an empty bill, for the work that was never estimated.
  const openAdminInvoice = (request, seed) => {
    setActiveRequest(request);
    const fromInvoice = request.invoice?.lineItems?.length
      ? request.invoice.lineItems.map((r) => ({
          description: r.description || '',
          qty: r.qty != null ? String(r.qty) : '1',
          unitCost: r.unitCost != null ? String(r.unitCost) : '',
          total: r.total != null ? String(r.total) : '',
        }))
      : null;
    const fromEstimate = request.estimate?.breakdown?.length
      ? request.estimate.breakdown.map((r) => {
          // A breakdown row may carry its workings — "3 hrs at $85" — or just a
          // price. An invoice line always has a qty and a unit cost, so the
          // workings come across when there are any, and a row without them
          // becomes one of itself at its own price.
          //
          // Never divided out to invent a unit cost: $100 across 3 would bill
          // $99.99, and the number they agreed to is the number they get.
          const cost = Number(r.cost) || 0;
          const rate = Number(r.rate) || 0;
          const qty = Number(r.qty) || 0;
          const useWorkings = rate > 0 && qty > 0;
          return {
            description: r.label || '',
            qty: String(useWorkings ? qty : 1),
            unitCost: String(useWorkings ? rate : cost),
            total: (useWorkings ? qty * rate : cost).toFixed(2),
          };
        })
      : null;
    const rows = (seed === 'estimate' ? fromEstimate : fromInvoice)
      || [{ description: '', qty: 1, unitCost: '', total: '' }];
    setInvoiceForm({
      lineItems: rows,
      notes: (seed === 'estimate' ? request.estimate?.notes : request.invoice?.notes) || '',
    });
    setModal('invoice');
  };

  // Photos, video, receipts and PDFs kept against a property, from the admin
  // board. Same component and same shape as the manager's own list; a different
  // route only because that one scopes by account membership, which Julian
  // hasn't got.
  const addAdminPropertyDocuments = async (files, meta) => {
    const d = adminAccountDetail;
    if (!d || !adminPropertyId) return;
    const docs = await uploadDocuments(files, meta);
    if (!docs.length) return;
    const accountId = d.account._id || d.account.id;
    try {
      await axios.post(`/api/accounts/${accountId}/properties/${adminPropertyId}/documents`, { documents: docs }, authHeaders());
      await openAdminAccount(accountId);
      showToast(t('doc.saved'));
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  const removeAdminPropertyDocument = async (doc) => {
    const d = adminAccountDetail;
    if (!d || !adminPropertyId || !window.confirm(t('doc.confirmRemove'))) return;
    const accountId = d.account._id || d.account.id;
    try {
      await axios.delete(`/api/accounts/${accountId}/properties/${adminPropertyId}/documents/${doc._id}`, authHeaders());
      await openAdminAccount(accountId);
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  // Attaching to a JOB from the property page. addRequestDocuments already does
  // this for the screen that has an activeRequest; here the job is one row of a
  // list, so it takes the request it is given and refreshes what is on screen.
  const addJobDocuments = async (request, files, meta) => {
    const docs = await uploadDocuments(files, meta);
    if (!docs.length) return;
    try {
      await axios.post(`/api/requests/${request._id}/documents`, { documents: docs }, authHeaders());
      await loadAdminData();
      await refreshAdminAccountDetail();
      showToast(t('doc.saved'));
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  const handleRemoveAccountMember = async (accountId, member) => {
    if (!window.confirm(`Remove ${member.name} from this account?`)) return;
    try {
      await axios.delete(`/api/accounts/${accountId}/members/${member.userId}`, authHeaders());
      await openAdminAccount(accountId);
      showToast('Removed');
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const loadAccount = async () => {
    try {
      const res = await axios.get('/api/accounts/me', authHeaders());
      setAccount(res.data.account);
      setAccountMembers(res.data.members || []);
    } catch { /* silently fail — the portal still lists work orders */ }
  };

  // Searched on the server. Four hundred properties is the opening position,
  // and filtering a whole portfolio in the browser is how this gets slow.
  const loadProperties = async (q) => {
    try {
      const res = await axios.get('/api/properties', {
        ...authHeaders(),
        params: { q: q ?? propertySearch, limit: 200 },
      });
      setProperties(res.data.properties || []);
      setPropertyTotal(res.data.total ?? 0);
    } catch { /* silently fail */ }
  };

  const loadAccountData = async () => {
    try {
      const rRes = await axios.get('/api/requests', authHeaders());
      setRequests(rRes.data);
    } catch { /* silently fail */ }
    // The company's statements, for the Billing tab. A property manager
    // promoted from an ordinary client arrives with one already running.
    try {
      const pRes = await axios.get('/api/projects', authHeaders());
      setProjects(pRes.data);
    } catch { /* silently fail */ }
    await Promise.all([loadAccount(), loadProperties('')]);
  };

  // Opening a work order from the Commercial Accounts list.
  //
  // Deliberately the SAME screen the homeowner portal uses: a work order reads
  // the same whoever raised it — the jobs, the photos, the appointment window,
  // the timeline, the estimate — and maintaining two of them would guarantee
  // they drifted apart.
  const openRequestDetailScreen = (r) => {
    setActiveRequest(r);
    goTo('screen-request-detail');
  };

  const openProperty = async (p) => {
    setSelectedProperty(p);
    setPropertyHistory([]);
    goTo('screen-property-detail');
    try {
      const res = await axios.get(`/api/properties/${p._id}`, authHeaders());
      setSelectedProperty(res.data.property);
      setPropertyHistory(res.data.history || []);
    } catch { /* the summary we already have still renders */ }
  };

  const openPropertyForm = (p) => {
    setFormError('');
    setPropertyForm(p ? { ...p } : { state: 'UT', propertyType: 'single-family', access: {}, building: {}, owner: {} });
    goTo('screen-property-form');
  };
  const closePropertyForm = () => {
    setPropertyForm(null);
    goTo(selectedProperty ? 'screen-property-detail' : 'screen-account-dashboard');
  };

  const saveProperty = async () => {
    const f = propertyForm || {};
    if (!String(f.addressLine1 || '').trim()) { setFormError(t('acct.errAddress')); return; }
    setFormBusy(true); setFormError('');
    try {
      const body = { ...f };
      // An empty box means "use the company default", which is not the same as
      // a limit of zero — so it has to travel as null rather than as "".
      body.nteAmount = f.nteAmount === '' || f.nteAmount == null ? null : Number(f.nteAmount);
      if (f._id) {
        const res = await axios.patch(`/api/properties/${f._id}`, body, authHeaders());
        setSelectedProperty(res.data);
      } else {
        await axios.post('/api/properties', body, authHeaders());
      }
      await loadProperties();
      setPropertyForm(null);
      showToast(t('acct.propertySaved'));
      goTo(f._id ? 'screen-property-detail' : 'screen-account-dashboard');
    } catch (err) {
      setFormError(err.response?.data?.message || t('err.generic'));
    } finally { setFormBusy(false); }
  };

  const removeProperty = async (p) => {
    if (!window.confirm(t('acct.confirmRemoveProperty'))) return;
    try {
      await axios.delete(`/api/properties/${p._id}`, authHeaders());
      setSelectedProperty(null);
      await loadProperties();
      showToast(t('acct.propertyRemoved'));
      goTo('screen-account-dashboard');
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  const addPropertyDocuments = async (files, meta) => {
    const p = selectedProperty;
    if (!p) return;
    const docs = await uploadDocuments(files, meta);
    if (!docs.length) return;
    try {
      const res = await axios.post(`/api/properties/${p._id}/documents`, { documents: docs }, authHeaders());
      setSelectedProperty(res.data);
      showToast(t('doc.saved'));
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  const removePropertyDocument = async (doc) => {
    const p = selectedProperty;
    if (!p || !window.confirm(t('doc.confirmRemove'))) return;
    try {
      await axios.delete(`/api/properties/${p._id}/documents/${doc._id}`, authHeaders());
      const res = await axios.get(`/api/properties/${p._id}`, authHeaders());
      setSelectedProperty(res.data.property);
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  // Attaching a file to a job that already exists — from the work order screen,
  // in any of the three portals that can see it.
  const addRequestDocuments = async (files, meta) => {
    const r = activeRequest;
    if (!r) return;
    const docs = await uploadDocuments(files, meta);
    if (!docs.length) return;
    try {
      const res = await axios.post(`/api/requests/${r._id}/documents`, { documents: docs }, authHeaders());
      setActiveRequest(res.data);
      setRequests(prev => prev.map(x => (x._id === res.data._id ? res.data : x)));
      showToast(t('doc.saved'));
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  // On the work-order FORM the job does not exist yet, so files are uploaded
  // and held on the form until it posts.
  const addWorkOrderDocuments = async (files, meta) => {
    const docs = await uploadDocuments(files, meta);
    if (!docs.length) return;
    setWoForm(prev => ({ ...prev, documents: [...(prev?.documents || []), ...docs] }));
  };

  const openUnitForm = (p, u) => {
    setFormError('');
    setSelectedProperty(p);
    setUnitForm(u ? { ...u } : { occupancy: 'occupied' });
    goTo('screen-unit-form');
  };
  const closeUnitForm = () => { setUnitForm(null); goTo('screen-property-detail'); };

  const saveUnit = async () => {
    const u = unitForm || {};
    const p = selectedProperty;
    if (!p) return;
    if (!String(u.unitNumber || '').trim()) { setFormError(t('acct.errUnitNumber')); return; }
    setFormBusy(true); setFormError('');
    try {
      const res = u._id
        ? await axios.patch(`/api/properties/${p._id}/units/${u._id}`, u, authHeaders())
        : await axios.post(`/api/properties/${p._id}/units`, u, authHeaders());
      setSelectedProperty(res.data);
      setUnitForm(null);
      await loadProperties();
      showToast(t('acct.unitSaved'));
      goTo('screen-property-detail');
    } catch (err) {
      setFormError(err.response?.data?.message || t('err.generic'));
    } finally { setFormBusy(false); }
  };

  const deleteUnit = async () => {
    const u = unitForm; const p = selectedProperty;
    if (!u?._id || !p || !window.confirm(t('acct.confirmRemoveUnit'))) return;
    try {
      await axios.delete(`/api/properties/${p._id}/units/${u._id}`, authHeaders());
      const res = await axios.get(`/api/properties/${p._id}`, authHeaders());
      setSelectedProperty(res.data.property);
      setUnitForm(null);
      goTo('screen-property-detail');
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  const openMemberForm = (m) => {
    setFormError('');
    setMemberForm(m ? { ...m } : { level: 'coordinator' });
    goTo('screen-member-form');
  };
  const closeMemberForm = () => { setMemberForm(null); goTo('screen-account-dashboard'); };

  const saveMember = async () => {
    const m = memberForm || {};
    setFormBusy(true); setFormError('');
    try {
      if (m.userId) {
        await axios.patch(`/api/accounts/me/members/${m.userId}`, { level: m.level }, authHeaders());
      } else {
        if (!m.name?.trim() || !m.email?.trim()) { setFormError(t('acct.errNameEmail')); setFormBusy(false); return; }
        await axios.post('/api/accounts/me/members', m, authHeaders());
      }
      await loadAccount();
      setMemberForm(null);
      showToast(m.userId ? t('acct.memberSaved') : t('acct.inviteSent'));
      goTo('screen-account-dashboard');
    } catch (err) {
      setFormError(err.response?.data?.message || t('err.generic'));
    } finally { setFormBusy(false); }
  };

  const removeMember = async (m) => {
    if (!window.confirm(t('acct.confirmRemoveMember'))) return;
    try {
      await axios.delete(`/api/accounts/me/members/${m.userId}`, authHeaders());
      await loadAccount();
      setMemberForm(null);
      goTo('screen-account-dashboard');
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  const saveAccountSettings = async (patch) => {
    try {
      const res = await axios.patch('/api/accounts/me', patch, authHeaders());
      setAccount(res.data);
      showToast(t('acct.settingsSaved'));
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  // Raising a work order. `property` pre-selects one when it was opened from
  // that property's own screen.
  // Who the company can ask for by name. A name and their trades, nothing else
  // — see GET /api/vendors/for-account for why that list is deliberately thin.
  const [accountTechs, setAccountTechs] = useState([]);
  const loadAccountTechs = async () => {
    try {
      const res = await axios.get('/api/vendors/for-account', authHeaders());
      setAccountTechs(res.data || []);
    } catch { /* the form still works; it just cannot name anybody */ }
  };

  const openAccountWorkOrder = (property) => {
    setFormError('');
    setWoForm({
      property: property || null,
      unitId: '',
      items: [{ title: '', serviceType: '', description: '', photos: [], files: [] }],
      priority: 'routine',
      nteAmount: '',
      access: {},
      documents: [],
      requestedVendorId: '',
    });
    if (!accountTechs.length) loadAccountTechs();
    goTo('screen-account-wo');
  };
  const closeAccountWorkOrder = () => { setWoForm(null); goTo('screen-account-dashboard'); };

  const submitAccountWorkOrder = async () => {
    const w = woForm || {};
    if (!w.property) { setFormError(t('acct.errPickProperty')); return; }
    const items = (w.items || []).filter(i => (i.title || '').trim() || (i.description || '').trim());
    if (!items.length) { setFormError(t('acct.errDescribeJob')); return; }

    setFormBusy(true); setFormError('');
    try {
      // Any photo still held as a File goes up first, so the work order itself
      // posts as plain JSON — the same shape Julian's Add-a-Lead uses.
      const withPhotos = [];
      for (const it of items) {
        let photos = it.photos || [];
        if (it.files?.length) {
          const urls = await uploadPhotos(it.files);
          photos = [...photos, ...urls];
        }
        withPhotos.push({ title: it.title, serviceType: it.serviceType, description: it.description, photos });
      }

      await axios.post('/api/requests/account', {
        propertyId: w.property._id,
        unitId: w.unitId || undefined,
        items: withPhotos,
        priority: w.priority || 'routine',
        nteAmount: w.nteAmount === '' ? undefined : w.nteAmount,
        access: w.access || {},
        documents: w.documents || [],
        requestedVendorId: w.requestedVendorId || undefined,
      }, authHeaders());

      setWoForm(null);
      await loadAccountData();
      showToast(w.priority === 'emergency' ? t('acct.emergencySent') : t('acct.workOrderSent'));
      setAccountView('jobs');
      goTo('screen-account-dashboard');
    } catch (err) {
      setFormError(err.response?.data?.message || t('err.generic'));
    } finally { setFormBusy(false); }
  };

  // The company raising the work order themselves, once they have approved the
  // price. The server holds them to that order — an instruction to do the work
  // over an estimate nobody said yes to is work done at an unauthorised number.
  const [isRaisingAccountWO, setIsRaisingAccountWO] = useState(false);
  const raiseAccountWorkOrder = async (request) => {
    setIsRaisingAccountWO(true);
    try {
      const res = await axios.post(`/api/requests/${request._id}/work-order`, {}, authHeaders());
      setActiveRequest(res.data);
      setRequests(prev => prev.map(r => (r._id === res.data._id ? { ...r, ...res.data } : r)));
      showToast(`${res.data.workOrder?.number}`);
      setPrintDoc({ request: res.data, kind: 'work-order' });
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally { setIsRaisingAccountWO(false); }
  };

  const openPropertyImport = () => { setFormError(''); setImportState({}); goTo('screen-property-import'); };
  const closePropertyImport = () => { setImportState(null); goTo('screen-account-dashboard'); };

  const previewImport = async (text, fileName, mapping) => {
    setFormBusy(true); setFormError('');
    try {
      const res = await axios.post('/api/properties/import/preview', { text, mapping }, authHeaders());
      // The raw text is kept because commit re-parses server-side rather than
      // trusting a parsed payload from here.
      setImportState({ text, fileName, preview: res.data });
    } catch (err) {
      setFormError(err.response?.data?.message || t('err.generic'));
    } finally { setFormBusy(false); }
  };

  const commitImport = async () => {
    const s = importState || {};
    setFormBusy(true); setFormError('');
    try {
      const res = await axios.post('/api/properties/import/commit',
        { text: s.text, mapping: s.preview?.mapping }, authHeaders());
      setImportState({ ...s, result: res.data });
      await loadProperties('');
    } catch (err) {
      setFormError(err.response?.data?.message || t('err.generic'));
    } finally { setFormBusy(false); }
  };

  // Refreshed every render, read only by the popstate listener above. This is
  // the list of things Back should close before it changes screen.
  backStack.current = {
    modal, setModal,
    vendorEditForm, setVendorEditForm,
    editingLeadItem, setEditingLeadItem,
    activeVendor, setActiveVendor,
    activeClient, setActiveClient,
    activeProject, setActiveProject,
    lastVendorLink, setLastVendorLink,
    closedByBack, unwinding,
  };

  // The portfolio, fetched once. No token, because the audience is a
  // stranger, and the page is reachable before anybody signs in.
  useEffect(() => { loadWork(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The admin's copy, with the drafts, only once an admin is actually signed
  // in. A homeowner's session must not fetch it: the request would 403, and
  // asking at all tells the browser's network tab that the route exists.
  useEffect(() => {
    if (currentUser?.role === 'admin' && authToken) loadWorkAll();
  }, [currentUser?.role, authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // How many layers are stacked over the screen right now, outermost first.
  // Each one needs a history entry of its own, or Back has nothing to consume
  // and the phone leaves the site instead — which is exactly what it used to do
  // with a half-filled sheet open.
  const overlayDepth =
    (activeVendor || activeClient || activeProject ? 1 : 0) +
    (modal || vendorEditForm || lastVendorLink ? 1 : 0) +
    (editingLeadItem !== null && editingLeadItem !== undefined ? 1 : 0);

  useEffect(() => {
    const was = overlayDepthRef.current;
    overlayDepthRef.current = overlayDepth;

    if (overlayDepth > was) {
      for (let i = was; i < overlayDepth; i++) {
        window.history.pushState({ screen: screenRef.current, overlay: i + 1 }, '', window.location.pathname);
      }
    } else if (overlayDepth < was) {
      // Closed with a Cancel or a Save rather than Back. The entries those
      // layers pushed are still in front of us, so wind them off — otherwise
      // Back would replay sheets the user has already finished with.
      if (closedByBack.current) closedByBack.current = false;
      else {
        // One popstate per go(), however many entries it winds off.
        unwinding.current += 1;
        window.history.go(-(was - overlayDepth));
      }
    }
  }, [overlayDepth]);

  // Everything the screens can read/call. Grouped for readability.
  const ctx = {
    // navigation + session
    currentScreen, goTo, currentUser, logout,
    // auth form
    authForm, setAuthForm, authError, handleAuthInputChange, handleAuthSubmit,
    // service request form
    formData, handleInputChange, isSubmitting, handleSubmit,
    requestItems, setRequestItems, newRequestItem, editingRequestItem, setEditingRequestItem,
    // data
    requests, vendors, clients, activeRequest, setActiveRequest, markRequestSeen,
    // dashboards
    activeTab, setActiveTab, adminView, setAdminView, homeownerTab, setHomeownerTab, vendorTab, setVendorTab,
    activeVendor, setActiveVendor, vendorLayout, setVendorLayout,
    handleToggleWatch, handleMoveVendorTrade,
    vendorEditForm, setVendorEditForm, openVendorEdit, openVendorAdd, handleSaveVendorEdit, isSavingVendor,
    lastVendorLink, setLastVendorLink,
    // estimate approval (homeowner)
    handleApproveEstimate,
    // modals
    modal, setModal,
    estimateForm, setEstimateForm, estimateTotal, handleSubmitEstimate,
    noteEdit, setNoteEdit, handleSaveNote, isSavingNote,
    printDoc, setPrintDoc, openWorkOrder, isRaisingWorkOrder,
    newBuild,
    assignVendorId, setAssignVendorId, handleAssignVendor, handleAdminApproveEstimate, openEstimate,
    completionFiles, setCompletionFiles, completionNote, setCompletionNote, handleSubmitCompletion,
    invoiceForm, setInvoiceForm, invoiceTotal, handleSubmitInvoice,
    handleApproveInvoice,
    // admin: add lead + appointments
    addLeadForm, setAddLeadForm, handleAddLead, isAddingLead, editingLeadItem, setEditingLeadItem, uploadPhotos,
    uploadDocuments,
    appointmentInput, setAppointmentInput, handleSetAppointment,
    // admin: add or correct a client by hand
    addClientForm, setAddClientForm, openAddClientForm, openClientForm, handleAddClient, isSavingClient,
    jobCustomerForm, setJobCustomerForm, openJobCustomer, handleSaveJobCustomer, isSavingJobCustomer,
    lastClientLink, setLastClientLink,
    approvedOpened, setApproved,
    promoteClientToAccount,
    leadQualify, setLeadQualify, openLeadQualify, saveLeadQualify, isSavingLead,
    // admin: a client's own page
    activeClient, setActiveClient, openLeadForClient,
    openEstimateForClient, estimateForClient, setEstimateForClient,
    clientInvoiceForm, setClientInvoiceForm,
    openClientInvoice, openInvoiceFromEstimate, handleSaveClientInvoice, isSavingInvoice, openInvoiceDoc,
    openJobInvoiceDoc, setLeadStatus, openLeadDoc,
    askDeleteEstimate, askDeleteInvoice,
    // admin delete-with-confirmation
    confirmDelete, setConfirmDelete, handleConfirmDelete,
    // change password
    passwordForm, setPasswordForm, handleChangePassword,
    emailForm, setEmailForm, handleChangeEmail,
    // forgot/reset password
    forgotEmail, setForgotEmail, forgotStatus, forgotFrom,
    openForgotPassword, handleForgotPassword,
    resetForm, setResetForm, isResetting, handleResetPassword,
    // technician partnership application
    vendorProfile, vendorAppForm, setVendorAppForm, vendorDocs, setVendorDocs,
    isSubmittingApp, handleSubmitVendorApp, seedVendorForm, saveVendorProfile,
    licenseForm, setLicenseForm, openLicenseCheck, handleSaveLicenseCheck,
    // the agents
    authHeaders, showToast,
    agentBoard, isLoadingBoard, boardBrief, loadAgentBoard,
    autoSetup, isSavingAuto, loadAutoDispatch, saveAutoDispatch,
    storage, loadStorage,
    // getting paid
    savePayout, isSavingPayout, w9Wording, loadW9Wording, signW9, isSigningW9,
    revealed, setRevealed, revealPayout, revealW9,
    respondToOffer, offerBusy, pauseJob,
    handleVendorLogo, handleRemoveVendorLogo, isSavingLogo,
    // vendor ledger
    ledger, ledgerVendorFilter, setLedgerVendorFilter, vendorView, setVendorView,
    activeEntry, isSavingLedger,
    ledgerForm, setLedgerForm, ledgerDocFiles, setLedgerDocFiles, openLedgerForm,
    handleSaveLedgerEntry, handleDeleteLedgerEntry, handleExportLedger,
    paymentForm, setPaymentForm, paymentBlock, openRecordPayment, handleRecordPayment, handleDeletePayment,
    progressForm, setProgressForm, progressFiles, setProgressFiles, openProgress, handleAddProgress,
    // messages
    thread, threadFor, isLoadingThread, openThread,
    messageText, setMessageText, messageFiles, setMessageFiles,
    isSendingMessage, handleSendMessage, unreadCounts,
    // inbound-text diagnostics
    smsStatus, refreshSmsStatus, isLoadingSms,
    // push notifications
    pushOn, pushBusy, togglePush, sendTestPush, pushBlockedReason, isInstalled,
    // installing + offline
    isOffline, canInstall, handleInstall, installHint,
    // projects
    projects, activeProject, setActiveProject, estimates,
    projectForm, setProjectForm, openProjectForm, handleSaveProject, handleDeleteProject,
    lineForm, setLineForm, openProjectLine, handleSaveProjectLine, handleDeleteProjectLine,
    clientPayForm, setClientPayForm, openProjectPayment, handleSaveClientPayment, handleDeleteClientPayment,
    expenseForm, setExpenseForm, receiptFile, setReceiptFile,
    openProjectExpense, handleSaveExpense, handleDeleteProjectExpense,
    importForm, setImportForm, importBusy, openProjectImport, runImportPreview, confirmImport,
    saveStatementCell,
    estimateForm2, setEstimateForm2, estimateItemsTotal, openEstimateForm,
    invoiceForm2, setInvoiceForm2, invoiceItemsTotal, openInvoiceForm, openInvoiceEditor, handleSaveTechInvoice,
    docClientForm, setDocClientForm, openDocClient, saveDocClient, pickDocClient, vendorClients, setDocDate,
    handleSubmitTechEstimate, handleSaveTechEstimate, openEstimateDraft, handleDeleteTechEstimate,
    openEstimatePaper, openInvoicePaper,
    techEstimates, loadTechEstimates,
    admins, loadAdmins, adminForm, setAdminForm, handleAddAdmin, isSavingAdmin, toggleAdminPermission,
    signInLink, setSignInLink, handleSendSignInLink,
    invoiceDetail, setInvoiceDetail, openInvoiceDetail,
    handleApproveEstimateToProject, handleDeclineEstimate, handleInvoiceFromEstimate, isSavingProject,
    // compliance documents
    credentialForm, setCredentialForm, credentialFile, setCredentialFile,
    openCredential, handleSaveCredential, handleDeleteCredential,
    // commercial accounts
    account, accountMembers, accountView, setAccountView,
    properties, propertyTotal, propertySearch, setPropertySearch, loadProperties,
    selectedProperty, propertyHistory, openProperty,
    propertyForm, setPropertyForm, openPropertyForm, closePropertyForm, saveProperty, removeProperty,
    unitForm, setUnitForm, openUnitForm, closeUnitForm, saveUnit, deleteUnit,
    memberForm, setMemberForm, openMemberForm, closeMemberForm, saveMember, removeMember,
    saveAccountSettings,
    woForm, setWoForm, openAccountWorkOrder, closeAccountWorkOrder, submitAccountWorkOrder,
    importState, setImportState, openPropertyImport, closePropertyImport, previewImport, commitImport,
    accountTechs, raiseAccountWorkOrder, isRaisingAccountWO,
    addPropertyDocuments, removePropertyDocument, addRequestDocuments, addWorkOrderDocuments,
    formBusy, formError,
    // admin's view of the commercial accounts
    adminAccounts, loadAdminAccounts, adminAccountDetail, openAdminAccount,
    accountForm, setAccountForm, openAccountForm, handleCreateAccount,
    accountMemberForm, setAccountMemberForm, openAccountMemberForm,
    handleSaveAccountMember, handleRemoveAccountMember,
    adminPropertyForm, setAdminPropertyForm, openAdminPropertyForm, handleCreateAdminProperty,
    adminPropertyId, setAdminPropertyId,
    addAdminPropertyDocuments, removeAdminPropertyDocument, addJobDocuments,
    propertyJobForm, setPropertyJobForm, openPropertyJobForm, handleCreatePropertyJob,
    openAdminInvoice,
    lastInviteLink, setLastInviteLink,
    // Shared by the commercial forms: the same uploader and service-type list
    // the public request form and Julian's Add-a-Lead already use.
    uploadFiles: uploadPhotos, serviceTypes: SERVICE_TYPES,
    // Our Work: the public page reads the first two, the admin editor the rest.
    workProjects, workLoaded, workAll, saveWorkProject, deleteWorkProject, loadWorkAll,
    reorderEstimates, sendInvoiceLine, openStatementInvoice,
    handleLogout: logout,
    openRequestDetail: openRequestDetailScreen,
    // toast
    toast,
  };

  return (
    <AppContext.Provider value={ctx}>
      <div className="App">
        {/* In the flow, above everything, rather than floating over the top of
            the page. Fixed with `top: 0` put the Reload button underneath the
            iPhone status bar, where it could not be tapped — an offer to
            update the app that the app would not let you take. Here the bar
            pushes the page down instead of covering it, and its own
            safe-area padding keeps its contents clear of the clock. */}
        <OfflineBar />
        <UpdateBar />
        <Home />
        <Home variant="pros" />
        <Home variant="managers" />
        <RequestForm />
        <LoginChoice />
        <HomeownerLogin />
        <VendorLogin />
        <AdminLogin />
        <AccountLogin />
        <ForgotPassword />
        <ResetPassword />
        <HomeownerDashboard />
        <RequestDetail />
        <EstimateReview />
        <AdminDashboard />
        <VendorDashboard />
        <AccountDashboard />
        <PropertyDetail />
        <PropertyForm />
        <UnitForm />
        <MemberForm />
        <AccountWorkOrderForm />
        <PropertyImport />
        <Work />
        <PrivacyPolicy />
        <Terms />
        <Modals />
        {/* A lead, opened from the board: the estimate or the invoice as a
            sheet, with Edit, Send, Share, Reminders, PDF and Record Payment
            above it. Rendered before paperDoc so that editing from here puts
            the form on top and Back returns to this sheet. */}
        {leadDoc && (() => {
          const job = requests.find(r => r._id === leadDoc.id);
          // The lead was deleted underneath the open sheet. Close rather than
          // crash — this is reachable, because Delete lead is in the same menu.
          if (!job) return null;
          return (
            <LeadDoc
              request={job}
              authHeaders={authHeaders}
              showToast={showToast}
              permissions={currentUser?.permissions}
              onBack={() => setLeadDoc(null)}
              onSaved={onLeadSaved}
              onEdit={(kind) => {
                setLeadDoc(null);
                if (kind === 'invoice') openAdminInvoice(job);
                else openEstimate(job);
              }}
            />
          );
        })()}

        {/* What the last save produced, as the sheet it will be read as.
            Above the builder, so Edit is one tap back to the form with
            everything still in it. */}
        {paperDoc && (
          <PaperDoc
            kind={paperDoc.kind}
            number={paperDoc.number}
            date={paperDoc.date}
            status={paperDoc.status}
            from={paperDoc.from}
            to={paperDoc.to}
            serviceAddress={paperDoc.serviceAddress}
            items={paperDoc.items}
            adjustments={paperDoc.adjustments || []}
            notes={paperDoc.notes}
            externalUrl={paperDoc.externalUrl}
            documents={paperDoc.documents || []}
            terms={paperDoc.terms}
            settlement={paperDoc.settlement}
            share={paperDoc.share}
            busy={isSavingProject || isSavingInvoice}
            onBack={() => { setPaperDoc(null); setModal(null); }}
            // Each opener decides what its document can do — a draft can be
            // edited and sent, a sent one can only be read, an approved one
            // can be invoiced. Working that out here from the state around it
            // is how a button ends up on the wrong sheet.
            onEdit={paperDoc.onEdit}
            onSend={paperDoc.onSend}
            // "Send" alone sat next to "To Customer" and both meant send. The
            // two destinations are the whole distinction this feature rests
            // on, so each button names its own.
            sendLabel="To QPS"
            extraActions={[
              // The free tool. It sits after Send and Print in the row, which
              // is the order the sheet renders its actions in.
              paperDoc.customerFor && {
                key: 'customer',
                label: paperDoc.share?.sentAt ? 'Send again' : 'To Customer',
                icon: (
                  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16v12H4z" /><path d="M4 7l8 6 8-6" /></svg>
                ),
                onClick: () => openSendToCustomer({ kind: paperDoc.kind, doc: paperDoc.customerFor }),
              },
              paperDoc.invoiceFrom && {
                key: 'invoice',
                label: 'Invoice',
                icon: (
                  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M6 3h9l4 4v14H6z" /><path d="M15 3v4h4M9.5 12h5M9.5 16h5" /></svg>
                ),
                onClick: () => handleInvoiceFromEstimate(paperDoc.invoiceFrom),
              },
              paperDoc.detailFor && {
                key: 'detail',
                label: 'Details',
                icon: (
                  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 7.6v.6" /></svg>
                ),
                onClick: () => openInvoiceDetail(paperDoc.detailFor),
              },
              paperDoc.progressFor && {
                key: 'progress',
                label: 'Update',
                icon: (
                  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="8.5" cy="10" r="1.6" /><path d="M21 16l-5-5-5 5-2.5-2.5L3 18" /></svg>
                ),
                onClick: () => openProgress(paperDoc.progressFor),
              },
            ]}
          />
        )}

        {/* Who this document is going to, and the link it comes back as.
            Above the paper, because that is where it was opened from. */}
        {/* Asked at the money moment, and never a wall: Not now sends the
            document anyway. */}
        {stripeGate && (
          <StripeRequiredSheet
            busy={stripeGateBusy}
            onSetUp={startStripeFromGate}
            onNotNow={() => { const doc = stripeGate; setStripeGate(null); setSendCustomer(doc); }}
          />
        )}

        {sendCustomer && (
          <SendToCustomer
            kind={sendCustomer.kind}
            doc={sendCustomer.doc}
            authHeaders={authHeaders}
            onClose={() => setSendCustomer(null)}
            // The band under the toolbar reads "Sent today · not opened yet"
            // straight away. The server has just written exactly these fields,
            // so this is not a guess about what it did — and refreshData puts
            // the real record underneath a moment later anyway.
            onSent={() => {
              const now = new Date().toISOString();
              setPaperDoc((p) => (p ? {
                ...p,
                share: {
                  ...(p.share || {}),
                  sentAt: p.share?.sentAt || now,
                  lastSentAt: now,
                  viewCount: p.share?.viewCount || 0,
                },
              } : p));
              refreshData();
            }}
          />
        )}

        {/* The work order / estimate as a sheet of paper. Rendered above
            everything because printing takes the whole page. */}
        {/* One invoice off a statement, in front of the page. Wraps the same
            PrintDoc the full-screen view uses, so there is one renderer for
            the document and the printed sheet rather than a copy that
            drifts. */}
        {peekInvoice && (
          <DocPeek
            onClose={() => setPeekInvoice(null)}
            label={peekInvoice.number ? `Invoice ${peekInvoice.number}` : 'Invoice'}
          >
            <PrintDoc invoice={peekInvoice} kind="invoice" onClose={() => setPeekInvoice(null)} />
          </DocPeek>
        )}

        {printDoc && (
          <PrintDoc
            request={printDoc.request} invoice={printDoc.invoice} kind={printDoc.kind}
            onClose={() => setPrintDoc(null)}
            // Only Julian takes a work order back; a manager can raise one on
            // their own job but cancelling it is a dispatch decision.
            onDeleteWorkOrder={currentUser?.role === 'admin'
              ? (req) => { setPrintDoc(null); setConfirmDelete({ type: 'work order', id: req._id, label: req.workOrder?.number }); }
              : undefined}
            // The More menu. Admin only: these act on the record, not the view.
            onCreateWorkOrder={currentUser?.role === 'admin' ? (req) => openWorkOrder(req) : undefined}
            onDuplicate={currentUser?.role === 'admin'
              ? (printDoc.kind === 'invoice' ? duplicateInvoice : duplicateEstimate)
              : undefined}
            onDelete={currentUser?.role === 'admin'
              ? (printDoc.kind === 'invoice' ? askDeleteInvoice : askDeleteEstimate)
              : undefined}
          />
        )}
        <Toast />
      </div>
    </AppContext.Provider>
  );
}

export default App;
